import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"
import { renderGroupChips, setChipMember, requestGroupMembership } from "../utils/contact_group_chips"
import { egoId, lifespanLabel, deathOf, birthOf } from "../family_tree/genealogy"
import { countGaps } from "../family_tree/gaps"
import { persistAppearance } from "../appearance_prefs"
import { generationsFromEgo } from "../family_tree/genealogy"

// Sex on a genealogy card, in the vocabulary the rest of the app already uses
// for the field (Woman / Man / Non-binary). Nothing at all when it is not set -
// 19 of one family's 79 people have no gender recorded, and a "?" on each of
// them would be nineteen little accusations rather than information.
const GENDER_ICONS = {
    female: { icon: "bi-gender-female", label: "Woman" },
    male: { icon: "bi-gender-male", label: "Man" },
    nonbinary: { icon: "bi-gender-ambiguous", label: "Non-binary" }
}
import { renderTimeline } from "../family_tree/timeline_view"
import { renderSources } from "../family_tree/sources_view"
import { renderRelationship } from "../family_tree/relationship_view"
import { renderTable } from "../family_tree/table_view"
import { renderFan } from "../family_tree/fan_view"
import { renderDescendancy } from "../family_tree/descendancy_view"
import { renderMap } from "../family_tree/map_view"

// Genealogy-style layout for the family and org tree views.
//
// People are grouped into "units" (a person, or a couple joined by a partner
// edge, rendered side-by-side). Children hang from the couple connector's
// midpoint via orthogonal SVG elbows; each unit is centered over its
// children, siblings share a generation row, and whole descendancies can be
// collapsed. Org mode uses the same engine with single-person units and
// manager edges. Contacts with no edges land in a flat "Not yet placed" row.
//
// A family graph is NOT a tree, and the units above are: each person is drawn
// exactly once, so a married couple can only hang under ONE set of parents.
// Both of your parents have parents in your tree, so whichever grandparent
// couple gets laid out second finds its own child already drawn beside their
// spouse — and used to lose the link entirely, floating as an unconnected
// couple (or, with aunts and uncles in the tree, as a branch that skips your
// parent). Those second-claim edges are kept on the unit as `links` and drawn
// after layout by _drawLinks: a dashed elbow from the couple's connector,
// along the empty channel above the child's row, into the top of their card.
// One person, one card, every parent edge still on screen.
//
// The stage is pan/zoomable: wheel and pinch zoom, drag to pan, +/- and fit
// buttons; the whole chart is fitted into the viewport on load. Each card
// carries a kebab menu (edit connections — groups + relationships in one
// modal —, pin, profile, LinkedIn).
//
// The family tree also offers ALTERNATE layouts over the same payload:
// timeline, table, fan chart, map (../family_tree/*). They are separate modules
// because they share only the graph indices and the search hook, not the unit
// layout engine; this controller stays the shell (fetch, pan/zoom, search,
// card menus) and dispatches in _render. The server picks the initial view and
// remembers it — see ContactsController#resolved_family_tree_view — so the
// switch is a page load carrying ?view=, not client-side state. The org chart
// passes no view and is always the chart.
const VIEWS = ["tree", "landscape", "descendancy", "timeline", "table", "fan", "map", "sources", "relationship"]
// The two orientations the unit engine draws. Portrait runs generations down
// the page, landscape runs them across it; see the (main, cross) note above
// _measure. Everything else about the chart is identical, which is why they
// share an engine rather than being two of them.
const CHART_VIEWS = ["tree", "landscape"]
const ALTERNATES = { timeline: renderTimeline, table: renderTable, fan: renderFan, descendancy: renderDescendancy, map: renderMap, sources: renderSources, relationship: renderRelationship }
// The card size lives in CSS (--ft-card-w / --ft-card-h in _contact_tree.scss)
// and is READ from there per render, because it depends on the viewport and a
// constant cannot see one: a 300px card on a 375px phone opened the chart on
// one person with their partner off the edge.
//
// The history behind the desktop numbers, since it is the reason they are not
// smaller: 260 -> 300 because at 260 a full lifespan left
// "Great-great-grandfather" clipped on exactly the generations where the
// relation is hardest to work out for yourself. 64 -> 88 for three lines
// instead of two - name, who they are to you, then the dates in FULL - which is
// also what lets the phone size take width back without truncating anything.
//
// These are the fallbacks for the one render that can beat the stylesheet to
// the paint; CSS is the source of truth.
// What a conflicted fact is called on a card's tooltip. The panel spells the
// disagreement out; the card only has room to say which fact it is about.
// Past this many people in the middle, the chain stops being something you can
// read and the common ancestor is the fact worth saying instead.
const CHAIN_NAMES_SHOWN = 3
// Enough to choose from without becoming a directory; the input narrows fast.
const RELATE_RESULTS_SHOWN = 8
// Accent-blind matching, the browser-side twin of TextSearch.fold - "Jerome"
// has to find "Jérome", and on a Flemish family that is most of the names.
const foldText = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
// Mirrors FamilyTreeSource::ORIGINAL_KINDS - the server is the authority, this
// only picks the default the form opens on.
const ORIGINAL_KINDS = new Set(["birth_record", "marriage_record", "death_record", "census", "gravestone", "archival_record", "photo"])
const CONFLICT_LABELS = { person: "who they are", name: "their name", birth: "their birth", death: "their death" }
// What the confidence selector offers, weakest first. "Reported" is deliberately
// spelled out: "a relative told me" is a real and common answer, and a tree
// whose only options were "proven" and "nothing" would push people to overclaim.
const CONFIDENCE_OPTIONS = [["unverified", "Unverified"], ["reported", "Family says so"], ["documented", "Documented"]]
// The facts every genealogical document states and the tree had nowhere to put:
// where somebody was born, died and is buried, and what they did. Payload key
// paired with the modal target that edits it.
const PLACE_FIELDS = [["birth_place", "personBirthPlace"], ["death_place", "personDeathPlace"], ["burial_place", "personBurialPlace"], ["occupation", "personOccupation"]]
// Every measurement the layout engine uses is a CSS custom property with a
// fallback here. CSS owns them because DENSITY owns them: "Roomy" and "Compact"
// are two sets of these numbers and nothing else, so the switch is a class on
// the page and the engine simply re-measures. See _readMetrics and
// --ft-card-w / --ft-couple-gap and friends in _contact_tree.scss.
const CARD_W = 300
const CARD_H = 88
// Past this, "living" is a data gap rather than a fact. Deliberately looser than
// the server's MAX_PLAUSIBLE_AGE (125): this only decides which side of a filter
// somebody falls on, and erring old keeps a genuine centenarian in.
const MAX_LIVING_AGE = 120
const COUPLE_GAP = 32
const SIBLING_GAP = 24
const TREE_GAP = 56
const ROW_GAP = 56
const PAD = 16
const ACCENTS = ["blue", "orange", "amber", "rose", "teal", "green", "purple", "slate"]
// The genealogy convention: one colour per GRANDPARENT LINE, four of them.
// Ancestry, MyHeritage, Family Tree Maker and Legacy all ship a version of it,
// and researchers have been quartering paper pedigree charts this way for far
// longer than any of them. The order here is the most commonly published one -
// paternal grandfather blue, paternal grandmother green, maternal grandfather
// red, maternal grandmother yellow.
//
// This REPLACES a hash of the person's id, which grouped nothing and looked
// exactly like a legend somebody could go and look up. (The org chart has
// always coloured by department; only the family tree was colouring noise.)
// Where the accent colours are visible at all. The table and the outline are
// text; the sources list and the relationship chart are neither a tree nor
// coloured by line.
const LEGEND_VIEWS = ["tree", "landscape", "fan"]
const GRANDPARENT_LINES = [
    { key: "ff", accent: "blue" },
    { key: "fm", accent: "green" },
    { key: "mf", accent: "rose" },
    { key: "mm", accent: "amber" }
]
// Card-menu shortcuts for growing the chart. Phrased from the CARD's point of
// view - on Bradley's card, "Add parent" gives Bradley a parent - which is the
// MIRROR of the stored kind: the modal's select reads "<this person> is <kind>
// <the person you pick>", so a parent FOR Bradley is stored as Bradley being
// the "child". Getting this backwards silently builds an upside-down tree.
const ADD_KINDS = {
    family: [
        { label: "Add parent…", kind: "child", icon: "bi-arrow-up-circle" },
        { label: "Add partner…", kind: "partner", icon: "bi-heart" },
        { label: "Add child…", kind: "parent", icon: "bi-arrow-down-circle" }
    ],
    org: [
        { label: "Add manager…", kind: "report", icon: "bi-arrow-up-circle" },
        { label: "Add report…", kind: "manager", icon: "bi-arrow-down-circle" }
    ]
}
const OWNER_NODE_ID = "owner"
const MIN_SCALE = 0.05
// How far the AUTOMATIC opening fit is allowed to zoom out. A fit that shows
// everything is the wrong opening move on a big tree: 60-odd people fitted into
// a viewport lands at about 0.2, where a card is a coloured smudge and the
// names are gone, so the page opens on something you cannot read and have to
// dig your way into. Below this the chart opens at a legible size, centred on
// you, and getting the whole thing on screen is what the fit button is for.
// 0.75 is the point where a 13px card name is still comfortably readable.
const MIN_OPEN_SCALE = 0.75
// The same floor for a compact card. A compact name is set smaller but the card
// around it is smaller still, so the name survives further out - and the whole
// point of compact is to get more of the family onto one screen, which a floor
// borrowed from the roomy card would immediately give back.
const MIN_OPEN_SCALE_COMPACT = 0.55
// The two densities, in the order the button cycles them. "roomy" is the
// shipped card; "compact" drops the relation line and shrinks everything, so
// roughly three times as many people fit on a screen. Mirrors
// UserSetting::FAMILY_TREE_DENSITIES, which is what a member's choice persists
// into.
const DENSITIES = ["roomy", "compact"]
const DEFAULT_DENSITY = "roomy"
const DENSITY_STORAGE_KEY = "jane.family-tree.density"
// Org charts at or above this many people open with branches below
// ORG_COLLAPSE_DEPTH folded - see _seedCollapse.
const ORG_COLLAPSE_MIN_PEOPLE = 60
const ORG_COLLAPSE_DEPTH = 2
// A family tree this big gets the same opening: every line folded below its
// first generations, with your own chain left open - see _seedCollapse. A
// one-name study of 1,500 people laid out flat is dust at any zoom that shows
// it whole, and a wall of cards at any zoom that does not.
const FAMILY_COLLAPSE_MIN_PEOPLE = 150
const FAMILY_COLLAPSE_DEPTH = 2
const FAMILY_HUGE_PEOPLE = 1000
const FAMILY_COLLAPSE_DEPTH_HUGE = 1
// Large family trees use the compact minimap and its lineage tour. The bar is
// low on purpose: a tree only has to outgrow one screenful before finding a
// particular branch by eye becomes a chore, and that happens well before the
// hundred-and-fifty this once required. A tree below it still fits in a glance
// and would only lose room to a pane it does not need.
const OVERVIEW_MIN_PEOPLE = 50
// How many lineages get a band of their own; the rest share the last one.
const OVERVIEW_BANDS = 12
// How many lineages the minimap pane lists by name (see _renderMinimap).
const MINIMAP_LINES = 8
const OVERVIEW_COLORS = ["#2563eb", "#0d9488", "#d97706", "#7c3aed", "#db2777", "#65a30d", "#ea580c", "#0891b2", "#9333ea", "#dc2626", "#059669", "#4f46e5"]
const OVERVIEW_REST_COLOR = "#94a3b8"
// Whether the minimap pane is collapsed to just its header + map, per browser -
// same persistence rule as the density store below (localStorage first, a
// reader's own choice, never lost to a re-render).
const MINIMAP_STORAGE_KEY = "jane.family-tree.minimap"
// The "Play" tour: one stop per lineage (founder), then the ego if there is
// one. ~900ms reads as a deliberate flight rather than a jump cut; 4s dwell is
// long enough to read a two-line stop without feeling stalled.
const TOUR_FLIGHT_MS = 900
const TOUR_DWELL_MS = 4000
const TOUR_ROUTE_COLOR = "#2563eb"
// How close to you a branch has to be to survive "Collapse all" on a family
// tree. 1 leaves your parents, your siblings and your children drawn and folds
// everything past them - the frame a person actually navigates from.
const COLLAPSE_ALL_DEPTH = 1
const MAX_SCALE = 2.5
const FIT_PAD = 24
const PAN_THRESHOLD = 4
// The three axes the people filter has, and the order the menu lists them in -
// see ContactsHelper::FAMILY_TREE_PEOPLE_AXES, which renders exactly these
// keys on both the member's tree and the public share. A branch is
// drawn FROM you and so always contains you; a status is a fact about
// everybody and is not about you at all.
const PEOPLE_AXES = [
    { key: "branch", keys: ["paternal", "maternal", "ancestors"], includesEgo: true },
    // How far out from you to read, counted in generations. A reach is drawn
    // FROM you like a branch is, so you are always in it.
    { key: "reach", keys: ["gen1", "gen2", "gen3"], includesEgo: true },
    { key: "status", keys: ["living", "deceased"], includesEgo: false }
]
const PEOPLE_KEYS = PEOPLE_AXES.flatMap((axis) => axis.keys)

export default class extends Controller {
    static targets = ["viewport", "tree", "flow", "modalOverlay", "modalForm", "modalName", "modalError", "modalGroups", "searchInput", "searchCount", "searchOnly", "filters", "personOverlay", "personName", "personBirthday", "personError", "personDeceased", "personDiedRow", "personDiedOn", "personBirthYear", "personDeathYear", "birthPrecision", "deathPrecision", "deathHint", "personLinks", "personLinkList", "personGender", "personRemove", "newPersonOverlay", "newPersonName", "newPersonKind", "newPersonAnchor", "newPersonError", "mergeOverlay", "mergeSubject", "mergeKeepId", "mergeError", "personMergeBtn", "peopleChip", "peopleChipIcon", "peopleChipLabel", "personBirthPlace", "personDeathPlace", "personBurialPlace", "personOccupation", "personSources", "personSourceList", "sourceOverlay", "sourceSubject", "sourceExisting", "sourcePick", "sourceNew", "sourceTitle", "sourceKind", "sourceFile", "sourceRepository", "sourceReference", "sourceUrl", "sourceFact", "sourceClaim", "sourceStanding", "sourceInformation", "sourceError", "sourceSubmit", "sourceClaims", "marriageOverlay", "marriageSubject", "marriageStatus", "marriageDate", "marriageYear", "marriagePrecision", "marriagePlace", "marriageError", "relateOverlay", "suggestOverlay", "suggestSubject", "suggestName", "suggestBody", "suggestError", "suggestSubmit", "relateSubject", "relateLabel", "relateInput", "relateResults", "relateError"]
    static values = { url: String, kind: String, contactsBase: String, contactActionsBase: String, pinsUrl: String, readonly: Boolean, public: Boolean, collaborator: Boolean, viewer: Boolean, view: String, root: String, people: String, gap: String, sourcesBase: String, placesUrl: String, focus: String, path: String, relationshipUrl: String, shareBase: String, suggestUrl: String, density: String, layout: Object, layoutLocked: Boolean, layoutUrl: String }

    connect() {
        this.collapsed = new Set()
        this._density = this._initialDensity()
        this._applyDensity()
        this._initLayout()
        this.scale = 1
        this.tx = 0
        this.ty = 0
        this._pointers = new Map()
        this._searchQuery = ""
        this._searchOnly = false
        this._searchMatches = []
        this._searchIndex = -1
        // The minimap's "Play" tour. Lives entirely here, not in the DOM - the
        // pane is torn down and rebuilt on every _render (see _renderMinimap),
        // so anything the tour needs to survive that has to be state, not a
        // node reference.
        this._tour = null
        // Anything saved in the detail sidebar - a renamed contact, a new
        // parent link, a removed one - changes what this chart should show,
        // and the panel's turbo_stream can't reach these JS-drawn cards.
        // Re-read the chart instead, keeping the current pan and zoom.
        this._onSubmitEnd = (event) => {
            if (!event.detail?.success) return
            if (!event.target.closest(".detail-sidebar, .email-modal-overlay")) return
            this._load({ refit: false })
        }
        document.addEventListener("turbo:submit-end", this._onSubmitEnd)
        this._bindViewport()
        this._bindCardSizeBreakpoint()
        this._load()
    }

    disconnect() {
        this._closeMenu()
        this._teardownAlternate()
        this._cancelTourMotion()
        clearTimeout(this._searchTimer)
        clearTimeout(this._layoutSaveTimer)
        this._endLayoutDrag(false)
        document.removeEventListener("turbo:submit-end", this._onSubmitEnd)
        if (this._cardSizeQuery) this._cardSizeQuery.removeEventListener("change", this._onCardSizeChange)
        if (this._windowPointerBound) {
            this._windowPointerBound = false
            window.removeEventListener("pointermove", this._boundPointerMove)
            window.removeEventListener("pointerup", this._boundPointerUp)
            window.removeEventListener("pointercancel", this._boundPointerUp)
        }
    }

    // The org-chart poller's way in: re-read the data without touching pan,
    // zoom, search or collapsed branches, so a growing build can be explored
    // while it runs (see org_chart_poll_controller.js).
    refresh() {
        this._load({ refit: false })
    }

    // --- Density ------------------------------------------------------------
    //
    // "Roomy" or "Compact", and the whole mechanism is a data attribute: CSS
    // redefines --ft-card-* and the four gap variables, and _readMetrics picks
    // the new numbers up on the next render. Nothing in the layout engine knows
    // this control exists.
    //
    // WHY IT EXISTS. The roomy card is 300x88 and reads beautifully; a 125-person
    // family drawn out of them is metres wide, and the report from the person it
    // was built for was that the tree is hard to navigate. Compact is the same
    // chart with the relation line dropped and everything pulled in, which fits
    // roughly three times as many people on a screen - and because the automatic
    // opening fit has its own floor per density (MIN_OPEN_SCALE_COMPACT), it
    // arrives that way rather than after a pinch.
    //
    // Chart chrome, not a filter: it changes nothing about WHO is on screen, so
    // it sits with the zoom buttons rather than in the toolbar - which is also
    // the only place left, the toolbar being at its width budget.
    toggleDensity(event) {
        event?.preventDefault()
        this._density = this._density === "compact" ? "roomy" : "compact"
        this._applyDensity()
        this._rememberDensity()
        // Every card changed size, so every position the engine worked out is
        // wrong. Refit too: a pan that framed six roomy cards frames a corner of
        // empty canvas once they are half the size.
        this._needsFit = true
        this._render()
    }

    // localStorage first, then whatever the server seeded.
    //
    // Same precedence as the theme, and correct for the same reason: on a device
    // this reader has used, localStorage was written at the moment of the choice
    // and is never staler; on one they have not, it is empty and the server value
    // is all there is. It is also the ONLY store a public share's reader has -
    // they have no account for the server copy to hang on.
    _initialDensity() {
        let stored = this._storedDensity()
        if (stored) return stored

        let seeded = this.hasDensityValue ? this.densityValue : ""
        return DENSITIES.includes(seeded) ? seeded : DEFAULT_DENSITY
    }

    // localStorage THROWS rather than returning null in a private window, so
    // every access is wrapped - a failure degrades to the default density, never
    // to a broken chart. Same rule as family_tree_hints_controller.
    _storedDensity() {
        try {
            let value = window.localStorage.getItem(DENSITY_STORAGE_KEY)
            return DENSITIES.includes(value) ? value : null
        } catch {
            return null
        }
    }

    _rememberDensity() {
        try {
            window.localStorage.setItem(DENSITY_STORAGE_KEY, this._density)
        } catch {
            // No store to write to. The class is already applied, so this
            // session is right either way.
        }
        // Fire-and-forget, and a no-op for a signed-out reader (the meta it
        // checks is only rendered for a member). So a share's visitor keeps
        // their choice per browser and a member keeps it per account.
        persistAppearance({ family_tree_density: this._density })
    }

    // The attribute the CSS keys on, plus the button's own label and pressed
    // state - which is the only thing on screen that says which mode you are in.
    _applyDensity() {
        this.element.dataset.treeDensity = this._density
        let compact = this._density === "compact"
        this.element.querySelectorAll("[data-density-toggle]").forEach((button) => {
            let label = compact ? "Roomier cards" : "Compact cards"
            button.setAttribute("title", label)
            button.setAttribute("aria-label", label)
            button.setAttribute("aria-pressed", compact ? "true" : "false")
            let icon = button.querySelector("i")
            if (!icon) return

            icon.classList.toggle("bi-arrows-angle-expand", compact)
            icon.classList.toggle("bi-arrows-angle-contract", !compact)
        })
    }

    async _load({ refit = true } = {}) {
        try {
            let response = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("bad response")
            let data = await response.json()
            this.allNodes = data.nodes || []
            this.allEdges = data.edges || []
            this.groups = data.groups || []
            this.truncatedCount = data.truncated_count || 0
            // Provenance. `active` is false until somebody cites their first
            // document, and while it is false the tree draws exactly as it
            // always has - dashing every link the day this shipped would mark a
            // whole family "unverified" in one go, which is true and useless.
            this.evidence = data.evidence || { active: false, people: {} }
            this.cappedTotal = data.capped_total || 0
            this._needsFit = refit
            this._render()
            // A refresh (poll, or a save in the detail sidebar) can change
            // who's in the tree - re-run the active search against the fresh
            // node set rather than leaving stale highlighting in place.
            if (this._searchQuery) this._recomputeMatches()
        } catch {
            this.treeTarget.textContent = "Could not load the tree right now."
        }
    }

    _render() {
        this._closeMenu()
        this._syncRelationshipOption()
        this._readMetrics()
        // One filter, applied to the SET of people before anything draws, so
        // every view inherits it - the chart, the table, the timeline, the
        // outline and the fan all read this.nodes. A filter that only worked on
        // one chart would be a fifth thing to learn per view.
        this._applyPeopleFilter()
        this._buildIndices()

        let view = this._activeView()
        // The tour's camera only makes sense over a chart with cardPos - leaving
        // for the table, the outline or any other view ends it rather than
        // leaving a dwell timer ticking toward a stop that no longer exists.
        if (this._tour && !CHART_VIEWS.includes(view)) this._endTour()
        // CSS keys off this for the per-view chrome (the table has no zoom
        // buttons and doesn't want the dotted canvas).
        this.element.dataset.treeView = view
        // Two different questions, and conflating them put a "You" badge on the
        // owner's card for every stranger reading a shared link.
        //
        // _egoId is whose perspective the CHART is drawn from - the centre of
        // the fan. On an unaddressed share that is still the owner: it is their
        // tree, and a fan has to radiate from somebody.
        //
        // _viewerId is who is actually looking, which is who may be told "You".
        // Nobody, on an unaddressed share - the reader is not in the payload.
        this._egoId = egoId({ nodes: this.nodes, nodesById: this.nodesById })
        // The difference between "there is no you on this tree" and "your
        // filter left you out" - which are the same missing centre and
        // completely different problems. Telling somebody to link themselves to
        // a parent when they already are, and a funnel is on, sends them to fix
        // a tree that is not broken.
        this._egoFilteredOut =
            this._egoId == null &&
            (this.allNodes || []).length > 0 &&
            egoId({ nodes: this.allNodes, nodesById: new Map((this.allNodes || []).map((node) => [node.id, node])) }) != null
        this._viewerId = this._resolveViewerId()
        // After the ego is known: a family tree's seed keeps your chain open.
        this._seedCollapse()
        this._buildGrandparentLines()
        // Only the chart renderer rebuilds this; clearing it per render stops an
        // alternate layout centring on a previous chart's stale card positions.
        this.cardPos = null
        // Before the DOM goes: whatever the last layout bound to it (see
        // _renderAlternate).
        this._teardownAlternate()
        this.treeTarget.innerHTML = ""
        // The scrolling host (see contacts/_tree). Cleared alongside the stage
        // so whichever one this render does not use collapses to nothing.
        if (this.hasFlowTarget) this.flowTarget.innerHTML = ""
        // The toolbar's filter slot belongs to whichever layout is drawing, so
        // it is emptied here rather than by each of them - a layout that offers
        // no filters simply never touches it, and CSS hides it while empty.
        if (this.hasFiltersTarget) this.filtersTarget.innerHTML = ""

        this.landscape = view === "landscape"
        this._panZoom = CHART_VIEWS.includes(view) ? this._renderTree(view) : this._renderAlternate(view)
        // The minimap and tour share the same lineage model.
        this._prepareOverview(view)

        if (this._panZoom) {
            // A traced chain frames ITSELF. Nine generations at readable zoom is
            // a screen and a half of empty canvas with the two ends off it, and
            // somebody who opened a ?path= link was sent the chain to look at -
            // leaving them to find it by dragging is the same mistake the
            // two-click picker made.
            if (this._needsFit && this._fitToPath()) {
                this._needsFit = false
            } else if (this._needsFit) {
                this._needsFit = false
                this._fit({ readable: true })
            } else {
                this._applyTransform()
            }
        } else {
            // A scrolling view must not inherit a pan offset from the chart,
            // or it opens somewhere off-screen.
            this._needsFit = false
            this.scale = 1
            this.tx = 0
            this.ty = 0
            this._applyTransform()
        }
        this._applySearchClasses()
        this._updateSearchCount()
        this._renderCollapsedNotice()
        this._applyFoldButton()
        this._applyHomeButton()
        // The menu's counts are drawn from the payload, so they cannot be
        // filled until it has landed - and they change whenever the selection
        // does, since each one reports what its row would leave you with.
        this._syncPeopleCounts(this._peopleModes())
        let pair = this._pathPair()
        if (pair) {
            this._fitPending = this._fitPending || !this._pathIds
            this._loadRelationship(pair)
        } else {
            this._relationKey = null
            this._pathIds = null
            this._relationship = null
        }
        this._renderLens()
        this._renderGapNotice(view)
        // After the gap notice: they share the bottom-left tray, and DOM order
        // is what puts the worklist first and the key beside it.
        this._renderLineLegend()
        this._renderMinimap()
        this._renderStory()
        this._renderLayoutStatus()
        this._restoreArrangeFocus()
        this._openRequestedPerson()
    }

    // ?edit_person=<id> opens that person's editor on arrival.
    //
    // The contact card sends people here for their gender and dates now, and a
    // link that merely lands on the tree would leave you hunting a card among
    // ninety - which is the same "where did it go" the add flow used to have.
    // Consumed once, so a later re-render (a poll, a save) does not reopen it.
    _openRequestedPerson() {
        let wanted = this.element.dataset.contactTreeEditPersonValue
        if (!wanted || this._editRequestHandled) return

        this._editRequestHandled = true
        let node = this.nodesById?.get(Number(wanted))
        if (!node) return

        this._settleOn(node.id)
        this._openPersonModal(node)
    }

    // What is still to finish, on the tree, where a member already is.
    //
    // The gap counts lived only in the table's filter chips - so the one view
    // that knew what a half-finished tree was missing was a view you had to go
    // looking for. Somebody transcribing a family document wants a worklist.
    //
    // Charts only: the table says the same thing as chips, and twice on one
    // screen is noise. Two at most, because seven counts is a report rather
    // than a nudge.
    _renderGapNotice(view) {
        if (!this.hasViewportTarget) return

        this._gapNotice?.remove()
        this._gapNotice = null
        // Genealogy worklist only: "232 need a birthday" is nonsense over a
        // company chart of web-sourced strangers.
        if (this.kindValue === "org") return
        if (this.publicValue || !CHART_VIEWS.includes(view)) return

        let gaps = countGaps(this.nodes, { linked: this.linked }, { keys: ["no_gender", "no_birthday", "no_death_date"] }).slice(0, 2)
        if (!gaps.length) return

        let bar = document.createElement("div")
        bar.className = "contact-tree-gap-notice"
        let icon = document.createElement("i")
        icon.className = "bi bi-list-check"
        icon.setAttribute("aria-hidden", "true")
        bar.appendChild(icon)
        gaps.forEach((gap, index) => {
            if (index > 0) {
                let dot = document.createElement("span")
                dot.className = "contact-tree-gap-dot"
                dot.setAttribute("aria-hidden", "true")
                dot.textContent = "·"
                bar.appendChild(dot)
            }
            let link = document.createElement("a")
            link.className = "contact-tree-gap-link"
            link.href = this._gapHref(gap.key)
            link.dataset.turboFrame = "_top"
            link.textContent = `${gap.count} ${gap.prompt}`
            bar.appendChild(link)
        })
        this._cornerTray().appendChild(bar)
        this._gapNotice = bar
    }

    // Into the table, already filtered to that gap: the point is to arrive at
    // the list, not at another view with the work still to find.
    _gapHref(key) {
        let url = new URL(window.location.href)
        url.searchParams.set("view", "table")
        url.searchParams.set("gap", key)
        return `${url.pathname}?${url.searchParams.toString()}`
    }

    // "Once I press -, I cannot bring back the nodes?"
    //
    // The + is there, on the card you collapsed - but nothing on the page says
    // a branch is hidden, and after the layout reflows that card can be several
    // screens away. So a fold you did not mean reads as a fold you cannot undo,
    // and the only reliable way back was a reload.
    //
    // A count and one button, in the viewport rather than the panned stage so
    // it cannot itself scroll off. Absent entirely when nothing is folded.
    _renderCollapsedNotice() {
        if (!this.hasViewportTarget) return

        this._collapsedNotice?.remove()
        this._collapsedNotice = null
        if (!CHART_VIEWS.includes(this._activeView())) return
        let count = this.collapsed.size
        if (!count) return

        let bar = document.createElement("div")
        bar.className = "contact-tree-collapsed-notice"
        let label = document.createElement("span")
        label.textContent = count === 1 ? "1 branch hidden" : `${count} branches hidden`
        let button = document.createElement("button")
        button.type = "button"
        button.className = "contact-tree-collapsed-expand"
        button.textContent = "Expand all"
        button.addEventListener("click", (event) => {
            event.preventDefault()
            this.collapsed.clear()
            this._render()
        })
        bar.append(label, button)
        this.viewportTarget.appendChild(bar)
        this._collapsedNotice = bar
    }

    // --- Fold everything, and put it back -----------------------------------
    //
    // The chart has always had per-branch fold toggles and, once something was
    // folded, an "Expand all" inside the notice. What it had no way to do was
    // fold in the first place without clicking a "-" on every branch in turn -
    // which on a 125-person tree is the work, not the shortcut.
    //
    // One button, and it reads the state rather than remembering its own: while
    // anything is folded it offers to put it back, otherwise it folds. Same
    // toggle the task list's Fold all is, and the same reason - a button whose
    // label lies about what it will do is worse than no button.
    toggleFold(event) {
        event?.preventDefault()
        if (this.collapsed.size) this.collapsed.clear()
        else this._collapseAll()
        this._render()
    }

    // Fold every branch that folds AWAY from you, leaving your own generation
    // and one step either side.
    //
    // A family tree has no single root to fold back to - that is what makes
    // this different from the org chart, which simply folds below level two.
    // Distance is counted in GENERATIONS from the person the chart is drawn for
    // (generationsFromEgo, the same count the timeline bands by), so folding at
    // distance 1 hides your grandparents above and your grandchildren below
    // while leaving your parents, your siblings and your children on screen.
    //
    // Two things it is careful about, both so the notice's count is honest:
    // only ids that would actually hide somebody are added (a childless cousin
    // folds nothing, and "87 branches hidden" for 3 real folds is a lie), and
    // only one member of a couple, since the pair draws as one unit with one
    // toggle.
    _collapseAll() {
        let generations = this._generationsFromEgo()
        this.nodes.forEach((node) => {
            if (generations) {
                let depth = generations.get(node.id)
                // Nobody ego can reach. A tree is not always one family: load a
                // one-name study alongside your own and most of the chart is
                // branches you are not attached to at all, and there is no
                // generational distance to measure for any of them. Measuring
                // returned null and null fell through to "leave it open", so
                // Collapse all folded your own relatives and left nine hundred
                // strangers expanded - the opposite of what the button is for.
                // Fall back to the rootward rule those branches would get if
                // there were no ego at all: keep the top of each one, fold what
                // hangs below it.
                if (depth == null) {
                    if (!(this.parentsOf.get(node.id) || []).some((id) => this.nodesById.has(id))) return
                } else if (Math.abs(depth) < COLLAPSE_ALL_DEPTH) {
                    return
                }
            } else if (!(this.parentsOf.get(node.id) || []).some((id) => this.nodesById.has(id))) {
                // No ego to count from - an org chart, or a share with nobody
                // to be. Everything folds downward there, so the roots are what
                // must stay open.
                return
            }
            if (!this._foldsAnything(node.id)) return
            if ((this.partnersOf.get(node.id) || []).some((id) => this.collapsed.has(id))) return

            this.collapsed.add(node.id)
        })
    }

    // Mirrors the condition _renderUnit uses to decide whether a unit gets a
    // toggle at all: upward branches need visible parents, downward ones need
    // visible children.
    _foldsAnything(id) {
        let members = this._unitMemberIds(id)
        let relatives = this._foldsUpward(id) ? this.parentsOf : this.childrenOf
        return members.some((memberId) => (relatives.get(memberId) || []).some((otherId) => this.nodesById.has(otherId)))
    }

    // The fold button says which of the two things it will do. Nothing else on
    // the page reports that a branch is folded except the notice, which is
    // exactly why both exist.
    _applyFoldButton() {
        let folded = this.collapsed.size > 0
        this.element.querySelectorAll("[data-fold-toggle]").forEach((button) => {
            let label = folded ? "Expand all branches" : "Collapse all branches"
            button.setAttribute("title", label)
            button.setAttribute("aria-label", label)
            button.setAttribute("aria-pressed", folded ? "true" : "false")
            let icon = button.querySelector("i")
            if (!icon) return

            icon.classList.toggle("bi-chevron-expand", folded)
            icon.classList.toggle("bi-chevron-contract", !folded)
        })
    }

    // Four colours are a code, and a code without a key is decoration that
    // looks like information - which is exactly what the old hashed accents
    // were. The legend names the GRANDPARENT rather than saying "paternal
    // grandfather": on a tree you are reading, "Michel Gunst" is the thing you
    // recognise, and it degrades gracefully when the tree does not know which
    // parent was the father.
    _renderLineLegend() {
        if (!this.hasViewportTarget) return

        this._lineLegend?.remove()
        this._lineLegend = null
        // Only where the colours are actually on screen: the table and the
        // outline are text, and the sources and relationship views are neither.
        if (!LEGEND_VIEWS.includes(this._activeView())) return
        if (!this.lineHeads?.length) return

        let bar = document.createElement("div")
        bar.className = "contact-tree-legend"
        let label = document.createElement("span")
        label.className = "contact-tree-legend-label"
        label.textContent = "Lines"
        bar.appendChild(label)
        this.lineHeads.forEach((head) => {
            let item = document.createElement("span")
            item.className = "contact-tree-legend-item"
            let dot = document.createElement("span")
            dot.className = `contact-tree-legend-dot contact-tree-legend-dot--${head.accent}`
            let name = document.createElement("span")
            name.textContent = head.name
            name.title = head.label ? `${head.label}: ${head.name}` : head.name
            item.append(dot, name)
            bar.appendChild(item)
        })
        this._cornerTray().appendChild(bar)
        this._lineLegend = bar
    }

    // The bottom-left corner, which more than one strip wants: the worklist
    // nudge and the grandparent-line key, on every chart view that draws both.
    //
    // They used to pin themselves there independently, so the legend had to
    // know the other one existed and lift itself a row clear - two pills, same
    // corner, stacked. A row is the honest arrangement: they are peers, they
    // are the same shape, and stacking read as one strip having pushed the
    // other out of place. The tray lays them out and wraps UPWARD when there
    // is no width for both, so whatever happens the bottom edge stays put.
    //
    // It does not take pointer events itself; only the pills inside it do.
    // Otherwise an invisible full-width strip would eat drags along the bottom
    // of the canvas, which is exactly where a reader grabs the chart to pan.
    _cornerTray() {
        let tray = this.viewportTarget.querySelector(":scope > .contact-tree-corner")
        if (!tray) {
            tray = document.createElement("div")
            tray.className = "contact-tree-corner"
            this.viewportTarget.appendChild(tray)
        }
        return tray
    }

    // --- The lens bar -------------------------------------------------------
    //
    // ONE bar for both focus and the relationship trace, in the slot the
    // collapsed notice already uses. Two bars, or two toolbar chips, would be
    // two things to learn; the toolbar is also at its width budget, which is
    // why neither of these is a chip. Everything it shows comes out of the URL,
    // so the state of the page IS the link - which is what makes "here is how
    // you fit into this family" something you can send to somebody.
    _renderLens() {
        if (!this.hasViewportTarget) return

        this._lensBar?.remove()
        this._lensBar = null

        let focus = this._focusTarget()
        let pair = this._pathPair()
        let modes = (!focus && !pair) ? this._peopleModes() : []
        if (!focus && !pair && !modes.length) return
        // The relationship chart IS the answer the bar reports, in better
        // words and with the evidence under it. A floating pill repeating it
        // over the top of its own title is noise covering the headline.
        if (this._activeView() === "relationship") return

        let bar = document.createElement("div")
        bar.className = "contact-tree-lens-notice"
        if (modes.length) {
            // The people filter said what it was doing only through a chip that
            // reads "2". The chip is a control and has one line to do it in;
            // this is the sentence - what the tree is showing and how many that
            // leaves - in the slot that already answers exactly that question
            // for the focus lens. One way of saying "you are not looking at
            // everybody", wherever the narrowing came from.
            let named = modes
                .map((mode) => this.element.querySelector(`[data-people-key="${mode}"] .msg-chip-submenu-label`)?.textContent?.trim())
                .filter(Boolean)
            let total = (this.allNodes || []).length
            let what = named.length ? named.join(" + ") : "A filter"
            bar.append(this._lensText(`${what} - ${this.nodes.length} of ${total} people.`))
            bar.appendChild(this._copyButton())
            bar.appendChild(this._lensButton("Everyone", () => this.clearPeople()))
        } else if (pair) {
            bar.append(this._lensText(this._relationSentence || "Working out how they're related…"))
            if (this._relationSentence) bar.appendChild(this._copyButton())
            bar.appendChild(this._lensButton("Clear", () => this._clearPath()))
        } else {
            let person = this.nodesById?.get(focus.id) || (this.allNodes || []).find((n) => n.id === focus.id)
            let shown = this.nodes.length
            let total = (this.allNodes || []).length
            // A possessive, not a pronoun: "Dirk Gunst and their family" reads
            // as a guess about Dirk, and the tree knows better than to guess.
            bar.append(this._lensText(`${person ? `${person.name}'s family` : "One branch"} - ${shown} of ${total} people.`))
            if (shown < total) bar.appendChild(this._lensButton("Show more", () => this._setFocus(focus.id, focus.steps + 1)))
            bar.appendChild(this._copyButton())
            bar.appendChild(this._lensButton("Everyone", () => this._setFocus(null)))
        }
        this.viewportTarget.appendChild(bar)
        this._lensBar = bar
    }

    // One button, and it copies the link that WORKS for whoever you are sending
    // it to: the public one when the tree has a share, your own otherwise.
    // Offering both would make somebody choose between two urls that look
    // identical and behave completely differently.
    _copyButton() {
        return this.shareBaseValue
            ? this._lensButton("Copy a link to share", () => this._copyShareLink())
            : this._lensButton("Copy link", () => this._copyLensLink())
    }

    _lensText(text) {
        let span = document.createElement("span")
        span.className = "contact-tree-lens-text"
        span.textContent = text
        return span
    }

    _lensButton(label, onClick) {
        let button = document.createElement("button")
        button.type = "button"
        button.className = "contact-tree-lens-action"
        button.textContent = label
        button.addEventListener("click", (event) => {
            event.preventDefault()
            onClick()
        })
        return button
    }

    async _copyLensLink() {
        await this._copy(window.location.href, "Link copied.")
    }

    // The link somebody else can open.
    //
    // WHY THIS EXISTS. Tracing a relationship and then sending it to the person
    // it is about was a dead end: "Copy link" copied the MEMBER url, which only
    // works for somebody already in the family. Getting the shareable one meant
    // knowing the tree had a public link, finding its token, and hand-editing
    // the query onto it - which is not an interface, it is a trick.
    //
    // Carries the lens across verbatim (the path, the view, the focus), so what
    // the reader opens on is what you were looking at when you pressed it.
    async _copyShareLink() {
        if (!this.shareBaseValue) return

        let here = new URL(window.location.href)
        let target = new URL(this.shareBaseValue, window.location.origin)
        ;["path", "focus", "view"].forEach((key) => {
            let value = here.searchParams.get(key)
            if (value) target.searchParams.set(key, value)
        })
        // A link sent to make one point does not need a second thing competing
        // to be pressed - see the public view's ?plain=1.
        target.searchParams.set("plain", "1")
        await this._copy(target.toString(), "Shareable link copied.")
    }

    async _copy(text, notice) {
        try {
            await navigator.clipboard.writeText(text)
            showSystemToast(notice)
        } catch {
            showSystemToast("Couldn't copy the link.")
        }
    }

    // The Relationship layout exists only while a pair is being traced, and the
    // pair is chosen WITHOUT a page load (_setParam uses replaceState so the
    // lens bar can answer in place). The switcher is server-rendered, so left
    // alone it never learns the pair arrived: "How are we related?" answered on
    // the tree while the layout that draws it stayed missing from the menu.
    //
    // The href is rebuilt from the CURRENT URL rather than from the server's
    // link, which is what makes one method enough for all three surfaces. The
    // owner's tree, the shared tree and the public share each build that link
    // differently and two of them dropped ?path= on the way; window.location
    // already carries path, people, focus, as and the share token, so copying
    // it and setting view=relationship cannot lose any of them.
    _syncRelationshipOption() {
        let link = this.element.querySelector("[data-relationship-option]")
        if (!link) return

        let pair = this._pathPair()
        // Never hidden: the menu is where somebody LOOKS for this, and an entry
        // point that only appears once you have already used the feature is no
        // entry point. Without a pair the click opens the picker instead.
        link.dataset.relationshipReady = pair ? "1" : ""
        if (!pair) return

        let url = new URL(window.location.href)
        url.searchParams.set("view", "relationship")
        url.searchParams.set("path", pair.join(","))
        link.setAttribute("href", `${url.pathname}${url.search}`)
    }

    // The switcher's Relationship entry. With a pair chosen it is an ordinary
    // link to the chart; without one there is nothing to draw, so it asks who.
    relationshipOptionClick(event) {
        if (event.currentTarget?.dataset.relationshipReady === "1") return

        event.preventDefault()
        this.startRelate()
    }

    // Begin the question from the menu rather than from a card.
    //
    // From a card we already know one end. From here we may not: an addressed
    // share and the owner's own tree both know who is reading (_viewerId), so
    // that end is filled in and only the other is asked for. A plain public
    // share knows nobody, so it asks for both, one after the other.
    startRelate() {
        this._openRelateModal(this._viewerId ?? null)
    }

    // [fromId, toId] from ?path=a,b, or null.
    _pathPair() {
        let raw = (this.hasPathValue ? this.pathValue : "").toString().trim()
        if (!raw) return null

        let ids = raw.split(",").map((part) => Number(part.trim())).filter((id) => Number.isFinite(id))
        return ids.length === 2 && ids[0] !== ids[1] ? ids : null
    }

    // Pick the other person by NAME.
    //
    // This used to arm on the menu and take your next card click. On a tree of
    // nine that is a pleasant gesture; on a hundred and twenty-five it means
    // panning a canvas hunting for a card you may never have seen, in order to
    // answer the question you asked BECAUSE you did not know where they were.
    // The gesture was the wrong shape for the tree that needed it most.
    // nodeId may be null - see startRelate. With no starting person the modal
    // asks for the first one, and _completePath re-opens it for the second.
    _openRelateModal(nodeId) {
        if (!this.hasRelateOverlayTarget) return

        this._relateFrom = nodeId
        this._relateIndex = -1
        let node = nodeId == null ? null : this.nodesById.get(nodeId)
        if (this.hasRelateSubjectTarget) {
            this.relateSubjectTarget.textContent = node ? `From ${node.name}` : "Pick two people and I'll work out the link."
        }
        if (this.hasRelateLabelTarget) this.relateLabelTarget.textContent = node ? "Who to?" : "Who from?"
        if (this.hasRelateInputTarget) this.relateInputTarget.value = ""
        this._setRelateError(null)
        this.filterRelate()
        this.relateOverlayTarget.hidden = false
        this.relateInputTarget?.focus()
    }

    closeRelateModal(event) {
        event?.preventDefault()
        if (this.hasRelateOverlayTarget) this.relateOverlayTarget.hidden = true
    }

    relateOverlayClick(event) {
        if (event.target === this.relateOverlayTarget) this.closeRelateModal(event)
    }

    submitRelate(event) {
        event.preventDefault()
        let first = this._relateMatches()[0]
        if (first) this._completePath(first.id)
    }

    // Accent-blind, like every other search in the tree: a reader who types
    // "Jerome" is looking for Jérome, and on this family that is most of them.
    filterRelate() {
        if (!this.hasRelateResultsTarget) return

        this.relateResultsTarget.innerHTML = ""
        this._relateMatches().forEach((node, index) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = `contact-tree-relate-result${index === this._relateIndex ? " is-active" : ""}`
            let name = document.createElement("span")
            name.textContent = node.name
            row.appendChild(name)
            let meta = [node.relation, lifespanLabel(node)].filter(Boolean).join(" · ")
            if (meta) {
                let sub = document.createElement("span")
                sub.className = "contact-tree-relate-meta"
                sub.textContent = meta
                row.appendChild(sub)
            }
            row.addEventListener("click", () => this._completePath(node.id))
            this.relateResultsTarget.appendChild(row)
        })
    }

    relateKeydown(event) {
        let matches = this._relateMatches()
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault()
            let delta = event.key === "ArrowDown" ? 1 : -1
            this._relateIndex = Math.max(0, Math.min(matches.length - 1, this._relateIndex + delta))
            this.filterRelate()
        } else if (event.key === "Escape") {
            this.closeRelateModal(event)
        }
    }

    _relateMatches() {
        let query = foldText(this.hasRelateInputTarget ? this.relateInputTarget.value : "")
        return (this.allNodes || this.nodes || [])
            .filter((node) => node.id !== this._relateFrom)
            .filter((node) => !query || foldText(node.name).includes(query))
            .slice(0, RELATE_RESULTS_SHOWN)
    }

    _setRelateError(message) {
        if (!this.hasRelateErrorTarget) return

        this.relateErrorTarget.textContent = message || ""
        this.relateErrorTarget.hidden = !message
    }

    _completePath(nodeId) {
        let from = this._relateFrom
        // Started from the menu with nobody known: this pick is the FIRST
        // person, so keep the modal open and ask for the second.
        if (from == null) {
            this._openRelateModal(nodeId)
            return
        }

        this.closeRelateModal()
        if (from === nodeId) return

        this._setParam("path", `${from},${nodeId}`)
    }

    _clearPath() {
        this._relationSentence = null
        // Leaving the relationship chart means leaving the VIEW too - it has
        // nothing to draw without a pair, and stranding somebody on a blank
        // page is the opposite of clearing.
        if (this._activeView() === "relationship") this._setParam("view", this._chartView())
        this._setParam("path", null)
    }

    _setFocus(id, steps = 1) {
        this._setParam("focus", id == null ? null : (steps > 1 ? `${id}:${steps}` : String(id)))
    }

    // One writer for both parameters. pushState rather than a navigation: the
    // chart is already drawn and re-fetching the whole page to dim some cards
    // would lose the pan, the zoom and the collapsed branches.
    _setParam(name, value) {
        let url = new URL(window.location.href)
        if (value == null) url.searchParams.delete(name)
        else url.searchParams.set(name, value)
        window.history.replaceState({}, "", url)
        if (name === "focus") {
            this.focusValue = value || ""
            this._needsFit = true
        } else if (name === "path") {
            this.pathValue = value || ""
        } else if (name === "view") {
            this.viewValue = value || ""
            this._needsFit = true
        }
        this._render()
    }

    // The sentence in the bar comes from the SERVER, because the answer is
    // genealogy rather than geometry: Contacts::RelationLabeler knows what
    // removal and in-laws are, and a second implementation of that in the
    // renderer is the last thing this app needs. See
    // FamilyTrees::Relationship.
    async _loadRelationship(pair) {
        let base = this.hasRelationshipUrlValue ? this.relationshipUrlValue : ""
        if (!base) return

        let key = pair.join(",")
        if (this._relationKey === key) return

        this._relationKey = key
        this._relationSentence = null
        // Ask it from the READER's seat whenever they are one of the two.
        // "Madeleine is your grandmother" is the sentence somebody wants;
        // "Pieter is Madeleine's grandson" is the same fact told about two
        // other people, and on a share it is the difference between a family
        // tree and a family tree that includes you.
        let [from, to] = this._viewerId != null && pair[1] === this._viewerId ? [pair[1], pair[0]] : pair
        try {
            let response = await fetch(`${base}?from=${from}&to=${to}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
            let data = await response.json().catch(() => ({}))
            this._relationship = data
            if (!data.ok) {
                this._relationSentence = data.error || "They aren't connected in this tree."
                this._pathIds = null
            } else {
                this._relationSentence = this._relationWords(data)
                this._pathIds = new Set(data.path.map((person) => person.id))
            }
            // The chart view IS this answer, so it can only draw once it lands.
            if (this._activeView() === "relationship") this._render()
        } catch {
            this._relationSentence = "Couldn't work that out just now."
            this._pathIds = null
        }
        this._renderLens()
        this._applySearchClasses()
        // The chain's ids arrive from the server after the first paint, so the
        // frame is only computable now.
        if (this._fitPending) {
            this._fitPending = false
            this._fitToPath()
        }
    }

    // "Madeleine is Pieter's grandmother, via Dirk Gunst." A label nobody can
    // picture ("first cousin once removed") plus the chain anybody can is the
    // whole point of showing both.
    _relationWords(data) {
        let viewerIsFrom = this._viewerId != null && data.from.id === this._viewerId
        // Two families joined by marriage have no word for it (see
        // FamilyTrees::CrossRelationship) - say whose ancestors the chain joins.
        if (data.cross && !data.label && data.marriages) {
            let head = viewerIsFrom ? `${data.to.name} is related to you by marriage` : `${data.from.name} and ${data.to.name} are related by marriage`
            let [a, b] = [data.from_ancestor, data.to_ancestor]
            return a && b ? `${head}, through ${a.name} and ${b.name}.` : `${head}.`
        }
        let subject = viewerIsFrom ? "your" : `${data.from.name}'s`
        let head = data.label
            ? `${data.to.name} is ${subject} ${data.label.toLowerCase()}`
            : `${data.to.name} is related to ${viewerIsFrom ? "you" : data.from.name} through ${data.steps} steps`
        let between = data.path.slice(1, -1).map((person) => person.name)
        if (!between.length) return `${head}.`
        // A close relation reads best as the actual chain. A distant one does
        // not: eighth cousins are eighteen steps apart, and seventeen names in
        // a pill is unreadable. Past a few hops the useful fact is the common
        // ancestor the two lines meet at, which the server picks out as `via`.
        if (between.length > CHAIN_NAMES_SHOWN && data.via) {
            let lifespan = data.via.lifespan ? ` (${data.via.lifespan})` : ""
            return `${head} - ${data.steps} steps, through ${data.via.name}${lifespan}.`
        }
        return `${head}, via ${between.join(" → ")}.`
    }

    // An addressed share marks the person it is for; that is the reader, and
    // the only node on a public payload that may be called "You". Anywhere the
    // viewer owns the page - their own tree, a collaborator's view - the ego is
    // the reader and the older heuristic still applies.
    _resolveViewerId() {
        let flagged = this.nodes.find((node) => node.viewer)
        if (flagged) return flagged.id
        return this.publicValue ? null : this._egoId
    }

    // --- Correcting a person (collaborator only) ------------------------------

    _openPersonModal(node) {
        if (!this.hasPersonOverlayTarget) return

        this._personNodeId = node.id
        this.personNameTarget.value = node.name || ""
        // The payload carries a full date for a collaborator; a day-and-month
        // birthday with no year (Contact::BIRTHDAY_NO_YEAR) is stored as year 4
        // and a date input cannot hold it, so leave the field empty rather than
        // offering to overwrite it with something wrong.
        if (this.hasPersonGenderTarget) this.personGenderTarget.value = node.gender || ""
        PLACE_FIELDS.forEach(([key, target]) => {
            if (this[`has${target[0].toUpperCase()}${target.slice(1)}Target`]) this[`${target}Target`].value = node[key] || ""
        })
        this.personBirthdayTarget.value = (node.birthday && node.birth_year_known) ? node.birthday : ""
        // Year-only arrives as a plain year and no date at all - the server
        // never sends the 1 January it stores (Genealogy::PartialDate).
        this._setPrecision("birth", node.birth_day_known === false, node.birth_year)
        // A date the inputs CANNOT hold - a day and month with no year, stored
        // as year 4 (Contact::BIRTHDAY_NO_YEAR) - is shown blank on purpose. It
        // must then not be SAVED as blank: saving anything else on the person
        // (their gender, that they died) would silently delete a birth date
        // nobody meant to touch, and those are the hardest dates to recover.
        this._unrepresentable = {
            birth: !!node.birthday && !node.birth_year_known,
            death: !!node.died_on && !node.death_year_known
        }
        if (this.hasPersonDeceasedTarget) {
            this.personDeceasedTarget.checked = !!node.deceased
            // Same year-4 sentinel rule as the birthday: a date input cannot
            // hold "1987, day unknown", so leave it empty rather than offer to
            // overwrite what is known with something wrong.
            this.personDiedOnTarget.value = (node.died_on && node.death_year_known) ? node.died_on : ""
            this._setPrecision("death", node.death_day_known === false, node.death_year)
            this._syncDeceasedRow()
        }
        this._renderPersonLinks(node)
        // A member of the family cannot be deleted out of the tree (the server
        // refuses it); offering the button would be a dead end.
        if (this.hasPersonRemoveTarget) this.personRemoveTarget.hidden = !!node.viewer || node.relation === "You"
        // A member's row carries their seat and everybody's relation labels, so
        // it can never be the one folded away (MergePeople refuses it too).
        if (this.hasPersonMergeBtnTarget) this.personMergeBtnTarget.hidden = !!node.viewer || node.relation === "You"
        this._setPersonError(null)
        this._loadPersonSources(node.id)
        this.personOverlayTarget.hidden = false
        this.personNameTarget.focus()
    }

    // --- Sources ---------------------------------------------------------------
    //
    // A fact and the document behind it are written in the same place, because
    // the moment somebody types "24 January 1922" is the moment they know where
    // they got it. Provenance on a screen of its own is provenance nobody fills.

    async _loadPersonSources(personId) {
        if (!this.hasPersonSourceListTarget) return

        this.personSourceListTarget.innerHTML = ""
        this._sourceCatalog = null
        let evidence = this.nodesById.get(personId)?.evidence
        let placeholder = document.createElement("p")
        placeholder.className = "contact-tree-person-source-empty"
        placeholder.textContent = evidence ? "Loading…" : "Nothing recorded about where these facts came from."
        this.personSourceListTarget.appendChild(placeholder)
        if (!this.sourcesBaseValue) return

        try {
            let response = await fetch(`${this.sourcesBaseValue}/sources`, { credentials: "same-origin", headers: { Accept: "application/json" } })
            let data = await response.json()
            this._sourceCatalog = data.sources || []
            // Still the same person? The modal can be closed and reopened on
            // somebody else while this is in flight.
            if (this._personNodeId === personId) {
                this._renderPersonSources(personId)
                // The source dialog can open before this request finishes.
                // Populate its picker when the existing documents arrive.
                if (this.hasSourceOverlayTarget && !this.sourceOverlayTarget.hidden &&
                    this._sourceSubjectId === personId && !this._editingSourceId) {
                    this._fillSourcePicker()
                }
            }
        } catch {
            this._sourceCatalog = []
        }
    }

    _renderPersonSources(personId) {
        if (!this.hasPersonSourceListTarget) return

        let linkIds = new Set((this.edges || []).filter((e) => e.from === personId || e.to === personId).map((e) => e.id))
        let rows = []
        ;(this._sourceCatalog || []).forEach((source) => {
            (source.cites || []).forEach((cite) => {
                let mine = cite.person_id === personId || (cite.link_id && linkIds.has(cite.link_id))
                if (mine) rows.push({ source, cite })
            })
        })
        this.personSourceListTarget.innerHTML = ""
        if (!rows.length) {
            let empty = document.createElement("p")
            empty.className = "contact-tree-person-source-empty"
            // Says what is missing without scolding: an unsourced person is the
            // normal state of a tree somebody is still building.
            empty.textContent = "No source yet for anything here."
            this.personSourceListTarget.appendChild(empty)
            return
        }
        // Two documents disagreeing about the same fact, spelled out. The card
        // only has room for a mark; this is where it says what about.
        let byFact = new Map()
        rows.forEach(({ cite }) => {
            if (!cite.claim || !cite.person_id) return
            let seen = byFact.get(cite.fact) || new Set()
            seen.add(cite.claim.trim().toLowerCase())
            byFact.set(cite.fact, seen)
        })
        rows.forEach(({ source, cite }) => {
            let row = document.createElement("div")
            row.className = "contact-tree-person-source"
            let text = document.createElement("span")
            text.className = "contact-tree-person-source-text"
            let label = cite.fact && cite.fact !== "person" ? `${cite.fact_label}: ` : ""
            text.textContent = `${label}${source.title}${cite.claim ? ` - "${cite.claim}"` : ""}`
            text.title = [source.kind_label, source.citation_line].filter(Boolean).join(" · ")
            if ((byFact.get(cite.fact)?.size || 0) > 1) row.classList.add("is-conflicted")
            let remove = document.createElement("button")
            remove.type = "button"
            remove.className = "contact-tree-person-link-remove"
            remove.title = `Remove this citation of ${source.title}`
            remove.setAttribute("aria-label", `Remove this citation of ${source.title}`)
            remove.innerHTML = '<i class="bi bi-x-lg" aria-hidden="true"></i>'
            remove.addEventListener("click", () => this._removeCitation(cite.citation_id, personId))
            row.append(text, remove)
            this.personSourceListTarget.appendChild(row)
        })
    }

    async _removeCitation(citationId, personId) {
        if (!citationId || !this.sourcesBaseValue) return

        try {
            let response = await fetch(`${this.sourcesBaseValue}/citations/${encodeURIComponent(citationId)}`, {
                method: "DELETE",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not remove that citation.")
                return
            }
            showSystemToast(data.notice || "Citation removed.")
            await this._load({ refit: false })
            this._loadPersonSources(personId)
        } catch {
            this._setPersonError("Could not remove that citation.")
        }
    }

    // Opened from the person modal (cites that person) or from the Sources view
    // (files a document with nothing to cite yet).
    openSourceModal(event) {
        event?.preventDefault()
        this._openSourceModal(this._personNodeId)
    }

    addSourceFromEvent(event) {
        this._openSourceModal(Number(event?.detail?.personId) || null)
    }

    // Correcting the RECORD of a document, from the Sources view. The same
    // dialog: it already asks every question this one does, and a second form
    // carrying the same eight fields would be two places to keep the wording
    // right. What it drops is everything about a NEW document - the
    // "use one already in the tree" picker, and the citation half, which
    // belongs to a person rather than to the document.
    editSourceFromEvent(event) {
        let source = event?.detail?.source || (this._sourceCatalog || []).find((entry) => entry.id === Number(event?.detail?.sourceId))
        if (!source?.id || !this.hasSourceOverlayTarget) return

        this._editingSourceId = source.id
        this._openSourceModal(null)
        if (this.hasSourceExistingTarget) this.sourceExistingTarget.hidden = true
        if (this.hasSourceNewTarget) this.sourceNewTarget.hidden = false
        let claim = this.hasSourceFactTarget ? this.sourceFactTarget.closest(".contact-tree-source-claim") : null
        if (claim) claim.hidden = true
        if (this.hasSourceSubjectTarget) this.sourceSubjectTarget.textContent = "Correcting what this document is, not what it says about anybody."
        this._setSourceModalMode("Edit document", "Save changes")

        if (this.hasSourceTitleTarget) this.sourceTitleTarget.value = source.title || ""
        if (this.hasSourceKindTarget) this.sourceKindTarget.value = source.kind || "other"
        if (this.hasSourceRepositoryTarget) this.sourceRepositoryTarget.value = source.repository || ""
        if (this.hasSourceReferenceTarget) this.sourceReferenceTarget.value = source.reference || ""
        if (this.hasSourceUrlTarget) this.sourceUrlTarget.value = source.url || ""
        // Its OWN standing, not the one the kind would default to - the whole
        // reason the field exists is the documents where those differ.
        if (this.hasSourceStandingTarget && source.standing) this.sourceStandingTarget.value = source.standing
        this._fillSourceClaims(source)
        if (this.hasSourceTitleTarget) this.sourceTitleTarget.focus()
    }

    // One textarea per citation, because a claim is a transcribed sentence and
    // the interesting ones run to a paragraph. Empty is allowed: a citation
    // with no claim is "this document mentions them", which is a real state.
    _fillSourceClaims(source) {
        if (!this.hasSourceClaimsTarget) return

        this.sourceClaimsTarget.querySelectorAll(".contact-tree-source-claim-row").forEach((row) => row.remove())
        let cites = source.cites || []
        this.sourceClaimsTarget.hidden = cites.length === 0
        cites.forEach((cite) => {
            let row = document.createElement("div")
            row.className = "contact-tree-source-claim-row"
            let label = document.createElement("label")
            label.className = "form-label contact-tree-person-label"
            label.textContent = [cite.name, cite.fact_label].filter(Boolean).join(" · ")
            label.htmlFor = `ft-claim-${cite.citation_id}`
            let field = document.createElement("textarea")
            field.className = "form-control form-control-sm"
            field.id = `ft-claim-${cite.citation_id}`
            field.rows = 2
            field.value = cite.claim || ""
            field.dataset.citationId = String(cite.citation_id)
            row.append(label, field)
            this.sourceClaimsTarget.appendChild(row)
        })
    }

    _setSourceModalMode(heading, submitLabel) {
        let title = this.sourceOverlayTarget.querySelector(".contact-tree-modal-title")
        if (title) title.textContent = heading
        if (this.hasSourceSubmitTarget) this.sourceSubmitTarget.textContent = submitLabel
    }

    _openSourceModal(personId) {
        if (!this.hasSourceOverlayTarget) return

        // Anything that opens this dialog without going through
        // editSourceFromEvent is filing a document, not correcting one.
        if (!this._editingSourceId) this._setSourceModalMode("Add a source", "Save")
        this._sourceSubjectId = personId || null
        let node = personId ? this.nodesById.get(personId) : null
        if (this.hasSourceSubjectTarget) {
            this.sourceSubjectTarget.textContent = node ? `Evidence for ${node.name}` : "Filed in the tree - cite it on people afterwards."
        }
        // The fact selector only means anything with somebody to be a fact
        // about; a document filed from the Sources view has no subject yet.
        if (this.hasSourceFactTarget) this.sourceFactTarget.closest(".contact-tree-source-claim").hidden = !node
        this._fillSourcePicker()
        this.syncSourceStanding()
        this._setSourceError(null)
        // One dialog at a time. Both overlays are full-screen scrims at the same
        // depth, so opening this one over the person editor showed two stacked
        // cards with the one behind bleeding out top and bottom - which reads as
        // a rendering fault rather than a second step.
        this._returnToPersonModal = this.hasPersonOverlayTarget && !this.personOverlayTarget.hidden
        if (this._returnToPersonModal) this.personOverlayTarget.hidden = true
        this.sourceOverlayTarget.hidden = false
        if (this.hasSourceTitleTarget) this.sourceTitleTarget.focus()
    }

    // One memorial card names a dozen people, so "that one again" has to be
    // easier than uploading it twice - it is the whole reason sources and
    // citations are separate things.
    _fillSourcePicker() {
        if (!this.hasSourcePickTarget) return

        let catalog = this._sourceCatalog || []
        this.sourcePickTarget.innerHTML = ""
        let blank = document.createElement("option")
        blank.value = ""
        blank.textContent = "Add a new document…"
        this.sourcePickTarget.appendChild(blank)
        catalog.forEach((source) => {
            let option = document.createElement("option")
            option.value = String(source.id)
            option.textContent = source.citation_line ? `${source.title} (${source.citation_line})` : source.title
            this.sourcePickTarget.appendChild(option)
        })
        if (this.hasSourceExistingTarget) this.sourceExistingTarget.hidden = catalog.length === 0
        this.toggleSourceMode()
    }

    toggleSourceMode() {
        if (!this.hasSourceNewTarget) return

        this.sourceNewTarget.hidden = !!this.sourcePickTarget?.value
    }

    // The document type supplies the default standing, so the usual case needs
    // no thought and the unusual one (a probate filed as "archival record") is
    // still a single select away.
    syncSourceStanding() {
        if (!this.hasSourceStandingTarget || !this.hasSourceKindTarget) return

        this.sourceStandingTarget.value = ORIGINAL_KINDS.has(this.sourceKindTarget.value) ? "original" : "derivative"
    }

    closeSourceModal(event) {
        event?.preventDefault()
        if (this.hasSourceOverlayTarget) this.sourceOverlayTarget.hidden = true
        // Back where you came from: filing a document is a step inside editing
        // a person, and closing it should not also close them.
        if (this._returnToPersonModal && this.hasPersonOverlayTarget) this.personOverlayTarget.hidden = false
        this._returnToPersonModal = false
    }

    sourceOverlayClick(event) {
        if (event.target === this.sourceOverlayTarget) this.closeSourceModal(event)
    }

    async submitSource(event) {
        event.preventDefault()
        if (!this.sourcesBaseValue) return

        let existingId = this.hasSourcePickTarget ? this.sourcePickTarget.value : ""
        this._setSourceError(null)
        if (this.hasSourceSubmitTarget) this.sourceSubmitTarget.disabled = true
        try {
            let result = this._editingSourceId ? await this._updateSource() : (existingId ? await this._citeExistingSource(existingId) : await this._createSource())
            if (!result.ok) {
                this._setSourceError(result.error || "Could not save that.")
                return
            }
            this.closeSourceModal()
            this._resetSourceForm()
            showSystemToast(result.notice || "Saved.")
            await this._load({ refit: false })
            if (this._personNodeId) this._loadPersonSources(this._personNodeId)
            // The Sources view is a list of exactly what just changed.
            if (this._activeView() === "sources") this._render()
        } catch {
            this._setSourceError("Could not save that.")
        } finally {
            if (this.hasSourceSubmitTarget) this.sourceSubmitTarget.disabled = false
        }
    }

    async _citeExistingSource(sourceId) {
        let body = new FormData()
        body.append("citation[source_id]", sourceId)
        if (this._sourceSubjectId) body.append("citation[person_id]", String(this._sourceSubjectId))
        body.append("citation[fact]", this.hasSourceFactTarget ? this.sourceFactTarget.value : "person")
        if (this.hasSourceClaimTarget) body.append("citation[claim]", this.sourceClaimTarget.value)
        this._appendInformation(body, "citation")
        return this._postSource(`${this.sourcesBaseValue}/citations`, body)
    }

    async _updateSource() {
        let body = new FormData()
        body.append("source[title]", this.hasSourceTitleTarget ? this.sourceTitleTarget.value : "")
        body.append("source[kind]", this.hasSourceKindTarget ? this.sourceKindTarget.value : "other")
        body.append("source[repository]", this.hasSourceRepositoryTarget ? this.sourceRepositoryTarget.value : "")
        body.append("source[reference]", this.hasSourceReferenceTarget ? this.sourceReferenceTarget.value : "")
        body.append("source[url]", this.hasSourceUrlTarget ? this.sourceUrlTarget.value : "")
        if (this.hasSourceStandingTarget) body.append("source[standing]", this.sourceStandingTarget.value)
        // A file picked while editing is the same "this is wrong" gesture, so
        // it rides along rather than being a second trip through Replace.
        let file = this.hasSourceFileTarget ? this.sourceFileTarget.files[0] : null
        if (file) body.append("source[document]", file)
        if (this.hasSourceClaimsTarget) {
            this.sourceClaimsTarget.querySelectorAll("textarea[data-citation-id]").forEach((field) => {
                body.append(`source[claims][${field.dataset.citationId}]`, field.value)
            })
        }
        return this._postSource(`${this.sourcesBaseValue}/sources/${encodeURIComponent(this._editingSourceId)}`, body, "PATCH")
    }

    async _createSource() {
        let body = new FormData()
        body.append("source[title]", this.hasSourceTitleTarget ? this.sourceTitleTarget.value : "")
        body.append("source[kind]", this.hasSourceKindTarget ? this.sourceKindTarget.value : "other")
        body.append("source[repository]", this.hasSourceRepositoryTarget ? this.sourceRepositoryTarget.value : "")
        body.append("source[reference]", this.hasSourceReferenceTarget ? this.sourceReferenceTarget.value : "")
        body.append("source[url]", this.hasSourceUrlTarget ? this.sourceUrlTarget.value : "")
        let file = this.hasSourceFileTarget ? this.sourceFileTarget.files[0] : null
        if (file) body.append("source[document]", file)
        if (this._sourceSubjectId) body.append("source[person_id]", String(this._sourceSubjectId))
        body.append("source[fact]", this.hasSourceFactTarget ? this.sourceFactTarget.value : "person")
        if (this.hasSourceClaimTarget) body.append("source[claim]", this.sourceClaimTarget.value)
        if (this.hasSourceStandingTarget) body.append("source[standing]", this.sourceStandingTarget.value)
        this._appendInformation(body, "source")
        return this._postSource(`${this.sourcesBaseValue}/sources`, body)
    }

    // One control on the form covers two fields: "what the document does NOT
    // say" is the negative-evidence case, which is a kind of EVIDENCE rather
    // than a kind of information - and asking a transcriber two questions to
    // record one thought is how a form stops being filled in.
    _appendInformation(body, prefix) {
        if (!this.hasSourceInformationTarget) return

        let value = this.sourceInformationTarget.value
        if (value === "__negative") {
            body.append(`${prefix}[evidence]`, "negative")
            return
        }
        body.append(`${prefix}[information]`, value)
    }

    async _postSource(url, body, method = "POST") {
        let response = await fetch(url, {
            method: method,
            credentials: "same-origin",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            body
        })
        return await response.json().catch(() => ({ ok: false }))
    }

    _resetSourceForm() {
        this._editingSourceId = null
        if (this.hasSourceClaimsTarget) {
            this.sourceClaimsTarget.hidden = true
            this.sourceClaimsTarget.querySelectorAll(".contact-tree-source-claim-row").forEach((row) => row.remove())
        }
        let claim = this.hasSourceFactTarget ? this.sourceFactTarget.closest(".contact-tree-source-claim") : null
        if (claim) claim.hidden = false
        ;["sourceTitle", "sourceRepository", "sourceReference", "sourceUrl", "sourceClaim"].forEach((name) => {
            if (this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) this[`${name}Target`].value = ""
        })
        if (this.hasSourceFileTarget) this.sourceFileTarget.value = ""
        if (this.hasSourcePickTarget) this.sourcePickTarget.value = ""
        this.toggleSourceMode()
    }

    _setSourceError(message) {
        if (!this.hasSourceErrorTarget) return

        this.sourceErrorTarget.textContent = message || ""
        this.sourceErrorTarget.hidden = !message
    }

    // The scan, arriving after the record. See FamilyTreeSourcesController#update.
    async uploadScanFromEvent(event) {
        let { sourceId, file } = event?.detail || {}
        if (!sourceId || !file || !this.sourcesBaseValue) return

        let body = new FormData()
        body.append("source[document]", file)
        try {
            let response = await fetch(`${this.sourcesBaseValue}/sources/${encodeURIComponent(sourceId)}`, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body
            })
            let data = await response.json().catch(() => ({}))
            showSystemToast(data.ok ? (data.notice || "Added.") : (data.error || "Could not add that."))
            if (data.ok && this._activeView() === "sources") this._render()
        } catch {
            showSystemToast("Could not add that.")
        }
    }

    // From the Sources view: remove the whole document, not one citation of it.
    async removeSourceFromEvent(event) {
        let { sourceId, title } = event?.detail || {}
        if (!sourceId || !this.sourcesBaseValue) return
        if (!window.confirm(`Remove "${title}"? Every citation of it goes too. The facts stay, without their evidence.`)) return

        try {
            let response = await fetch(`${this.sourcesBaseValue}/sources/${encodeURIComponent(sourceId)}`, {
                method: "DELETE",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }
            })
            let data = await response.json().catch(() => ({}))
            showSystemToast(data.ok ? (data.notice || "Removed.") : (data.error || "Could not remove that."))
            if (data.ok) await this._load({ refit: false })
            if (data.ok && this._activeView() === "sources") this._render()
        } catch {
            showSystemToast("Could not remove that.")
        }
    }

    // A name in the Sources view takes you to that person.
    //
    // For a member that means their editor, where the facts this document is
    // evidence for actually are. A public reader has no editor - the chip used
    // to be a button that did nothing - so it takes them to the person in the
    // tree instead, which is the useful half of the same intent.
    focusPersonFromEvent(event) {
        let id = Number(event?.detail?.personId)
        let node = this.nodesById?.get(id)
        if (!node) return

        if (this.hasPersonOverlayTarget) {
            this._openPersonModal(node)
            return
        }
        this._setParam("view", this._chartView())
        this._setFocus(node.id)
    }

    // Which chart to land on when a list view sends somebody to a person.
    _chartView() {
        let current = this._activeView()
        return CHART_VIEWS.includes(current) ? current : "tree"
    }

    // The marriage editor. A prompt-free inline form would need four more
    // controls on a row that already has three, so it opens the same kind of
    // small dialog everything else on this modal does.
    _openMarriage(link) {
        if (!this.hasMarriageOverlayTarget) return

        this._marriageLink = link
        this.marriageSubjectTarget.textContent = `${link.label} ${link.name}`
        this.marriageStatusTarget.value = link.union_status || "partner"
        // Year-only arrives as a plain year and no date, the same shape a birth
        // uses - the server never sends the 1 January it stores.
        this.marriageDateTarget.value = (link.married_on && link.marriage_day_known !== false) ? link.married_on : ""
        this.marriageYearTarget.value = link.marriage_year || ""
        this._setMarriagePrecision(link.marriage_day_known === false)
        this.marriagePlaceTarget.value = link.marriage_place || ""
        this._setMarriageError(null)
        // One dialog at a time - see _openSourceModal. Two full-screen scrims at
        // the same depth read as a rendering fault, not as a second step.
        this._returnToPersonFromMarriage = this.hasPersonOverlayTarget && !this.personOverlayTarget.hidden
        if (this._returnToPersonFromMarriage) this.personOverlayTarget.hidden = true
        this.marriageOverlayTarget.hidden = false
        this.marriageDateTarget.focus()
    }

    closeMarriage(event) {
        event?.preventDefault()
        if (this.hasMarriageOverlayTarget) this.marriageOverlayTarget.hidden = true
        // Back to the person you opened it from - editing a marriage is a step
        // inside editing them, and closing it should not close them too.
        if (this._returnToPersonFromMarriage && this.hasPersonOverlayTarget) this.personOverlayTarget.hidden = false
        this._returnToPersonFromMarriage = false
    }

    marriageOverlayClick(event) {
        if (event.target === this.marriageOverlayTarget) this.closeMarriage(event)
    }

    // "1683" is what a parish register yields. A date input alone would make a
    // transcriber invent a day - the same reason births have this toggle.
    toggleMarriagePrecision(event) {
        event?.preventDefault()
        this._setMarriagePrecision(this.marriageDateTarget.hidden === false)
    }

    _setMarriagePrecision(yearOnly) {
        this.marriageDateTarget.hidden = yearOnly
        this.marriageYearTarget.hidden = !yearOnly
        this.marriagePrecisionTarget.textContent = yearOnly ? "Full date" : "Year only"
    }

    async submitMarriage(event) {
        event.preventDefault()
        let link = this._marriageLink
        let base = this.personOverlayTarget?.dataset.contactTreeLinksUrlValue
        if (!link || !base) return

        let married_on = this.marriageYearTarget.hidden ? this.marriageDateTarget.value : this.marriageYearTarget.value
        try {
            let response = await fetch(`${base}/${encodeURIComponent(link.id)}`, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
                body: JSON.stringify({
                    link: {
                        union_status: this.marriageStatusTarget.value,
                        married_on: married_on,
                        marriage_place: this.marriagePlaceTarget.value
                    }
                })
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setMarriageError(data.error || "Could not save that.")
                return
            }
            this.closeMarriage()
            showSystemToast(data.notice || "Saved.")
            await this._load({ refit: false })
            let fresh = this.nodesById.get(this._personNodeId)
            if (fresh) this._renderPersonLinks(fresh)
        } catch {
            this._setMarriageError("Could not save that.")
        }
    }

    _setMarriageError(message) {
        if (!this.hasMarriageErrorTarget) return

        this.marriageErrorTarget.textContent = message || ""
        this.marriageErrorTarget.hidden = !message
    }

    async _setLinkConfidence(link, confidence) {
        let base = this.personOverlayTarget?.dataset.contactTreeLinksUrlValue
        if (!base) return

        try {
            let response = await fetch(`${base}/${encodeURIComponent(link.id)}`, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
                body: JSON.stringify({ link: { confidence } })
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not save that.")
                return
            }
            showSystemToast(data.notice || "Saved.")
            await this._load({ refit: false })
        } catch {
            this._setPersonError("Could not save that.")
        }
    }

    // Every edge this person is an end of, read from THEIR side: the same row
    // is "Parent" on the child's card and "Child" on the parent's. A partner
    // edge is stored once and reads the same either way.
    _personLinks(node) {
        if (!this.edges) return []

        return this.edges.filter((e) => e.from === node.id || e.to === node.id).map((edge) => {
            let outgoing = edge.from === node.id
            let other = this.nodesById.get(outgoing ? edge.to : edge.from)
            // "Partner of", not "Partner": the other two read "Parent of X" and
            // "Child of X", so the bare noun made one row of the same list
            // ungrammatical - "Partner Edmond Van de Velde". The contact panel
            // has always said "Partner of" for the same edge, so the two
            // surfaces disagreed about one word on the same screen.
            let label = edge.kind === "partner" ? "Partner of" : (outgoing ? "Parent of" : "Child of")
            return {
                id: edge.id, label, kind: edge.kind, confidence: edge.confidence || "unverified",
                name: other ? other.name : "Someone",
                marriage: edge.marriage, union_status: edge.union_status,
                married_on: edge.married_on, marriage_year: edge.marriage_year,
                marriage_year_known: edge.marriage_year_known, marriage_day_known: edge.marriage_day_known,
                marriage_place: edge.marriage_place
            }
        })
    }

    // "Ambrosius is recorded as a parent; his partner Juliana is not."
    //
    // THE shape that let two mothers go missing in a real tree while both
    // fathers were fine: parentage is two independent edges, so nothing
    // structurally ties them together (GEDCOM's FAM record does, which is why
    // this cannot happen there). A warning is the cheap half of that fix - it
    // catches the case rather than preventing it, and it catches it where
    // somebody is already looking at the person.
    _missingParents(node) {
        let parents = (this.parentsOf.get(node.id) || [])
        if (!parents.length) return []

        let known = new Set(parents)
        return parents.flatMap((parentId) => (this.partnersOf.get(parentId) || [])
            .filter((partnerId) => !known.has(partnerId))
            .map((partnerId) => ({
                id: partnerId,
                name: this.nodesById.get(partnerId)?.name || "someone",
                via: this.nodesById.get(parentId)?.name || "their parent"
            })))
    }

    _renderMissingParent(node) {
        if (!this.hasPersonLinksTarget || !this.sourcesBaseValue) return

        let missing = this._missingParents(node)
        if (!missing.length) return

        let candidate = missing[0]
        let row = document.createElement("div")
        row.className = "contact-tree-person-gap"
        let text = document.createElement("span")
        text.textContent = `${candidate.via} is recorded as a parent; their partner ${candidate.name} is not.`
        let add = document.createElement("button")
        add.type = "button"
        add.className = "contact-tree-person-gap-add"
        add.textContent = `Add ${candidate.name}`
        add.addEventListener("click", () => this._addMissingParent(candidate, node))
        row.append(text, add)
        this.personLinkListTarget.appendChild(row)
    }

    async _addMissingParent(candidate, node) {
        let base = this.contactsBaseValue
        if (!base) return

        try {
            let body = new FormData()
            body.append("relationship[kind]", "parent")
            body.append("relationship[related_contact_id]", String(node.id))
            let response = await fetch(`${base}/${encodeURIComponent(candidate.id)}/relationships`, {
                method: "POST",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not add them.")
                return
            }
            showSystemToast(data.notice || "Added.")
            await this._load({ refit: false })
            let fresh = this.nodesById.get(node.id)
            if (fresh) this._renderPersonLinks(fresh)
        } catch {
            this._setPersonError("Could not add them.")
        }
    }

    _renderPersonLinks(node) {
        if (!this.hasPersonLinksTarget) return

        let links = this._personLinks(node).filter((link) => link.id != null)
        let showConfidence = !!this.sourcesBaseValue
        this.personLinksTarget.hidden = links.length === 0 && this._missingParents(node).length === 0
        this.personLinkListTarget.innerHTML = ""
        links.forEach((link) => {
            let row = document.createElement("div")
            row.className = "contact-tree-person-link"
            let text = document.createElement("span")
            text.className = "contact-tree-person-link-text"
            text.textContent = `${link.label} ${link.name}`
            let remove = document.createElement("button")
            remove.type = "button"
            remove.className = "contact-tree-person-link-remove"
            remove.title = `Remove: ${link.label} ${link.name}`
            remove.setAttribute("aria-label", `Remove link: ${link.label} ${link.name}`)
            remove.innerHTML = '<i class="bi bi-x-lg" aria-hidden="true"></i>'
            remove.addEventListener("click", () => this._removeLink(link, node))
            // How well this link is supported, set right where the link is.
            // The commonest genealogical fact of all has no document behind it
            // - "my mother told me" - so a tree that could only record
            // confidence by attaching a scan would leave every such link
            // looking like a guess.
            if (showConfidence && link.kind === "partner") {
                // The couple's own facts live on this row because the row IS
                // the couple - one shared record, so there is no way for two
                // halves of a marriage to disagree.
                // A real button: it opens a dialog, so it has to be reachable
                // by keyboard and announce itself as something you can press.
                let summary = document.createElement("button")
                summary.type = "button"
                summary.className = "contact-tree-link-marriage"
                summary.textContent = link.marriage || "Add marriage…"
                summary.title = `Marriage of ${link.name}`
                summary.addEventListener("click", () => this._openMarriage(link))
                row.append(text, summary, remove)
            } else if (showConfidence && link.kind === "parent") {
                let select = document.createElement("select")
                select.className = "form-select form-select-sm contact-tree-link-confidence"
                select.setAttribute("aria-label", `How well is "${link.label} ${link.name}" supported?`)
                CONFIDENCE_OPTIONS.forEach(([value, label]) => {
                    let option = document.createElement("option")
                    option.value = value
                    option.textContent = label
                    if (value === (link.confidence || "unverified")) option.selected = true
                    select.appendChild(option)
                })
                select.addEventListener("change", () => this._setLinkConfidence(link, select.value))
                row.append(text, select, remove)
            } else {
                row.append(text, remove)
            }
            this.personLinkListTarget.appendChild(row)
        })
        this._renderMissingParent(node)
    }

    async _removeLink(link, node) {
        let base = this.personOverlayTarget.dataset.contactTreeLinksUrlValue
        if (!base) return

        try {
            let response = await fetch(`${base}/${encodeURIComponent(link.id)}`, {
                method: "DELETE",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not remove that link.")
                return
            }
            showSystemToast(data.notice || "Link removed.")
            this._refreshOpenPanel()
            // Re-read, then redraw the list from the fresh edges - the modal
            // stays open because removing one wrong link is usually the first
            // of two edits, not the last.
            await this._load({ refit: false })
            let fresh = this.nodesById.get(node.id)
            if (fresh) this._renderPersonLinks(fresh)
        } catch {
            this._setPersonError("Could not remove that link.")
        }
    }

    // The only undo for "Add parent…". Confirmed, because it is the one control
    // here that destroys something rather than correcting it.
    async removePerson() {
        if (this._personNodeId == null) return

        let node = this.nodesById.get(this._personNodeId)
        let name = node ? node.name : "this person"
        if (!window.confirm(`Remove ${name} from the tree? Their links go too. This cannot be undone.`)) return

        let base = this.personOverlayTarget.dataset.contactTreePersonUrlValue
        try {
            let response = await fetch(`${base}/${encodeURIComponent(this._personNodeId)}`, {
                method: "DELETE",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not remove them.")
                return
            }
            this.closePersonModal()
            showSystemToast(data.notice || "Removed.")
            this._load({ refit: false })
        } catch {
            this._setPersonError("Could not remove them.")
        }
    }

    // The person panel is rendered by the server and has no access to this
    // controller, so "Edit details" there comes back as an event.
    // Adopting somebody from their panel changes what their card opens next
    // time, so the tree re-reads rather than waiting for the poll.
    reloadFromEvent() {
        this._load({ refit: false })
    }

    openPersonById(event) {
        let id = Number(event?.detail?.personId)
        let node = this.nodesById?.get(id)
        if (node) this._openPersonModal(node)
    }

    // The panel next to the tree shows what the tree just changed - dates,
    // relationships, the lot. Without this it keeps the values it was rendered
    // with until the page is reloaded, and the member cannot tell whether the
    // edit saved.
    _refreshOpenPanel() {
        document.dispatchEvent(new CustomEvent("detail-modal:refresh"))
    }

    // --- Same person, twice -------------------------------------------------

    openMerge(event) {
        event?.preventDefault()
        if (!this.hasMergeOverlayTarget || this._personNodeId == null) return

        this._mergeFoldId = this._personNodeId
        let node = this.nodesById.get(this._mergeFoldId)
        let name = node ? node.name : "this entry"
        if (this.hasMergeSubjectTarget) {
            // Named in the dialog, because after the person modal closes there
            // is nothing else on screen saying which entry is about to go.
            this.mergeSubjectTarget.textContent = `${name} will be folded into the entry you pick, and then removed.`
        }
        this.mergeKeepIdTarget.value = ""
        // Do not offer the entry being folded as the entry to keep. The submit
        // refuses it anyway, but a list that contains the obviously-wrong answer
        // is a list you have to read carefully.
        let pickerRoot = this.mergeOverlayTarget.querySelector("[data-controller~='contact-picker']")
        if (pickerRoot) pickerRoot.dataset.contactPickerExcludeIdValue = String(this._mergeFoldId)
        let picker = this.mergeOverlayTarget.querySelector(".contact-picker input[type=text]")
        if (picker) picker.value = ""
        this._setMergeError(null)
        this.closePersonModal()
        this.mergeOverlayTarget.hidden = false
        picker?.focus()
    }

    closeMerge(event) {
        event?.preventDefault()
        if (this.hasMergeOverlayTarget) this.mergeOverlayTarget.hidden = true
    }

    mergeOverlayClick(event) {
        if (event.target === this.mergeOverlayTarget) this.closeMerge(event)
    }

    async submitMerge(event) {
        event.preventDefault()
        let keepId = this.mergeKeepIdTarget.value
        if (!keepId) return this._setMergeError("Pick the entry to keep.")
        if (String(keepId) === String(this._mergeFoldId)) return this._setMergeError("That is the same entry.")

        let base = this.personOverlayTarget?.dataset.contactTreePersonUrlValue
        if (!base) return this._setMergeError("Could not merge them.")

        try {
            let body = new FormData()
            body.append("merge[keep_id]", keepId)
            let response = await fetch(`${base}/${encodeURIComponent(this._mergeFoldId)}/merge`, {
                method: "POST",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) return this._setMergeError(data.error || "Could not merge them.")

            this.closeMerge()
            showSystemToast(data.notice || "Merged.")
            await this._load({ refit: false })
            this._settleOn(data.person_id)
            this._refreshOpenPanel()
        } catch {
            this._setMergeError("Could not merge them.")
        }
    }

    _setMergeError(message) {
        if (!this.hasMergeErrorTarget) return

        this.mergeErrorTarget.textContent = message || ""
        this.mergeErrorTarget.hidden = !message
    }

    // --- Add someone -------------------------------------------------------

    openNewPerson(event) {
        event?.preventDefault()
        if (!this.hasNewPersonOverlayTarget) return

        this.newPersonNameTarget.value = ""
        this.newPersonAnchorTarget.value = ""
        let picker = this.newPersonOverlayTarget.querySelector(".contact-picker input[type=text]")
        if (picker) picker.value = ""
        this._setNewPersonError(null)
        this.newPersonOverlayTarget.hidden = false
        this.newPersonNameTarget.focus()
    }

    closeNewPerson(event) {
        event?.preventDefault()
        if (this.hasNewPersonOverlayTarget) this.newPersonOverlayTarget.hidden = true
    }

    newPersonOverlayClick(event) {
        if (event.target === this.newPersonOverlayTarget) this.closeNewPerson(event)
    }

    async submitNewPerson(event) {
        event.preventDefault()
        let name = this.newPersonNameTarget.value.trim()
        if (!name) return this._setNewPersonError("What is their name?")

        let anchorId = this.newPersonAnchorTarget.value
        if (!anchorId) return this._setNewPersonError("Pick who they're related to.")

        // The form asks "<name> is the <relation> of <person>". The endpoint
        // reads the other way round - "the anchor is <kind> of the person
        // picked" - so child and parent swap here rather than in the copy. The
        // sentence a member reads should be the one she would say.
        let inverse = { child: "parent", parent: "child", partner: "partner" }
        let kind = inverse[this.newPersonKindTarget.value] || "parent"

        let body = new FormData()
        body.append("relationship[kind]", kind)
        body.append("relationship[related_contact_name]", name)
        try {
            let response = await fetch(`${this.contactsBaseValue}/${encodeURIComponent(anchorId)}/relationships`, {
                method: "POST",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) return this._setNewPersonError(data.error || "Could not add them.")

            this.closeNewPerson()
            showSystemToast(data.notice || "Added.")
            await this._load({ refit: false })
            this._settleOn(data.person_id)
            this._refreshOpenPanel()
        } catch {
            this._setNewPersonError("Could not add them.")
        }
    }

    _setNewPersonError(message) {
        if (!this.hasNewPersonErrorTarget) return

        this.newPersonErrorTarget.textContent = message || ""
        this.newPersonErrorTarget.hidden = !message
    }

    togglePersonDeceased() {
        this._syncDeceasedRow()
    }

    toggleBirthPrecision() {
        this._setPrecision("birth", !this._yearOnly.birth, this._carryYear("birth"))
    }

    toggleDeathPrecision() {
        this._setPrecision("death", !this._yearOnly.death, this._carryYear("death"))
    }

    // Whichever field is on screen, read a year out of it - so flipping the
    // control keeps what was already typed instead of blanking it.
    _carryYear(prefix) {
        let { date, year } = this._precisionFields(prefix)
        if (this._yearOnly[prefix]) return date.value ? date.value.slice(0, 4) : year.value
        return year.value || (date.value ? date.value.slice(0, 4) : "")
    }

    _precisionFields(prefix) {
        return prefix === "birth"
            ? { date: this.personBirthdayTarget, year: this.personBirthYearTarget, button: this.hasBirthPrecisionTarget ? this.birthPrecisionTarget : null }
            : { date: this.personDiedOnTarget, year: this.personDeathYearTarget, button: this.hasDeathPrecisionTarget ? this.deathPrecisionTarget : null }
    }

    // One of the two inputs is always hidden, and only the visible one is read
    // on submit - so there is never a question of which value won.
    _setPrecision(prefix, yearOnly, value) {
        this._yearOnly = this._yearOnly || { birth: false, death: false }
        if (!this.hasPersonBirthYearTarget || !this.hasPersonDeathYearTarget) return

        this._yearOnly[prefix] = !!yearOnly
        let { date, year, button } = this._precisionFields(prefix)
        date.hidden = !!yearOnly
        year.hidden = !yearOnly
        if (yearOnly) {
            year.value = value == null ? "" : String(value)
            date.value = ""
        } else if (value != null && String(value).length === 4 && !date.value) {
            year.value = String(value)
        }
        if (button) button.textContent = yearOnly ? "Exact date" : "Year only"
        if (prefix === "death" && this.hasDeathHintTarget) {
            this.deathHintTarget.textContent = yearOnly
                ? "Leave empty if you don't know the year."
                : "Leave empty if you don't know the date."
        }
    }

    // The date only exists once the fact does - a living person's form should
    // not be asking when they died.
    _syncDeceasedRow() {
        if (!this.hasPersonDiedRowTarget) return

        let on = this.personDeceasedTarget.checked
        this.personDiedRowTarget.hidden = !on
        if (on) return

        this.personDiedOnTarget.value = ""
        if (this.hasPersonDeathYearTarget) this.personDeathYearTarget.value = ""
    }

    closePersonModal(event) {
        event?.preventDefault()
        if (!this.hasPersonOverlayTarget) return
        this.personOverlayTarget.hidden = true
        this._personNodeId = null
    }

    personOverlayClick(event) {
        if (event.target === this.personOverlayTarget) this.closePersonModal(event)
    }

    // Only ever the visible input's value.
    _dateValue(prefix) {
        if (!this.hasPersonBirthYearTarget || !this.hasPersonDeathYearTarget) {
            return prefix === "birth" ? this.personBirthdayTarget.value : this.personDiedOnTarget.value
        }
        let { date, year } = this._precisionFields(prefix)
        return (this._yearOnly && this._yearOnly[prefix]) ? year.value.trim() : date.value
    }

    // Null means "do not touch this field" - the server only writes the keys it
    // is sent. Used for a date the form could not render: leaving it out is the
    // difference between not editing it and erasing it.
    _dateParam(prefix) {
        let value = this._dateValue(prefix)
        if (value === "" && this._unrepresentable && this._unrepresentable[prefix]) return null

        return value
    }

    async submitPerson(event) {
        event.preventDefault()
        if (this._personNodeId == null) return

        let name = this.personNameTarget.value.trim()
        if (!name) {
            this._setPersonError("A name is needed.")
            return
        }

        let base = this.personOverlayTarget.dataset.contactTreePersonUrlValue
        try {
            // A bare "2012" and an ISO date go through the same field;
            // Genealogy::PartialDate tells them apart.
            let person = {
                name,
                gender: this.hasPersonGenderTarget ? this.personGenderTarget.value : "",
                birthday: this._dateParam("birth"),
                deceased: this.hasPersonDeceasedTarget ? this.personDeceasedTarget.checked : false,
                died_on: this.hasPersonDiedOnTarget ? this._dateParam("death") : ""
            }
            // Only sent where the fields exist: the per-user tree edits
            // Contacts, which have no birthplace to write to.
            PLACE_FIELDS.forEach(([key, target]) => {
                if (this[`has${target[0].toUpperCase()}${target.slice(1)}Target`]) person[key] = this[`${target}Target`].value.trim()
            })
            // An absent key is left alone by update_person; a null one would
            // arrive as "" and clear the column.
            Object.keys(person).forEach((key) => { if (person[key] === null) delete person[key] })
            let response = await fetch(`${base}/${encodeURIComponent(this._personNodeId)}`, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
                body: JSON.stringify({ person })
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) {
                this._setPersonError(data.error || "Could not save that.")
                return
            }
            this.closePersonModal()
            showSystemToast(data.notice || "Saved.")
            this._refreshOpenPanel()
            // Re-read rather than patching the card in place: a rename changes
            // sort order in the table and the outline, which only a re-render
            // gets right. Keep the pan and zoom.
            this._load({ refit: false })
        } catch {
            this._setPersonError("Could not save that.")
        }
    }

    _setPersonError(message) {
        if (!this.hasPersonErrorTarget) return
        this.personErrorTarget.textContent = message || ""
        this.personErrorTarget.hidden = !message
    }

    // A new descendancy root. Kept in the URL rather than in memory so the
    // outline you are looking at is the one you can send someone — that is the
    // whole point of the view (descendancy_view's header), and it is what lets
    // a share link carry a root. replaceState rather than a visit: the payload
    // is already loaded and re-rendering is instant, so a round trip would only
    // throw away the scroll position.
    _setRoot(id) {
        this.rootValue = String(id || "")
        let url = new URL(window.location.href)
        if (this.rootValue) url.searchParams.set("root", this.rootValue)
        else url.searchParams.delete("root")
        window.history.replaceState({}, "", url)
        this._render()
    }

    // Which layout to draw. The server resolves and remembers the choice and
    // passes it as a Stimulus value; anything unrecognised (or absent, as on
    // the org chart) falls back to the chart.
    _activeView() {
        let view = String(this.viewValue || "")
        return VIEWS.includes(view) ? view : "tree"
    }

    // Graph indices every view shares: who are this person's children,
    // parents, partners, and is anyone linked to them at all.
    // Tick or untick one filter. Every menu item is also a working link to the
    // same selection, so this is an enhancement, not the mechanism: without it
    // the click navigates and the server renders the same tree.
    togglePeople(event) {
        let key = event.currentTarget?.dataset?.peopleKey
        if (!key || !PEOPLE_KEYS.includes(key)) return

        event.preventDefault()
        let modes = this._peopleModes()
        this._setPeople(modes.includes(key) ? modes.filter((mode) => mode !== key) : [...modes, key])
    }

    clearPeople(event) {
        event?.preventDefault()
        this._setPeople([])
    }

    _setPeople(keys) {
        this.peopleValue = PEOPLE_KEYS.filter((key) => keys.includes(key)).join(",")
        // A filter changes who is on screen, so the pan and zoom it was read at
        // are about a chart that no longer exists - narrow to eight people and
        // the old transform leaves them off the edge.
        this._needsFit = true
        this._render()
        this._syncPeopleUI()
    }

    // Everything on the page that quotes the filter: the URL, the chip, the
    // ticks, each item's fallback href, and the links that carry the filter
    // across a real navigation. Kept in one place because a filter that the
    // view switcher had not heard about would hand back the whole tree the
    // moment somebody switched to the fan.
    _syncPeopleUI() {
        let modes = this._peopleModes()
        let url = new URL(window.location.href)
        this._writePeopleParam(url, modes)
        window.history.replaceState(window.history.state, "", url)

        this.element.querySelectorAll("[data-people-key]").forEach((item) => {
            let key = item.dataset.peopleKey
            let on = modes.includes(key)
            let next = on ? modes.filter((mode) => mode !== key) : [...modes, key]
            item.classList.toggle("is-active", on)
            let check = item.querySelector(".msg-chip-submenu-check")
            if (check) check.hidden = !on
            item.setAttribute("href", this._peopleUrl(url, next))
        })

        // "Everyone" is the one item with no key: it clears, and it is ticked
        // when nothing else is.
        let everyone = this.element.querySelector('[data-action*="contact-tree#clearPeople"]')
        if (everyone) {
            everyone.classList.toggle("is-active", !modes.length)
            let check = everyone.querySelector(".msg-chip-submenu-check")
            if (check) check.hidden = modes.length > 0
            everyone.setAttribute("href", this._peopleUrl(url, []))
        }

        this.element.querySelectorAll("[data-people-carrier]").forEach((link) => {
            let href = new URL(link.getAttribute("href"), window.location.origin)
            this._writePeopleParam(href, modes)
            link.setAttribute("href", `${href.pathname}${href.search}${href.hash}`)
        })

        this._syncPeopleChip(modes)
        this._syncPeopleCounts(modes)
    }

    // What each row would leave you with, written into the menu.
    //
    // The server cannot do this - the tree is drawn from a payload the page
    // fetches after load, so the browser is the only thing that knows how many
    // people are on your father's side. Which is also why the markup ships an
    // empty slot rather than a zero.
    //
    // The number is the size of the set you would get BY PICKING THIS ROW, with
    // the other axes still applied - not the row's own population. A badge
    // reading 43 next to a row that lands you on 12 because a second filter is
    // on is the classic misleading count, and the whole point of the badge is
    // to save a click.
    _syncPeopleCounts(modes) {
        let slots = this.element.querySelectorAll("[data-people-count]")
        if (!slots.length) return

        let all = this.allNodes || []
        let context = this._filterContext(all)
        let sizeFor = (keys) => {
            if (!keys.length) return all.length

            let keep = this._peopleKeepSet(keys, context)
            return keep ? keep.size : all.length
        }
        slots.forEach((slot) => {
            let key = slot.dataset.peopleCount
            if (key === "all") {
                slot.textContent = all.length ? String(all.length) : ""
                return
            }
            // An already-picked row keeps the selection as it stands; picking it
            // again is what turns it OFF, and reporting the count for its own
            // absence would be a number for the state you are leaving.
            let keys = modes.includes(key) ? modes : [...modes, key]
            let size = sizeFor(keys)
            slot.textContent = all.length ? String(size) : ""
        })
    }

    // The graph indices a filter walks, built over the WHOLE tree: a filter has
    // to see the real family, not whatever the last filter left behind. Shared
    // by _applyPeopleFilter and the menu's counts so the two can never disagree
    // about what a pick means.
    _filterContext(all) {
        let allEdges = this.allEdges || this.edges || []
        let parents = new Map()
        let children = new Map()
        let partners = new Map()
        allEdges.forEach((edge) => {
            if (edge.kind === "partner") {
                this._push(partners, edge.from, edge.to)
                this._push(partners, edge.to, edge.from)
            } else {
                this._push(children, edge.from, edge.to)
                this._push(parents, edge.to, edge.from)
            }
        })
        let ego = egoId({ nodes: all, nodesById: new Map(all.map((n) => [n.id, n])) })
        return { all, ego, parents, children, partners }
    }

    _writePeopleParam(url, modes) {
        if (modes.length) url.searchParams.set("people", modes.join(","))
        else url.searchParams.delete("people")
    }

    _peopleUrl(url, modes) {
        let next = new URL(url.href)
        this._writePeopleParam(next, modes)
        return `${next.pathname}${next.search}${next.hash}`
    }

    // "Filter" when nothing is picked, the pick's own word when one is, a count
    // for two or more - three short labels chained together is wider than the
    // rest of the toolbar. The title carries the full reading in every case.
    // Mirrors what the server renders on load, so a toggled chip and a reloaded
    // one say the same thing.
    _syncPeopleChip(modes) {
        if (!this.hasPeopleChipTarget) return

        let picked = modes.map((mode) => this.element.querySelector(`[data-people-key="${mode}"]`)).filter(Boolean)
        let labels = picked.map((item) => (item.querySelector(".msg-chip-submenu-label")?.textContent || "").trim())
        let shorts = picked.map((item) => item.dataset.peopleShort || "")

        this.peopleChipTarget.title = labels.length ? labels.join(" + ") : "Everyone"
        if (this.hasPeopleChipIconTarget) {
            this.peopleChipIconTarget.classList.toggle("bi-funnel-fill", modes.length > 0)
            this.peopleChipIconTarget.classList.toggle("bi-funnel", modes.length === 0)
        }
        if (this.hasPeopleChipLabelTarget) {
            this.peopleChipLabelTarget.textContent = modes.length === 1 ? shorts[0] : modes.length > 1 ? String(modes.length) : "Filter"
            // On a phone the secondary chips show icons only, and this word is
            // the exception: it survives while it is saying the tree is
            // narrowed. See _contact_tree.scss.
            this.peopleChipLabelTarget.classList.toggle("is-narrowed", modes.length > 0)
        }
    }

    // CSS owns every measurement the engine lays out with; this is where it
    // picks them up. Read per render rather than cached at connect, because the
    // answer changes when a phone is turned on its side - and now also when the
    // reader switches density, which is nothing but a second set of these
    // numbers.
    //
    // Read off THIS element rather than the document root so a class on the page
    // can override them. Custom properties inherit, so the :root defaults still
    // resolve here; what the page cannot see is .ft-rel's own --ft-card-w: 100%,
    // which belongs to the relationship chart's fluid cards and would otherwise
    // hand the engine a card one pixel wide.
    _readMetrics() {
        let styles = getComputedStyle(this.element)
        let num = (name, fallback) => {
            let value = parseFloat(styles.getPropertyValue(name))
            return Number.isFinite(value) && value > 0 ? value : fallback
        }
        this._cardW = num("--ft-card-w", CARD_W)
        this._cardH = num("--ft-card-h", CARD_H)
        this._coupleGap = num("--ft-couple-gap", COUPLE_GAP)
        this._siblingGap = num("--ft-sibling-gap", SIBLING_GAP)
        this._treeGap = num("--ft-tree-gap", TREE_GAP)
        this._rowGap = num("--ft-row-gap", ROW_GAP)
        this._pad = num("--ft-pad", PAD)
    }

    // Turning the phone crosses the breakpoint, which changes the card size,
    // which changes every position the engine worked out. Redrawn and refitted,
    // because a layout measured at the other size leaves people off the edge.
    _bindCardSizeBreakpoint() {
        if (typeof window.matchMedia !== "function") return

        this._cardSizeQuery = window.matchMedia("(max-width: 640px)")
        this._onCardSizeChange = () => {
            // Table columns resize in CSS; rebuilding discards its sort, gap
            // filter and pagination when a phone rotates or a sidebar opens.
            if (this._activeView() === "table") return
            this._needsFit = true
            this._render()
        }
        this._cardSizeQuery.addEventListener("change", this._onCardSizeChange)
    }

    // Whatever is ticked, in payload order, unknown keys dropped. The value is
    // a comma list because the URL is - see shared_family_trees/show.
    _peopleModes() {
        let raw = (this.hasPeopleValue ? this.peopleValue : "") || ""
        let picked = raw
            .split(",")
            .map((key) => key.trim())
            .filter(Boolean)
        return PEOPLE_KEYS.filter((key) => picked.includes(key))
    }

    // Reduce the payload to the people this filter is about. Always a SUBSET -
    // never a re-layout, never a different chart - which is what lets one
    // control mean the same thing in six views.
    _applyPeopleFilter() {
        let all = this.allNodes || this.nodes || []
        let allEdges = this.allEdges || this.edges || []
        let modes = this._peopleModes()
        // Focus rides the same machinery as the branch/status filter for one
        // reason: that filter reduces the SET of people before anything draws,
        // so the chart, the fan, the table, the timeline and the outline all
        // narrow the same way for free. A focus that only worked on the chart
        // would be a sixth thing to learn per view.
        let focus = this._focusTarget()
        let keep = null
        if (focus) keep = this._focusKeepSet(focus, all, allEdges)
        else if (modes.length && all.length) keep = this._peopleKeepSet(modes, this._filterContext(all))

        this.nodes = keep ? all.filter((node) => keep.has(node.id)) : all
        this.edges = keep ? allEdges.filter((edge) => keep.has(edge.from) && keep.has(edge.to)) : allEdges
        this._applySearchNarrowing(allEdges)
    }

    // "Show only matches", the last narrowing of the three and the only one
    // that is not in the URL.
    //
    // A search DIMS by default and should: while you are reading, the hundred
    // people you did not ask about are the context that makes the three you did
    // mean anything. While you are hunting they are a hundred cards you still
    // have to drag past, and this puts them away - the chart re-lays out around
    // the matches, so five people fill the screen instead of hiding in it.
    //
    // Matches keep their partners, so no couple is cut in half, and a query
    // that matches nobody narrows to nothing - so it deliberately does not
    // apply, leaving a dimmed tree rather than a blank canvas with no way back.
    _applySearchNarrowing(allEdges) {
        if (!this._searchOnly || !this._searchQuery) return

        let matches = this.nodes.filter((node) => this._nodeMatchesSearch(node, this._searchQuery))
        if (!matches.length) return

        let keep = new Set(matches.map((node) => node.id))
        ;(allEdges || []).forEach((edge) => {
            if (edge.kind !== "partner") return
            if (keep.has(edge.from)) keep.add(edge.to)
            if (keep.has(edge.to)) keep.add(edge.from)
        })
        this.nodes = this.nodes.filter((node) => keep.has(node.id))
        this.edges = this.edges.filter((edge) => keep.has(edge.from) && keep.has(edge.to))
    }

    // The toggle itself. Ephemeral on purpose - it belongs to this search, not
    // to this reader, so it is neither stored nor put in the URL and a fresh
    // page starts by dimming again.
    toggleSearchOnly(event) {
        event?.preventDefault()
        this._searchOnly = !this._searchOnly
        this._needsFit = true
        this._render()
        this._syncSearchOnlyButton()
    }

    // Only offered while something is typed: a "show only matches" button over
    // an empty search box is a control for a state that does not exist.
    _syncSearchOnlyButton() {
        if (!this.hasSearchOnlyTarget) return

        let active = !!this._searchOnly
        this.searchOnlyTarget.hidden = !this._searchQuery
        this.searchOnlyTarget.classList.toggle("is-active", active)
        this.searchOnlyTarget.setAttribute("aria-pressed", active ? "true" : "false")
        let label = active ? "Show the whole tree" : "Show only matches"
        this.searchOnlyTarget.setAttribute("title", label)
        this.searchOnlyTarget.setAttribute("aria-label", label)
    }

    // --- Focus, and the relationship trace --------------------------------
    //
    // Both live in the URL and nowhere else, which is what makes them
    // shareable: "here is how you fit into this family" is a link, and it works
    // on the public share too, where the reader has no account to hold state.

    // { id, steps } or null. steps is how far out from that person to reach -
    // 1 is the household (parents, partners, children, siblings), and each
    // press of "Show more" adds a generation.
    _focusTarget() {
        let raw = (this.hasFocusValue ? this.focusValue : "").toString().trim()
        if (!raw) return null

        let [id, steps] = raw.split(":")
        let personId = Number(id)
        if (!Number.isFinite(personId)) return null
        if (!(this.allNodes || []).some((node) => node.id === personId)) return null

        return { id: personId, steps: Math.min(Math.max(Number(steps) || 1, 1), 6) }
    }

    // Everyone within `steps` edges, walking parent and partner links in both
    // directions. A partner hop is deliberately free at the boundary: a focus
    // that showed your uncle but not his wife would look like a mistake rather
    // than a radius.
    _focusKeepSet(focus, all, allEdges) {
        let adjacency = new Map()
        let push = (map, from, to) => { if (!map.has(from)) map.set(from, []); map.get(from).push(to) }
        allEdges.forEach((edge) => {
            push(adjacency, edge.from, edge.to)
            push(adjacency, edge.to, edge.from)
        })

        let keep = new Set([focus.id])
        let frontier = [focus.id]
        for (let step = 0; step < focus.steps; step++) {
            let next = []
            frontier.forEach((id) => {
                (adjacency.get(id) || []).forEach((other) => {
                    if (keep.has(other)) return

                    keep.add(other)
                    next.push(other)
                })
            })
            frontier = next
        }
        // Partners of everyone on the edge, so no couple is cut in half.
        let edgeOfSet = [...keep]
        allEdges.forEach((edge) => {
            if (edge.kind !== "partner") return
            if (keep.has(edge.from) && !keep.has(edge.to)) edgeOfSet.push(edge.to)
            if (keep.has(edge.to) && !keep.has(edge.from)) edgeOfSet.push(edge.from)
        })
        edgeOfSet.forEach((id) => keep.add(id))
        // A focus that filtered everybody out would be an empty canvas with no
        // way back; the whole tree is a better answer than nothing.
        return keep.size > 1 ? keep : new Set(all.map((node) => node.id))
    }

    // OR within an axis, AND across them. "Father's side, Mother's side" is
    // both branches; "Father's side, No longer living" is the dead on that
    // branch. Anything else - two branches AND'd together, say - would answer
    // a question nobody asks and usually return nobody at all.
    _peopleKeepSet(modes, context) {
        let { ego } = context
        let keep = null

        PEOPLE_AXES.forEach((axis) => {
            let picked = axis.keys.filter((key) => modes.includes(key))
            if (!picked.length) return

            let union = new Set()
            let resolved = false
            picked.forEach((mode) => {
                let set = this._filterSet(mode, context)
                if (!set) return

                resolved = true
                set.forEach((id) => union.add(id))
            })
            // Nothing in this axis could be worked out - no ego, or no father
            // on record. It constrains nothing rather than emptying the tree,
            // which is what a single unresolvable filter has always done.
            if (!resolved) return

            // You are always in your own tree when the filter is ABOUT you - a
            // side or a pedigree is drawn from you and makes no sense without
            // you. Added to the branch union BEFORE the axes meet, so "my
            // father's side, no longer living" still does not list you among
            // the dead.
            if (ego != null && axis.includesEgo) union.add(ego)

            keep = keep == null ? union : new Set([...keep].filter((id) => union.has(id)))
        })

        return keep
    }

    _filterSet(mode, { all, ego, parents, children, partners }) {
        if (mode === "living") return new Set(all.filter((n) => this._probablyLiving(n)).map((n) => n.id))
        if (mode === "deceased") return new Set(all.filter((n) => !this._probablyLiving(n)).map((n) => n.id))
        if (ego == null) return null

        if (mode === "ancestors") {
            // The pedigree reading: you and the people you descend from, with
            // nobody married in and no cousins.
            return this._closure([ego], parents)
        }
        if (mode.startsWith("gen")) {
            // Generations from you, the same count the timeline bands by and
            // "Collapse all" folds by - so "two generations" means one set of
            // people everywhere it is said. Symmetric on purpose: a reader
            // narrowing a big tree wants their grandparents AND their
            // grandchildren, not a pedigree (which is what "Direct ancestors"
            // is for, one axis up).
            let reach = Number(mode.slice(3))
            if (!Number.isFinite(reach)) return null

            let gen = generationsFromEgo({ egoId: ego, parentsOf: parents, childrenOf: children, partnersOf: partners })
            if (!gen) return null

            let keep = new Set()
            gen.forEach((depth, id) => { if (Math.abs(depth) <= reach) keep.add(id) })
            // Nobody married in is left standing alone at the boundary: a
            // reach that showed your uncle but not his wife reads as a bug
            // rather than as a radius. Same rule the focus lens uses.
            new Set(keep).forEach((id) => (partners.get(id) || []).forEach((pid) => keep.add(pid)))
            return keep
        }
        if (mode === "paternal" || mode === "maternal") {
            // A "side" is a whole branch, not a single line: the parent's
            // ancestors AND everyone descended from them, which is what makes
            // aunts, uncles and cousins belong to a side at all.
            let side = this._sideParent(mode, ego, parents, all)
            if (side == null) return null

            let up = this._closure([side], parents)
            let out = this._closure([...up], children)
            up.forEach((id) => (partners.get(id) || []).forEach((pid) => out.add(pid)))
            out.forEach((id) => (partners.get(id) || []).forEach((pid) => out.add(pid)))
            return out
        }
        return null
    }

    // Which parent heads this side. Gender is the honest signal and is often
    // missing, so fall back to the relation label the payload already computed
    // rather than guessing from names.
    _sideParent(mode, ego, parents, all) {
        let byId = new Map(all.map((n) => [n.id, n]))
        let ids = parents.get(ego) || []
        let want = mode === "paternal" ? "male" : "female"
        let byGender = ids.find((id) => byId.get(id)?.gender === want)
        if (byGender != null) return byGender

        let label = mode === "paternal" ? /^father$/i : /^mother$/i
        let byLabel = ids.find((id) => label.test(byId.get(id)?.relation || ""))
        return byLabel == null ? null : byLabel
    }

    // Not the same as "nobody ticked the box". Three people in one real tree are
    // recorded with 1880s birth years and no deceased flag, because nobody has
    // got round to it - and a Living filter that hands you a 141-year-old is
    // wrong in a way the reader cannot argue with. An impossible age counts as
    // evidence; the flag stays the only thing that MARKS somebody, this just
    // stops the filter asserting the opposite.
    _probablyLiving(node) {
        if (node.deceased) return false

        let born = birthOf(node)
        if (!born.known) return true

        return (new Date().getFullYear() - born.year) <= MAX_LIVING_AGE
    }

    // Everything reachable from `startIds` through `edges`, inclusive.
    _closure(startIds, edges) {
        let seen = new Set(startIds)
        let queue = [...startIds]
        let guard = 0
        while (queue.length && guard++ < 8192) {
            let id = queue.shift()
            ;(edges.get(id) || []).forEach((next) => {
                if (seen.has(next)) return

                seen.add(next)
                queue.push(next)
            })
        }
        return seen
    }

    _buildIndices() {
        // Generations are counted from the ego over the current node set, so a
        // filter change or a new person must not reuse the last answer.
        this._generationCache = undefined
        this._minGeneration = 0
        this.nodesById = new Map(this.nodes.map((node) => [node.id, node]))
        this.childrenOf = new Map()
        this.parentsOf = new Map()
        this.partnersOf = new Map()
        this.linked = new Set()
        // "from|to" -> how well that link is supported. Read by the chart, so a
        // parentage nobody has evidence for looks different from one somebody
        // holds the certificate for.
        this.edgeConfidence = new Map()
        this.edgeIds = new Map()

        this.edges.forEach((edge) => {
            if (!this.nodesById.has(edge.from) || !this.nodesById.has(edge.to)) return
            this.linked.add(edge.from)
            this.linked.add(edge.to)
            this.edgeConfidence.set(`${edge.from}|${edge.to}`, edge.confidence || "unverified")
            this.edgeIds.set(`${edge.from}|${edge.to}`, edge.id)
            if (edge.kind === "partner") {
                this._push(this.partnersOf, edge.from, edge.to)
                this._push(this.partnersOf, edge.to, edge.from)
            } else {
                this._push(this.childrenOf, edge.from, edge.to)
                this._push(this.parentsOf, edge.to, edge.from)
            }
        })
    }

    // A big chart opens legible or it opens useless: 150+ cards laid out flat
    // fit the viewport only as unreadable dust. Fold everything below the
    // first two levels of every root (CEO = 0, their reports = 1, the level
    // under those = 2, collapsed; on a family tree the founders of each line
    // take the CEO's place) so the chart opens on its SHAPE; every folded
    // branch keeps its "+" toggle, the collapsed notice offers "expand all",
    // and search still reaches everyone. Seeded ONCE per mount, the first time
    // the chart is big enough — never re-seeded over the user's own expanding
    // and collapsing (a background poll refresh keeps their state).
    //
    // A family tree adds one thing the org chart does not need: your own
    // chain stays open. Opening on your card only to find it behind a "+"
    // twelve generations under a founder is worse than the dust was.
    _seedCollapse() {
        if (this._collapseSeeded) return
        let family = this.kindValue === "family"
        if (this.kindValue !== "org" && !family) return
        if (this.nodes.length < (family ? FAMILY_COLLAPSE_MIN_PEOPLE : ORG_COLLAPSE_MIN_PEOPLE)) return

        this._collapseSeeded = true
        let foldDepth = family
            ? (this.nodes.length >= FAMILY_HUGE_PEOPLE ? FAMILY_COLLAPSE_DEPTH_HUGE : FAMILY_COLLAPSE_DEPTH)
            : ORG_COLLAPSE_DEPTH
        if (family) this.element.dataset.foldDepth = String(foldDepth)
        let queue = this.nodes
            .filter((node) => this.linked.has(node.id) && !(this.parentsOf.get(node.id) || []).length)
            .map((node) => ({ id: node.id, depth: 0 }))
        let seen = new Set(queue.map((entry) => entry.id))
        while (queue.length) {
            let { id, depth } = queue.shift()
            let children = this.childrenOf.get(id) || []
            if (depth >= foldDepth && children.length) this.collapsed.add(id)
            children.forEach((childId) => {
                if (seen.has(childId)) return
                seen.add(childId)
                queue.push({ id: childId, depth: depth + 1 })
            })
        }
        if (family && this._egoId != null) {
            // Both parents' lines, not just the first - _expandAncestorsFor
            // follows one parent and is for revealing a search hit.
            this._closure([this._egoId], this.parentsOf).forEach((id) => {
                this.collapsed.delete(id)
                ;(this.partnersOf.get(id) || []).forEach((partnerId) => this.collapsed.delete(partnerId))
            })
        }
    }

    // Shared lineage model for the compact minimap and guided tour.
    _prepareOverview(view) {
        this._overview = null
        if (!this.hasViewportTarget || this.kindValue !== "family" || !CHART_VIEWS.includes(view)) return
        if (this.nodes.length < OVERVIEW_MIN_PEOPLE) return
        this._overview = this._overviewModel()
    }

    _overviewModel() {
        let year = new Date().getFullYear()
        let yearOf = new Map()
        this.nodes.forEach((node) => {
            let born = birthOf(node)
            if (born.known) yearOf.set(node.id, born.year)
        })
        // Lineages: connected components over every kind of link.
        let neighbours = new Map()
        let link = (a, b) => {
            if (!neighbours.has(a)) neighbours.set(a, [])
            neighbours.get(a).push(b)
        }
        this.edges.forEach((edge) => {
            if (!this.nodesById.has(edge.from) || !this.nodesById.has(edge.to)) return
            link(edge.from, edge.to)
            link(edge.to, edge.from)
        })
        let seen = new Set()
        let lineages = []
        this.nodes.forEach((node) => {
            if (seen.has(node.id)) return
            let members = [...this._closure([node.id], neighbours)]
            members.forEach((id) => seen.add(id))
            lineages.push(this._lineageOf(members, yearOf))
        })
        lineages.sort((a, b) => b.members.length - a.members.length)

        let ancestors = this._egoId != null ? this._closure([this._egoId], this.parentsOf) : new Set()
        let years = [...yearOf.values()]
        let deaths = this.nodes.map((node) => deathOf(node)).filter((death) => death.known).map((death) => death.year)
        let living = this.nodes.some((node) => this._probablyLiving(node))
        let places = new Set()
        let citations = 0
        this.nodes.forEach((node) => {
            ;[node.birth_place, node.death_place, node.location].forEach((place) => { if (place) places.add(place) })
            citations += Number(node.evidence?.sources) || 0
        })
        return {
            people: this.nodes.length,
            lineages,
            generations: Math.max(0, ...lineages.map((lineage) => lineage.generations)),
            minYear: years.length ? Math.min(...years) : null,
            maxYear: living ? year : (years.length || deaths.length ? Math.max(...years, ...deaths) : null),
            places: places.size,
            citations,
            yearOf,
            ancestors,
            egoId: this._egoId
        }
    }

    // One connected family: who founded it, how deep it goes, who is in it.
    _lineageOf(members, yearOf) {
        let memberSet = new Set(members)
        let roots = members.filter((id) => !(this.parentsOf.get(id) || []).some((parentId) => memberSet.has(parentId)))
        // The founder is the oldest root CARRYING THE FAMILY'S NAME, then the
        // oldest root at all. Year first named the Flemish Gunst line after
        // Joanna Fiers, born two years before the husband whose name the other
        // 570 people carry - true, and not what anybody calls that line.
        let surnames = new Map()
        members.forEach((id) => {
            let name = this.nodesById.get(id)?.name || ""
            let surname = name.trim().split(/\s+/).pop() || ""
            surnames.set(surname, (surnames.get(surname) || 0) + 1)
        })
        let familyName = [...surnames.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
        let bears = (id) => ((this.nodesById.get(id)?.name || "").trim().split(/\s+/).pop() === familyName ? 0 : 1)
        roots.sort((a, b) => bears(a) - bears(b) || (yearOf.get(a) ?? 9999) - (yearOf.get(b) ?? 9999))
        let founderId = roots[0] ?? members[0]

        // Longest root-to-leaf chain, counted in people.
        let depth = new Map()
        let stack = new Set()
        let depthOf = (id) => {
            if (depth.has(id)) return depth.get(id)
            if (stack.has(id)) return 0
            stack.add(id)
            let parents = (this.parentsOf.get(id) || []).filter((parentId) => memberSet.has(parentId))
            let value = parents.length ? 1 + Math.max(...parents.map(depthOf)) : 0
            stack.delete(id)
            depth.set(id, value)
            return value
        }
        let generations = 1 + Math.max(0, ...members.map(depthOf))
        let knownYears = members.map((id) => yearOf.get(id)).filter((year) => year != null)
        return {
            members,
            founderId,
            founderName: this.nodesById.get(founderId)?.name || "",
            founderYear: yearOf.get(founderId) ?? null,
            fromYear: knownYears.length ? Math.min(...knownYears) : null,
            generations
        }
    }

    _landOn(id) {
        if (id == null) return
        if (this._expandAncestorsFor([id])) this._render()
        this.scale = this._density === "compact" ? MIN_OPEN_SCALE_COMPACT : MIN_OPEN_SCALE
        this._focusMatch(id)
        let card = this.treeTarget?.querySelector(`[data-node-id="${CSS.escape(String(id))}"]`)
        if (!card) return
        card.querySelector(".contact-tree-card-open")?.focus({ preventScroll: true })
        card.classList.add("contact-tree-card--landed")
        window.setTimeout(() => card.classList.remove("contact-tree-card--landed"), 2200)
    }

    // --- The minimap --------------------------------------------------------
    //
    // A big tree's map, in the corner: every drawn card as a speck coloured by
    // lineage, the viewport as a rectangle you can drag, the tree's size in
    // figures above and its lineages listed below, each a button that lands
    // on that line's founder. It is what lets a stranger find their way round
    // a 1,500-person forest without the page choosing a starting line for
    // them, and it is drawn from the layout the chart just produced (cardPos),
    // so it shows exactly what is drawn - folded branches included, as gaps.
    // Only on a tree big enough to need one (the overview's threshold), and
    // only on the chart views, which are the ones with a camera.
    _renderMinimap() {
        this._minimapEl?.remove()
        this._minimapEl = null
        this._minimap = null
        this._minimapPlayBtn = null
        this._minimapPlayIcon = null
        this._minimapPlayLabel = null
        if (!this.hasViewportTarget || !this._overview || this._panZoom === false || !this.cardPos?.size) return

        let model = this._overview
        let lineageOf = new Map()
        model.lineages.forEach((lineage, index) => lineage.members.forEach((id) => lineageOf.set(id, index)))

        let pane = document.createElement("aside")
        pane.className = "contact-tree-minimap"
        pane.setAttribute("aria-label", "Map of the tree")
        // A click or a scroll in here is reading the map, not steering the
        // chart underneath - it must never register as the tour's "hands-on
        // gesture wins" interruption (see _onPointerDown/_onWheel) or as a pan.
        pane.addEventListener("pointerdown", (event) => event.stopPropagation())
        pane.addEventListener("wheel", (event) => event.stopPropagation())

        // The header row: what this pane is, the tour control, and the
        // collapse toggle - always present, even collapsed or on a phone, so
        // Play and the toggle itself are never the thing that gets hidden.
        let header = document.createElement("div")
        header.className = "contact-tree-minimap-header"
        let label = document.createElement("span")
        label.className = "contact-tree-minimap-label"
        label.textContent = "Map"
        header.appendChild(label)

        let playBtn = document.createElement("button")
        playBtn.type = "button"
        playBtn.className = "contact-tree-minimap-play"
        let playIcon = document.createElement("i")
        let playLabel = document.createElement("span")
        playBtn.append(playIcon, playLabel)
        playBtn.addEventListener("click", (event) => {
            event.preventDefault()
            this._toggleTourPlay()
        })
        header.appendChild(playBtn)
        this._minimapPlayBtn = playBtn
        this._minimapPlayIcon = playIcon
        this._minimapPlayLabel = playLabel

        let toggleBtn = document.createElement("button")
        toggleBtn.type = "button"
        toggleBtn.className = "contact-tree-minimap-toggle"
        let toggleIcon = document.createElement("i")
        toggleBtn.appendChild(toggleIcon)
        toggleBtn.addEventListener("click", (event) => {
            event.preventDefault()
            this._setMinimapCollapsed(!this._minimapCollapsed)
        })
        header.appendChild(toggleBtn)

        // One compact line rather than the figure grid it replaces - the same
        // facts, a quarter of the height.
        let figuresLine = document.createElement("div")
        figuresLine.className = "contact-tree-minimap-figures"
        let parts = []
        if (model.people) parts.push(`${model.people.toLocaleString()} ${model.people === 1 ? "person" : "people"}`)
        if (model.lineages.length > 1) parts.push(`${model.lineages.length.toLocaleString()} ${model.lineages.length === 1 ? "lineage" : "lineages"}`)
        // Keep the compact summary on one line where space allows.
        if (model.minYear != null && model.maxYear != null) parts.push(`${model.minYear}–${model.maxYear}`)
        figuresLine.textContent = parts.join(" · ")

        let canvas = document.createElement("canvas")
        canvas.className = "contact-tree-minimap-canvas"
        canvas.setAttribute("role", "img")
        canvas.setAttribute("aria-label", "Where you are in the tree. Click or drag to move.")

        let list = document.createElement("ol")
        list.className = "contact-tree-minimap-lines"
        // Which lines to list: the first few, or every one once "N smaller
        // lines" has been pressed. Remembered on the controller (not the DOM)
        // so a re-render mid-read does not fold the list back up.
        let showAll = !!this._minimapAll && model.lineages.length > MINIMAP_LINES
        list.classList.toggle("contact-tree-minimap-lines--all", showAll)
        let shown = showAll ? model.lineages : model.lineages.slice(0, MINIMAP_LINES)
        shown.forEach((lineage, index) => {
            let item = document.createElement("li")
            let button = document.createElement("button")
            button.type = "button"
            button.className = "contact-tree-minimap-line"
            button.dataset.lineageFounder = String(lineage.founderId)
            let dot = document.createElement("i")
            dot.className = "contact-tree-minimap-dot"
            dot.style.background = index < OVERVIEW_BANDS ? OVERVIEW_COLORS[index % OVERVIEW_COLORS.length] : OVERVIEW_REST_COLOR
            // Hovering a line lights its specks on the map and dims the rest -
            // the list and the map are two views of the same thing.
            button.addEventListener("pointerenter", () => this._hoverLineage(index))
            button.addEventListener("pointerleave", () => this._hoverLineage(null))
            let name = document.createElement("span")
            name.className = "contact-tree-minimap-line-name"
            let yearLabel = lineage.founderYear ? String(lineage.founderYear) : (lineage.fromYear ? `from ${lineage.fromYear}` : "")
            name.textContent = lineage.founderName + (yearLabel ? ` · ${yearLabel}` : "")
            let count = document.createElement("span")
            count.className = "contact-tree-minimap-line-count"
            count.textContent = lineage.members.length.toLocaleString()
            button.append(dot, name, count)
            button.title = `${lineage.members.length.toLocaleString()} people · ${lineage.generations} generations`
            button.addEventListener("click", (event) => {
                event.preventDefault()
                if (window.matchMedia("(max-width: 575.98px)").matches) this._setMinimapCollapsed(true)
                this._landOn(lineage.founderId)
            })
            item.appendChild(button)
            list.appendChild(item)
        })
        if (model.lineages.length > MINIMAP_LINES) {
            let item = document.createElement("li")
            let more = document.createElement("button")
            more.type = "button"
            more.className = "contact-tree-minimap-more"
            let rest = model.lineages.length - MINIMAP_LINES
            more.textContent = showAll ? "Show fewer" : `${rest} smaller ${rest === 1 ? "line" : "lines"}`
            more.setAttribute("aria-expanded", showAll ? "true" : "false")
            more.addEventListener("click", (event) => {
                event.preventDefault()
                this._minimapAll = !showAll
                this._renderMinimap()
                let target = this._minimapListEl.querySelector(showAll ? ".contact-tree-minimap-more" : `li:nth-child(${MINIMAP_LINES + 1}) button`)
                target?.focus({ preventScroll: true })
                target?.scrollIntoView({ block: "nearest" })
            })
            item.appendChild(more)
            list.appendChild(item)
        }

        pane.append(header, figuresLine, canvas, list)
        this.viewportTarget.appendChild(pane)
        this._minimapEl = pane
        this._minimap = { canvas, lineageOf, label, names: model.lineages.map((lineage) => lineage.founderName) }
        this._minimapHover = null
        this._minimapToggleBtn = toggleBtn
        this._minimapToggleIcon = toggleIcon
        this._minimapFiguresEl = figuresLine
        this._minimapListEl = list
        this._minimapCollapsed = this._readMinimapCollapsed()
        this._applyMinimapCollapsed()
        this._syncPlayButton()
        this._bindMinimap(canvas)
        this._syncMinimap()
    }

    // localStorage THROWS in a private window rather than returning null -
    // same rule as _storedDensity, and for the same reason: a failure here
    // degrades to "expanded", never to a broken pane.
    _readMinimapCollapsed() {
        try {
            let saved = window.localStorage.getItem(MINIMAP_STORAGE_KEY)
            return saved ? saved === "collapsed" : window.matchMedia("(max-width: 575.98px)").matches
        } catch {
            return window.matchMedia("(max-width: 575.98px)").matches
        }
    }

    _setMinimapCollapsed(collapsed) {
        this._minimapCollapsed = collapsed
        try {
            window.localStorage.setItem(MINIMAP_STORAGE_KEY, collapsed ? "collapsed" : "open")
        } catch {
            // No store to write to - the pane still reflects the choice for
            // the rest of this render.
        }
        this._applyMinimapCollapsed()
        this._syncMinimap()
    }

    // Collapsed keeps only the header and the map itself; the figures line and
    // the lineage list disappear, and the pane keeps its width so nothing else
    // in the corner jumps when it does.
    _applyMinimapCollapsed() {
        if (!this._minimapEl) return
        let collapsed = this._minimapCollapsed
        this._minimapEl.classList.toggle("contact-tree-minimap--collapsed", collapsed)
        if (this._minimapFiguresEl) this._minimapFiguresEl.hidden = collapsed
        if (this._minimapListEl) this._minimapListEl.hidden = collapsed
        if (this._minimapToggleIcon) this._minimapToggleIcon.className = collapsed ? "bi bi-chevron-up" : "bi bi-chevron-down"
        if (this._minimapToggleBtn) {
            this._minimapToggleBtn.setAttribute("aria-label", collapsed ? "Expand the map" : "Collapse the map")
            this._minimapToggleBtn.setAttribute("aria-expanded", collapsed ? "false" : "true")
        }
    }

    // The stage mapped onto the canvas. A folded forest is sixty times wider
    // than it is tall, and at true scale that is a hairline across an empty
    // box - so the long axis WRAPS, the way a line of text does: the stage is
    // cut into up to six equal segments drawn as stacked rows (or, for a tall
    // landscape stage, side-by-side columns), each card and the viewport
    // rectangle landing in whichever segment holds it. `toCanvas`/`toStage`
    // are the only two places that know the wrapping, so everything else
    // draws in stage coordinates and asks.
    _minimapGeometry() {
        let stage = this.treeTarget
        let sw = Math.max(stage.offsetWidth, stage.scrollWidth, 1)
        let sh = Math.max(stage.offsetHeight, stage.scrollHeight, 1)
        let { canvas } = this._minimap
        let cw = canvas.clientWidth || 228
        let pad = 4
        let gap = 3
        let wrapX = sw >= sh
        let longLen = wrapX ? sw : sh
        let shortLen = wrapX ? sh : sw
        let aspect = longLen / shortLen
        // Segments: enough that each row is a readable ~26px thick, capped so
        // the box stays a corner pane. Collapsed, one strip at floor height.
        let n = this._minimapCollapsed || aspect <= 3 ? 1 : Math.min(6, Math.ceil((aspect * 26) / (wrapX ? cw - 2 * pad : 124)))
        let segLen = longLen / n
        let k
        let ch
        if (wrapX) {
            k = Math.min((cw - 2 * pad) / segLen, (this._minimapCollapsed ? 36 : 60) / shortLen)
            ch = Math.round(2 * pad + n * shortLen * k + (n - 1) * gap)
        } else {
            ch = this._minimapCollapsed ? 44 : 132
            k = Math.min((ch - 2 * pad) / segLen, (cw - 2 * pad - (n - 1) * gap) / (n * shortLen))
        }
        ch = Math.max(ch, 44)
        if (Math.abs(canvas.clientHeight - ch) > 1) canvas.style.height = `${ch}px`
        let thick = shortLen * k
        // Centre the drawing on its short axis.
        let ox = wrapX ? pad : (cw - (n * thick + (n - 1) * gap)) / 2
        let oy = wrapX ? (ch - (n * thick + (n - 1) * gap)) / 2 : pad
        let clampSeg = (i) => Math.min(n - 1, Math.max(0, i))
        let toCanvas = (x, y) => {
            if (wrapX) {
                let i = clampSeg(Math.floor(x / segLen))
                return { x: ox + (x - i * segLen) * k, y: oy + i * (thick + gap) + y * k, seg: i }
            }
            let i = clampSeg(Math.floor(y / segLen))
            return { x: ox + i * (thick + gap) + x * k, y: oy + (y - i * segLen) * k, seg: i }
        }
        let toStage = (px, py) => {
            if (wrapX) {
                let i = clampSeg(Math.floor((py - oy) / (thick + gap)))
                return { x: i * segLen + (px - ox) / k, y: (py - oy - i * (thick + gap)) / k }
            }
            let i = clampSeg(Math.floor((px - ox) / (thick + gap)))
            return { x: (px - ox - i * (thick + gap)) / k, y: i * segLen + (py - oy) / k }
        }
        return { sw, sh, cw, ch, k, n, segLen, thick, gap, wrapX, ox, oy, toCanvas, toStage }
    }

    // A lineage lit on the map (from the list, or from the pointer over the
    // map itself); the header names it while it is.
    _hoverLineage(index) {
        if (!this._minimap) return
        if (this._minimapHover === index) return
        this._minimapHover = index
        this._minimap.label.textContent = index == null ? "Map" : (this._minimap.names[index] || "Map")
        this._syncMinimap()
    }

    // Redrawn on every camera move: the specks are cheap (one fillRect each)
    // and the rectangle is what makes it a map rather than a picture.
    _syncMinimap() {
        if (!this._minimap || !this.hasViewportTarget) return

        let g = this._minimapGeometry()
        let { canvas, lineageOf } = this._minimap
        let dpr = window.devicePixelRatio || 1
        if (canvas.width !== Math.round(g.cw * dpr) || canvas.height !== Math.round(g.ch * dpr)) {
            canvas.width = Math.round(g.cw * dpr)
            canvas.height = Math.round(g.ch * dpr)
        }
        let ctx = canvas.getContext("2d")
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, g.cw, g.ch)

        // The segments, faintly, so a wrapped map reads as rows of one thing.
        if (g.n > 1) {
            ctx.fillStyle = "rgba(15, 23, 42, 0.035)"
            for (let i = 0; i < g.n; i++) {
                let seg = g.toCanvas(g.wrapX ? i * g.segLen : 0, g.wrapX ? 0 : i * g.segLen)
                if (g.wrapX) ctx.fillRect(g.ox, seg.y, g.segLen * g.k, g.thick)
                else ctx.fillRect(seg.x, g.oy, g.thick, g.segLen * g.k)
            }
        }

        let size = this._pt(this._mainSize(), this._crossSize())
        let w = Math.max(size.x * g.k, 1.5)
        let h = Math.max(size.y * g.k, 1.5)
        let hover = this._minimapHover
        this.cardPos.forEach((pos, id) => {
            let at = this._pt(pos.main, pos.cross)
            let index = lineageOf.get(id)
            let here = g.toCanvas(at.x, at.y)
            ctx.fillStyle = index == null || index >= OVERVIEW_BANDS ? OVERVIEW_REST_COLOR : OVERVIEW_COLORS[index % OVERVIEW_COLORS.length]
            ctx.globalAlpha = hover != null && index !== hover ? 0.15 : (id === this._egoId ? 1 : 0.65)
            ctx.fillRect(here.x, here.y, w, h)
        })
        ctx.globalAlpha = 1

        // The viewport, in stage coordinates, then drawn into every segment it
        // crosses - a wide window straddling two rows shows as two pieces.
        let vw = this.viewportTarget.clientWidth
        let vh = this.viewportTarget.clientHeight
        let vx = -this.tx / this.scale
        let vy = -this.ty / this.scale
        let vws = vw / this.scale
        let vhs = vh / this.scale
        ctx.fillStyle = "rgba(37, 99, 235, 0.08)"
        ctx.strokeStyle = "#2563eb"
        ctx.lineWidth = 1.5
        for (let i = 0; i < g.n; i++) {
            let lo = i * g.segLen
            let hi = lo + g.segLen
            let a0 = Math.max(g.wrapX ? vx : vy, lo)
            let a1 = Math.min((g.wrapX ? vx : vy) + (g.wrapX ? vws : vhs), hi)
            if (a1 <= a0) continue
            let from = g.wrapX ? g.toCanvas(a0, vy) : g.toCanvas(vx, a0)
            let rw = g.wrapX ? (a1 - a0) * g.k : vws * g.k
            let rh = g.wrapX ? vhs * g.k : (a1 - a0) * g.k
            ctx.fillRect(from.x, from.y, rw, rh)
            ctx.strokeRect(from.x, from.y, rw, rh)
        }

        this._drawTourRoute(ctx, g)
    }

    // The tour's own layer: a dotted line through the stops in order, and a
    // pulsing ring on whichever one is current - so the map answers "where is
    // this tour taking me" as well as "where am I".
    _drawTourRoute(ctx, g) {
        if (!this._tour) return

        let centreOf = (id) => {
            let pos = this.cardPos.get(id)
            if (!pos) return null
            let at = this._pt(pos.main + this._mainSize() / 2, pos.cross + this._crossSize() / 2)
            let here = g.toCanvas(at.x, at.y)
            return { x: here.x, y: here.y }
        }
        // A folded stop (not yet unfolded into the current render) has no
        // cardPos entry - skip it in the line rather than snapping through the
        // origin.
        let points = this._tour.stops.map((stop) => centreOf(stop.id)).filter(Boolean)
        if (points.length > 1) {
            ctx.save()
            ctx.setLineDash([2, 3])
            ctx.strokeStyle = TOUR_ROUTE_COLOR
            ctx.globalAlpha = 0.7
            ctx.lineWidth = 1
            ctx.beginPath()
            points.forEach((point, index) => {
                if (index === 0) ctx.moveTo(point.x, point.y)
                else ctx.lineTo(point.x, point.y)
            })
            ctx.stroke()
            ctx.restore()
        }

        let current = centreOf(this._tour.stops[this._tour.index]?.id)
        if (current) {
            // A slow breathe between 4 and 5px - _startTourPulse redraws this
            // on an interval so it animates even while the camera is parked
            // through a stop's dwell.
            let radius = 4 + (Math.sin(Date.now() / 350) + 1) / 2
            ctx.save()
            ctx.strokeStyle = TOUR_ROUTE_COLOR
            ctx.globalAlpha = 0.9
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.arc(current.x, current.y, radius, 0, Math.PI * 2)
            ctx.stroke()
            ctx.restore()
        }
    }

    // Click to jump, drag to pan. Stops at the canvas so the stage's own pan
    // does not also fire, and the wheel is left alone: zooming over the map
    // zooms the tree, which is what the rectangle then shows shrinking.
    _bindMinimap(canvas) {
        let dragging = false
        let moveTo = (event) => {
            let rect = canvas.getBoundingClientRect()
            let g = this._minimapGeometry()
            let at = g.toStage(event.clientX - rect.left, event.clientY - rect.top)
            let vw = this.viewportTarget.clientWidth
            let vh = this.viewportTarget.clientHeight
            this.tx = vw / 2 - at.x * this.scale
            this.ty = vh / 2 - at.y * this.scale
            this._applyTransform()
        }
        // Which lineage is under the pointer: the nearest speck within a few
        // pixels, or nothing. Names it in the header and lights its line.
        let hoverAt = (event) => {
            let rect = canvas.getBoundingClientRect()
            let g = this._minimapGeometry()
            let px = event.clientX - rect.left
            let py = event.clientY - rect.top
            let best = null
            let bestD = 36
            this.cardPos.forEach((pos, id) => {
                let at = this._pt(pos.main, pos.cross)
                let here = g.toCanvas(at.x, at.y)
                let d = (here.x - px) ** 2 + (here.y - py) ** 2
                if (d < bestD) {
                    bestD = d
                    best = id
                }
            })
            this._hoverLineage(best == null ? null : (this._minimap.lineageOf.get(best) ?? null))
        }
        canvas.addEventListener("pointerdown", (event) => {
            event.stopPropagation()
            event.preventDefault()
            dragging = true
            canvas.setPointerCapture?.(event.pointerId)
            moveTo(event)
        })
        canvas.addEventListener("pointermove", (event) => { if (dragging) moveTo(event); else hoverAt(event) })
        canvas.addEventListener("pointerleave", () => this._hoverLineage(null))
        let stop = () => { dragging = false }
        canvas.addEventListener("pointerup", stop)
        canvas.addEventListener("pointercancel", stop)
    }

    // The genealogy chart — the unit layout engine below. Returns whether the
    // result wants the pan/zoom chrome.
    //
    // `view` picks the orientation: "tree" is the portrait chart (generations
    // down the page), "landscape" the same chart with its two axes swapped.
    _renderTree(view = "tree") {
        this.landscape = view === "landscape"

        let forest = this._buildForest()
        this._lastForest = forest
        // The chart's top card (the CEO on an org chart) - the opening
        // anchor when there is no ego to center on (see _centerOnEgo).
        this._firstRootId = forest.length ? forest[0].id : null
        this._measure(forest)

        let cross = this._pad
        let maxDepth = 0
        forest.forEach((unit) => {
            // A root no longer starts on the top row (see _rootDepth), so the
            // deepest row is its starting row plus its own depth. Counting only
            // the depth sized the chart short, and the SVG clipped every line
            // below that height while the cards - plain divs - still drew:
            // Jo's father, grandparents and cousins with no lines at all.
            let rootDepth = this._rootDepth(unit)
            this._place(unit, cross, rootDepth)
            cross += unit.subtreeCross + this._treeGap
            maxDepth = Math.max(maxDepth, rootDepth + this._depth(unit))
        })
        // Extent along each axis, then mapped back to real pixels: in
        // landscape the generations are what runs across the page and the
        // sibling spread is what runs down it.
        let crossExtent = forest.length ? cross - this._treeGap + this._pad : 0
        let mainExtent = forest.length ? this._pad + (maxDepth + 1) * (this._mainSize() + this._rowGap) - this._rowGap + this._pad : 0
        // The owner's hand-arranged layout, over the automatic one. Null when
        // there is nothing to apply, which leaves the extents above untouched.
        let arranged = this._applyLayout(forest)
        if (arranged) {
            crossExtent = arranged.crossExtent
            mainExtent = arranged.mainExtent
        }
        let { x: width, y: height } = this._pt(mainExtent, crossExtent)
        // Cross-links (_drawLinks) reach into another root's subtree, so they
        // need every card's final position, not just their own unit's.
        this.cardPos = new Map()
        this._indexPositions(forest)

        if (forest.length) {
            let canvas = document.createElement("div")
            canvas.className = "contact-tree-canvas-inner"
            canvas.style.width = `${width}px`
            canvas.style.height = `${height}px`
            canvas.appendChild(this._svg(forest, width, height))
            forest.forEach((unit) => this._renderUnit(unit, canvas))
            this.treeTarget.appendChild(canvas)
            this._bindArrange(canvas)
        }

        let unplaced = this.nodes.filter((node) => !this.linked.has(node.id))
        if (unplaced.length) this.treeTarget.appendChild(this._unplacedRow(unplaced))
        if (this.truncatedCount > 0) this.treeTarget.appendChild(this._truncatedNote())
        if (this.cappedTotal > 0) this.treeTarget.appendChild(this._cappedNote())
        let viewportW = this.hasViewportTarget ? this.viewportTarget.clientWidth : 0
        this.treeTarget.style.width = forest.length ? `${width}px` : `${Math.max(viewportW - 2 * FIT_PAD, this._cardW)}px`
        return true
    }

    // Timeline / table / fan. They get the shared indices and draw their own
    // DOM; anything they emit carrying data-node-id joins the search highlight
    // for free (see _applySearchClasses).
    _renderAlternate(view) {
        let render = ALTERNATES[view] || renderFan
        let result = render({
            nodes: this.nodes,
            edges: this.edges,
            // Which gap the table should open filtered to, when a member
            // arrived from the chart's "still to finish" line.
            gap: (this.hasGapValue ? this.gapValue : null) || null,
            nodesById: this.nodesById,
            childrenOf: this.childrenOf,
            parentsOf: this.parentsOf,
            partnersOf: this.partnersOf,
            linked: this.linked,
            egoId: this._egoId,
            // Descendancy's root. Unlike the table's sort this is addressable
            // state — see the note at the top of descendancy_view — so it lives
            // in the URL and a change re-renders through _setRoot.
            rootId: this.rootValue || null,
            onRootChange: (id) => this._setRoot(id),
            // Where a layout puts its own controls for reading the tree: the
            // table's gap chips, descendancy's root picker. They go on the
            // shared toolbar row rather than above each layout's own body.
            filterSlot: this.hasFiltersTarget ? this.filtersTarget : null,
            // A layout that addresses the reader has to know whether the reader
            // can act on what it says - "link yourself to a parent" is an
            // instruction only the owner can follow.
            publicView: !!this.publicValue,
            // A viewer of a family-owned tree reads what a member reads and
            // changes none of it - no add, edit or remove on the Sources view.
            readOnly: !!this.viewerValue,
            egoFilteredOut: !!this._egoFilteredOut,
            viewportWidth: this.hasViewportTarget ? this.viewportTarget.clientWidth : 0,
            // The Sources view fetches its own payload (transcripts are too big
            // to ride the poll) and needs to know where from.
            sourcesUrl: this.sourcesBaseValue ? `${this.sourcesBaseValue}/sources` : null,
            // Same reasoning for the map: a free-text place has no coordinates,
            // and the ones it does have arrive minutes later from a geocoding
            // job. See FamilyTrees::MapPlaces.
            placesUrl: this.hasPlacesUrlValue ? this.placesUrlValue : null,
            evidence: this.evidence || { active: false, people: {} },
            // The relationship chart draws the same answer the lens bar reports.
            relationship: this._relationship,
            pathPair: this._pathPair(),
            // The document owns its own actions, since the floating bar is
            // suppressed for it.
            onCopyLink: () => this._copyLensLink(),
            onCopyShareLink: this.shareBaseValue ? () => this._copyShareLink() : null,
            onClearPath: () => this._clearPath(),
            // The chart draws the app's OWN card rather than a lookalike, so
            // it inherits the accent, the avatar, the relation line, the kebab
            // and the click behaviour - and cannot drift from them later.
            renderCard: (node) => this._card(node)
        })
        this._alternateSearch = result.search || null
        this._alternateSearch?.(this._searchMatches || [], !!this._searchQuery)
        // A layout that wants pan/zoom goes on the transformed stage; one that
        // scrolls goes in the plain flow block beside it, where no transform
        // can reach it. Chosen after rendering because panZoom is the layout's
        // own answer, reported back in its result.
        // A layout that owns more than its DOM (the map binds Leaflet to the
        // window and keeps tile requests in flight) hands back a teardown. The
        // hosts below are emptied with innerHTML on every render, which frees
        // the markup and nothing else.
        this._alternateCleanup = result.cleanup || null
        let host = result.panZoom || !this.hasFlowTarget ? this.treeTarget : this.flowTarget
        host.appendChild(result.element)
        if (this.truncatedCount > 0) host.appendChild(this._truncatedNote())
        this.treeTarget.style.width = result.panZoom && result.width ? `${result.width}px` : ""
        return !!result.panZoom
    }

    _teardownAlternate() {
        let cleanup = this._alternateCleanup
        this._alternateCleanup = null
        this._alternateSearch = null
        cleanup?.()
    }

    // --- Pan & zoom ------------------------------------------------------------

    _bindViewport() {
        if (!this.hasViewportTarget) return
        this._boundPointerMove = (event) => this._onPointerMove(event)
        this._boundPointerUp = (event) => this._onPointerUp(event)
        let viewport = this.viewportTarget
        viewport.addEventListener("wheel", (event) => this._onWheel(event), { passive: false })
        viewport.addEventListener("pointerdown", (event) => this._onPointerDown(event))
        viewport.addEventListener("dragstart", (event) => event.preventDefault())
        // A drag that started on a card must not fire the card's link click.
        viewport.addEventListener("click", (event) => {
            if (!this._suppressClick) return
            this._suppressClick = false
            event.preventDefault()
            event.stopPropagation()
        }, true)
    }

    // The button means "show me everything", so it fits without the floor - the
    // whole point of pressing it on a large tree is to zoom out past legible.
    fit(event) {
        event?.preventDefault()
        this._fit()
    }

    // Pan and zoom belong to the CHART layouts. The table and the descendancy
    // outline are documents that scroll - the zoom buttons are already hidden
    // for them in CSS, but the wheel, drag and pinch handlers below are bound
    // to the viewport for every layout, so without this gate a trackpad scroll
    // over the table zoomed it instead of scrolling it, and _onWheel's
    // preventDefault meant it could not be scrolled at all.
    //
    // _panZoom is what each layout reports back from its render (see
    // _renderAlternate), so a future scrolling layout is covered by saying so
    // rather than by being listed here.
    get _zoomable() {
        return this._panZoom !== false
    }

    // Where am I? On a chart of a hundred people you can find yourself by
    // looking. On a chart of a thousand - a family plus a one-name study - you
    // cannot, and the chart only ever centres on you once, when it opens. One
    // pan later that is gone and the way back was a reload.
    //
    // So: a button that returns the camera to you, expanding any branch that
    // has you folded away, and flashing the card so you can see where you
    // landed. The same landed pulse search uses, for the same reason.
    centerOnMe(event) {
        event?.preventDefault()
        if (this._egoId == null) return

        if (this._expandAncestorsFor([this._egoId])) this._render()
        this._focusMatch(this._egoId)

        let card = this.treeTarget?.querySelector(`[data-node-id="${CSS.escape(String(this._egoId))}"]`)
        if (!card) return

        card.classList.add("contact-tree-card--landed")
        window.setTimeout(() => card.classList.remove("contact-tree-card--landed"), 2200)
    }

    // The button is meaningless where there is nobody to be - an org chart, or
    // a public share opened by a stranger - so it is not offered there rather
    // than offered and inert.
    _applyHomeButton() {
        let offer = this._egoId != null
        this.element.querySelectorAll("[data-home-button]").forEach((button) => {
            button.hidden = !offer
        })
    }

    zoomIn(event) {
        event?.preventDefault()
        if (!this._zoomable) return
        this._zoomAtViewportCenter(this.scale * 1.25)
    }

    zoomOut(event) {
        event?.preventDefault()
        if (!this._zoomable) return
        this._zoomAtViewportCenter(this.scale / 1.25)
    }

    // `readable` is the automatic open (see MIN_OPEN_SCALE); the fit button
    // passes nothing and gets a true fit.
    _fit({ readable = false } = {}) {
        if (!this.hasViewportTarget || !this._zoomable) return
        let vw = this.viewportTarget.clientWidth
        let vh = this.viewportTarget.clientHeight

        // The first render can land before the flex layout that sizes the
        // viewport has resolved, and a zero-width viewport makes every
        // candidate scale clamp to MIN_SCALE (0.05) - which is why the chart
        // opened as an apparently blank canvas, correcting itself only when you
        // pressed "fit to view". Wait for a viewport with real dimensions
        // instead of fitting against nothing. Bounded so a permanently
        // zero-size viewport (a hidden tab) can't spin forever.
        if (vw < 1 || vh < 1) {
            this._fitRetries = (this._fitRetries || 0) + 1
            if (this._fitRetries <= 30) {
                this._needsFit = true
                requestAnimationFrame(() => this._fit({ readable }))
            }
            return
        }
        this._fitRetries = 0
        this._needsFit = false

        let stage = this.treeTarget
        let sw = Math.max(stage.offsetWidth, stage.scrollWidth, 1)
        let sh = Math.max(stage.offsetHeight, stage.scrollHeight, 1)
        let scale = Math.min(Math.max(vw - 2 * FIT_PAD, 1) / sw, Math.max(vh - 2 * FIT_PAD, 1) / sh, 1)

        let floor = this._density === "compact" ? MIN_OPEN_SCALE_COMPACT : MIN_OPEN_SCALE
        if (readable && scale < floor) {
            this.scale = floor
            this._centerOnEgo(vw, vh, sw, sh)
        } else {
            this.scale = Math.max(scale, MIN_SCALE)
            this.tx = (vw - sw * this.scale) / 2
            this.ty = (vh - sh * this.scale) / 2
        }
        this._applyTransform()
    }

    // Frame the traced chain: every card on the path, plus a margin. Returns
    // false when there is no path, no geometry yet, or the chain is a single
    // card - in which case the ordinary opening rules apply.
    _fitToPath() {
        if (!this._pathIds?.size || !this.cardPos || !this.hasViewportTarget || !this._zoomable) return false

        let vw = this.viewportTarget.clientWidth
        let vh = this.viewportTarget.clientHeight
        if (vw < 1 || vh < 1) return false

        let points = [...this._pathIds].map((id) => this.cardPos.get(id)).filter(Boolean)
        if (points.length < 2) return false

        let corners = points.map((pos) => ({
            a: this._pt(pos.main, pos.cross),
            b: this._pt(pos.main + this._mainSize(), pos.cross + this._crossSize())
        }))
        let minX = Math.min(...corners.map((c) => Math.min(c.a.x, c.b.x)))
        let maxX = Math.max(...corners.map((c) => Math.max(c.a.x, c.b.x)))
        let minY = Math.min(...corners.map((c) => Math.min(c.a.y, c.b.y)))
        let maxY = Math.max(...corners.map((c) => Math.max(c.a.y, c.b.y)))
        let width = Math.max(maxX - minX, 1)
        let height = Math.max(maxY - minY, 1)

        // Never magnify past 1: a two-card chain blown up to fill the window
        // looks broken rather than focused.
        this.scale = Math.min((vw - 2 * FIT_PAD) / width, (vh - 2 * FIT_PAD) / height, 1)
        this.scale = Math.max(this.scale, MIN_SCALE)
        this.tx = (vw - width * this.scale) / 2 - minX * this.scale
        this.ty = (vh - height * this.scale) / 2 - minY * this.scale
        this._applyTransform()
        return true
    }

    // Where a too-large chart opens: your own card in the middle of the
    // viewport. Centring the chart's GEOMETRY instead would drop you among
    // whichever cousins happen to sit in the middle of the layout, which is no
    // more useful a starting point than the top-left corner.
    //
    // Falls back to centring the chart when there is no ego to centre on (the
    // org chart, a tree you are not in) or no card positions (the timeline and
    // fan draw their own DOM and index nothing).
    _centerOnEgo(vw, vh, sw, sh) {
        // No ego on an org chart - its natural opening anchor is the top of
        // the tree (the CEO), not the geometric middle of the layout, which
        // used to open a big chart on whatever cards happened to sit there
        // (often with the root off-screen entirely).
        let anchorId = this._egoId ?? (this.kindValue === "org" ? this._firstRootId : null)
        let pos = anchorId == null ? null : this.cardPos?.get(anchorId)
        if (!pos) {
            this.tx = (vw - sw * this.scale) / 2
            this.ty = (vh - sh * this.scale) / 2
            return
        }
        let at = this._pt(pos.main + this._mainSize() / 2, pos.cross + this._crossSize() / 2)
        this.tx = this._clampPan(vw / 2 - at.x * this.scale, vw, sw * this.scale)
        this.ty = this._clampPan(vh / 2 - at.y * this.scale, vh, sh * this.scale)
    }

    // Keep the chart against the viewport rather than leaving a band of empty
    // canvas at an edge: when it overflows, pan within its own bounds; when it
    // fits on that axis, centre it.
    _clampPan(value, viewport, content) {
        if (content <= viewport) return (viewport - content) / 2

        return Math.min(0, Math.max(viewport - content, value))
    }

    _applyTransform() {
        this.treeTarget.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
        this._syncMinimap()
    }

    _onWheel(event) {
        // Any hands-on gesture on the chart itself wins over the tour - the
        // minimap pane and the story card stop this event before it reaches
        // here (see _renderMinimap/_renderStory), so this only fires for a
        // real interruption.
        this._pauseTour()
        // No preventDefault before this check, or the layout cannot scroll.
        if (!this._zoomable) return
        event.preventDefault()
        let delta = event.deltaMode === 1 ? event.deltaY * 33 : event.deltaY
        let factor = Math.exp(-delta * (event.ctrlKey ? 0.01 : 0.0018))
        this._zoomAt(event.clientX, event.clientY, this.scale * factor)
    }

    _zoomAtViewportCenter(nextScale) {
        if (!this.hasViewportTarget) return
        let rect = this.viewportTarget.getBoundingClientRect()
        this._zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, nextScale)
    }

    // Zoom keeping the given client point fixed on screen.
    _zoomAt(clientX, clientY, nextScale) {
        nextScale = Math.min(Math.max(nextScale, MIN_SCALE), MAX_SCALE)
        let rect = this.viewportTarget.getBoundingClientRect()
        let px = clientX - rect.left
        let py = clientY - rect.top
        this.tx = px - ((px - this.tx) / this.scale) * nextScale
        this.ty = py - ((py - this.ty) / this.scale) * nextScale
        this.scale = nextScale
        this._applyTransform()
    }

    _onPointerDown(event) {
        this._pauseTour()
        if (!this._zoomable) return
        if (event.pointerType === "mouse" && event.button !== 0) return
        this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
        this._panMoved = false
        if (!this._windowPointerBound) {
            this._windowPointerBound = true
            window.addEventListener("pointermove", this._boundPointerMove)
            window.addEventListener("pointerup", this._boundPointerUp)
            window.addEventListener("pointercancel", this._boundPointerUp)
        }
    }

    _onPointerMove(event) {
        if (!this._pointers.has(event.pointerId)) return
        if (this._pointers.size === 2) {
            this._onPinchMove(event)
            return
        }
        let prev = this._pointers.get(event.pointerId)
        let dx = event.clientX - prev.x
        let dy = event.clientY - prev.y
        if (!this._panMoved && Math.hypot(dx, dy) < PAN_THRESHOLD) return
        this._panMoved = true
        this.viewportTarget.classList.add("is-panning")
        this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
        this.tx += dx
        this.ty += dy
        this._applyTransform()
    }

    _onPinchMove(event) {
        let prev = this._pointers.get(event.pointerId)
        let other = null
        this._pointers.forEach((point, id) => {
            if (id !== event.pointerId) other = point
        })
        if (!other) return
        let prevDist = Math.hypot(prev.x - other.x, prev.y - other.y)
        this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
        let nextDist = Math.hypot(event.clientX - other.x, event.clientY - other.y)
        if (prevDist <= 0 || nextDist <= 0) return
        this._panMoved = true
        let midX = (event.clientX + other.x) / 2
        let midY = (event.clientY + other.y) / 2
        this._zoomAt(midX, midY, this.scale * (nextDist / prevDist))
    }

    _onPointerUp(event) {
        this._pointers.delete(event.pointerId)
        if (this._pointers.size === 0) {
            this.viewportTarget.classList.remove("is-panning")
            if (this._panMoved) this._suppressClick = true
            this._panMoved = false
            if (this._windowPointerBound) {
                this._windowPointerBound = false
                window.removeEventListener("pointermove", this._boundPointerMove)
                window.removeEventListener("pointerup", this._boundPointerUp)
                window.removeEventListener("pointercancel", this._boundPointerUp)
            }
        }
    }

    // --- Structure -----------------------------------------------------------

    _buildForest() {
        this.placed = new Set()
        // Folded-up ancestors are not drawn at all, so "has no parents" has to
        // mean "has no VISIBLE parents" - otherwise the person you folded above
        // stops being a root and disappears along with them.
        this._hiddenAncestors = this._upwardHidden()
        this._hiddenAncestors.forEach((id) => this.placed.add(id))
        let visibleParents = (id) => (this.parentsOf.get(id) || []).filter((pid) => !this._hiddenAncestors.has(pid))
        let roots = this.nodes.filter((node) => {
            if (!this.linked.has(node.id)) return false
            if (this._hiddenAncestors.has(node.id)) return false
            if (visibleParents(node.id).length) return false
            // A parentless person whose partner has parents in the tree gets
            // pulled in beside the partner instead of starting their own tree.
            let partners = this.partnersOf.get(node.id) || []
            return !partners.some((id) => visibleParents(id).length)
        })
        let forest = []
        roots.forEach((node) => {
            if (this.placed.has(node.id)) return
            forest.push(this._buildUnit(node.id))
        })
        // Safety net for cycles/cross-tree couples: render anything linked
        // but unreached as its own root.
        this.nodes.forEach((node) => {
            if (!this.linked.has(node.id) || this.placed.has(node.id)) return
            if (this._hiddenAncestors.has(node.id)) return

            forest.push(this._buildUnit(node.id))
        })
        return this._orderForest(forest)
    }

    // Put a branch next to the branch it marries into.
    //
    // Roots are "people with no parents recorded", and they were laid out in
    // payload order - which is id order, which is the order the tree happened
    // to be built in. So a partner's ancestors became their own tree and landed
    // wherever their ids fell: on one real family that put a great-grandparent
    // line at the far left of the canvas, several screens from the couple it
    // belongs to, with nothing but empty grid in between. Collapsing anything
    // in it left four cards stranded in a void with no way to tell what they
    // were attached to.
    //
    // This is the layout half of a convention every genealogy tool follows in
    // one form or another: ancestry hangs off the person it belongs to. The
    // other half - only ever charting ONE bloodline and re-rooting to see the
    // other - is what the "As <person>" control already does.
    _orderForest(forest) {
        if (forest.length < 2) return forest

        let treeOf = new Map()
        let membersOf = forest.map((tree, index) => {
            let ids = []
            let walk = (unit) => {
                unit.members.forEach((id) => { ids.push(id); treeOf.set(id, index) })
                unit.children.forEach(walk)
            }
            walk(tree)
            return ids
        })

        // Which tree does each root tree descend INTO? A parent edge whose
        // child was already drawn elsewhere is exactly the marries-into case.
        let attachesTo = forest.map((_tree, index) => {
            let target = null
            membersOf[index].some((id) => {
                return (this.childrenOf.get(id) || []).some((childId) => {
                    let other = treeOf.get(childId)
                    if (other == null || other === index) return false

                    target = other
                    return true
                })
            })
            return target
        })

        // Emit each tree once, immediately before whatever it attaches to, so
        // the in-laws read as an approach to the couple rather than an island.
        let emitted = new Set()
        let ordered = []
        let emit = (index, guard) => {
            if (emitted.has(index) || guard.has(index)) return

            guard.add(index)
            forest.forEach((_t, other) => { if (attachesTo[other] === index) emit(other, guard) })
            if (emitted.has(index)) return

            emitted.add(index)
            ordered.push(forest[index])
        }
        forest.forEach((_tree, index) => {
            if (attachesTo[index] != null) return

            emit(index, new Set())
        })
        // Anything left (a cycle of attachments) keeps its original place.
        forest.forEach((tree, index) => {
            if (emitted.has(index)) return

            emitted.add(index)
            ordered.push(tree)
        })
        return ordered
    }

    _buildUnit(anchorId) {
        this.placed.add(anchorId)
        let members = [anchorId]
        let partners = (this.partnersOf.get(anchorId) || []).filter((id) => !this.placed.has(id))
        if (partners.length) {
            this.placed.add(partners[0])
            members.push(partners[0])
        }
        let unit = { id: anchorId, members, children: [], links: [] }
        if (this.collapsed.has(anchorId) && !this._foldsUpward(anchorId)) {
            // Mark hidden descendants as placed so the cycle safety net
            // doesn't resurrect them as their own roots.
            this._markDescendantsPlaced(members)
        } else {
            let childIds = []
            members.forEach((id) => {
                (this.childrenOf.get(id) || []).forEach((childId) => {
                    if (!childIds.includes(childId)) childIds.push(childId)
                })
            })
            // Oldest first. Every family tree ever drawn on paper runs a sibling
            // row eldest to youngest, and this one ran in whatever order the
            // edges happened to be entered - which on a 125-person tree meant
            // the generation you were reading had no order at all, and a reader
            // who knows the family could not use what they know to find anyone.
            //
            // The descendancy outline has always sorted this way. The chart
            // disagreeing with the outline about the order of the same four
            // children is its own small confusion.
            this._sortBySeniority(childIds)
            childIds.forEach((childId) => {
                // Already drawn under someone else (typically as the spouse in
                // a couple hanging off the OTHER grandparents): keep the edge
                // as a cross-link rather than dropping it on the floor.
                if (this.placed.has(childId)) {
                    unit.links.push(childId)
                    return
                }
                unit.children.push(this._buildUnit(childId))
            })
        }
        return unit
    }

    // In place, by birth year. A sibling with no date sorts after the ones
    // that have one rather than to the front: an undated person is unplaced in
    // the row, not the eldest, and putting them first would state something
    // about them that nobody knows. Ties and undated pairs fall back to the
    // name, so the order is at least stable between renders.
    _sortBySeniority(ids) {
        ids.sort((a, b) => {
            let left = this.nodesById.get(a)
            let right = this.nodesById.get(b)
            if (!left || !right) return 0

            let ay = birthOf(left).year
            let by = birthOf(right).year
            if (Number.isFinite(ay) && Number.isFinite(by) && ay !== by) return ay - by
            if (Number.isFinite(ay) !== Number.isFinite(by)) return Number.isFinite(ay) ? -1 : 1
            return String(left.name).localeCompare(String(right.name))
        })
        return ids
    }

    _markDescendantsPlaced(ids) {
        ids.forEach((id) => {
            (this.childrenOf.get(id) || []).forEach((childId) => {
                if (this.placed.has(childId)) return
                this.placed.add(childId)
                let hidden = [childId]
                ;(this.partnersOf.get(childId) || []).forEach((partnerId) => {
                    if (this.placed.has(partnerId)) return
                    this.placed.add(partnerId)
                    hidden.push(partnerId)
                })
                this._markDescendantsPlaced(hidden)
            })
        })
    }

    // Collapse folds AWAY from the person the chart is drawn for.
    //
    // The toggle used to hide a unit's descendants everywhere, which is right
    // below you and exactly backwards above you: your mother's parents descend
    // to your mother, to you, to your children. Collapsing them took the whole
    // family with them - eight cards down to three, and the three survivors
    // were ancestors floating over an empty canvas with no way to tell what had
    // happened.
    //
    // Every genealogy tool resolves this the same way, because there is only
    // one sensible answer: a branch folds in the direction away from the focus.
    // Above you that means hiding a person's PARENTS; below you it means hiding
    // their children. Nothing you can click makes you disappear.
    _foldsUpward(unitId) {
        if (this._egoId == null || unitId === this._egoId) return false

        let unitIds = this._unitMemberIds(unitId)
        if (unitIds.includes(this._egoId)) return false

        return this._reaches(unitIds, this._egoId)
    }

    _unitMemberIds(unitId) {
        let partners = this.partnersOf.get(unitId) || []
        return [unitId, ...partners]
    }

    _hasParents(unit) {
        return unit.members.some((id) => (this.parentsOf.get(id) || []).some((pid) => this.nodesById.has(pid)))
    }

    // Everyone strictly above a person folded upward. These are dropped from
    // the drawing entirely, which is why root selection has to ask for parents
    // that are still visible rather than parents at all.
    _upwardHidden() {
        let hidden = new Set()
        if (this._egoId == null) return hidden

        this.collapsed.forEach((unitId) => {
            if (!this._foldsUpward(unitId)) return

            let queue = this._unitMemberIds(unitId).flatMap((id) => this.parentsOf.get(id) || [])
            let guard = 0
            while (queue.length && guard++ < 4096) {
                let id = queue.shift()
                if (hidden.has(id)) continue

                hidden.add(id)
                ;(this.partnersOf.get(id) || []).forEach((pid) => hidden.add(pid))
                ;(this.parentsOf.get(id) || []).forEach((pid) => queue.push(pid))
            }
        })
        // Never hide the focus, whatever the shape of the graph.
        hidden.delete(this._egoId)
        return hidden
    }

    // Does any descendant chain from these people arrive at `targetId`?
    //
    // Partners count. A couple is drawn as ONE unit, so hiding a descendant
    // hides whoever is drawn beside them - which is how collapsing a
    // great-grandparent on your mother's side took your father off the canvas
    // even though he is nobody's descendant here. He was standing next to one.
    _reaches(startIds, targetId) {
        let seen = new Set(startIds)
        let queue = [...startIds]
        let guard = 0
        while (queue.length && guard++ < 4096) {
            let id = queue.shift()
            for (let childId of this.childrenOf.get(id) || []) {
                if (childId === targetId) return true
                if ((this.partnersOf.get(childId) || []).includes(targetId)) return true
                if (seen.has(childId)) continue

                seen.add(childId)
                queue.push(childId)
            }
        }
        return false
    }

    _hasHiddenChildren(unit) {
        return this.collapsed.has(unit.id) && unit.members.some((id) => (this.childrenOf.get(id) || []).some((childId) => this.nodesById.has(childId)))
    }

    // --- Hand-arranged layout -------------------------------------------------
    //
    // The chart lays itself out and the OWNER of a shared tree may move it.
    // Three states, one saved record (FamilyTree#layout / #layout_locked):
    //
    //   automatic  nothing saved; every card is where the engine puts it.
    //   arranged   `offsets`: a unit (a person, or a couple) moved off its place
    //              by some distance. Offsets COMPOSE down the branch - moving a
    //              grandparent carries everyone beneath them, moving one child
    //              carries only that child's own branch - which is what makes
    //              "move this branch" one number rather than one per card, and
    //              what lets a person added later land sensibly under a parent
    //              who has been moved.
    //   locked     `frozen`: where every unit sat at the moment of locking. The
    //              automatic layout no longer decides anything: adding someone,
    //              folding a branch or changing density does not move a card
    //              that was locked in place. Somebody new inherits their
    //              parent's displacement and is outlined until it is unlocked.
    //
    // Distances are stored in CARD SLOTS (one generation step, one sibling
    // step), never pixels, so changing density or flipping to landscape does not
    // scatter a hand-made chart. `main` runs along the generations and `cross`
    // along the sibling spread - the engine's own two axes, which is why the same
    // numbers mean the same thing in both orientations.
    //
    // Units are keyed by their FIRST person (unit.id). Members read the same
    // arrangement; only the owner (layoutUrl present) can change it.

    _initLayout() {
        let saved = this.hasLayoutValue && this.layoutValue ? this.layoutValue : {}
        this._layout = {
            offsets: { ...(saved.offsets || {}) },
            frozen: { ...(saved.frozen || {}) }
        }
        this._locked = this.hasLayoutLockedValue && this.layoutLockedValue === true && Object.keys(this._layout.frozen).length > 0
        this._arranging = false
        this._unitsByMember = new Map()
        this._forestUnits = []
        this._freshCount = 0
    }

    get _ownsLayout() {
        return this.kindValue === "family" && this.hasLayoutUrlValue && !!this.layoutUrlValue
    }

    _layoutCustomised() {
        return this._locked || Object.keys(this._layout.offsets).length > 0
    }

    _slots() {
        return { main: this._mainSize() + this._rowGap, cross: this._crossSize() + this._siblingGap }
    }

    // Apply the saved arrangement to freshly placed units. Returns the new
    // canvas extents, or null when nothing is arranged (the automatic extents
    // are right and this must cost a normal render nothing).
    _applyLayout(forest) {
        this._unitsByMember = new Map()
        this._forestUnits = []
        this._freshCount = 0
        let { offsets, frozen } = this._layout
        let locked = this._locked
        let active = locked ? Object.keys(frozen).length > 0 : Object.keys(offsets).length > 0
        let slot = this._slots()

        let visit = (unit, parentMain, parentCross) => {
            this._forestUnits.push(unit)
            unit.members.forEach((id) => this._unitsByMember.set(String(id), unit))
            unit.autoMain = unit.main
            unit.autoCross = unit.cross
            unit.fresh = false
            let dMain = parentMain
            let dCross = parentCross
            if (active) {
                let key = String(unit.id)
                if (locked) {
                    let at = frozen[key]
                    if (at) {
                        dMain = at[0] * slot.main - unit.main
                        dCross = at[1] * slot.cross - unit.cross
                    } else {
                        unit.fresh = true
                        this._freshCount += 1
                    }
                } else if (offsets[key]) {
                    dMain += offsets[key][0] * slot.main
                    dCross += offsets[key][1] * slot.cross
                }
            }
            unit.main += dMain
            unit.cross += dCross
            unit.children.forEach((child) => visit(child, dMain, dCross))
        }
        forest.forEach((unit) => visit(unit, 0, 0))
        if (!active) return null

        if (locked) this._clearFreshOverlaps()

        // Keep everything on the canvas. Only ever pushes INWARD from the top
        // and left edge: shrinking back toward the padding would move a locked
        // chart whenever a far-left branch were folded, which is the one thing
        // a lock must not do.
        let minMain = Math.min(...this._forestUnits.map((u) => u.main))
        let minCross = Math.min(...this._forestUnits.map((u) => u.cross))
        let shiftMain = minMain < this._pad ? this._pad - minMain : 0
        let shiftCross = minCross < this._pad ? this._pad - minCross : 0
        this._forestUnits.forEach((u) => {
            u.main += shiftMain
            u.cross += shiftCross
        })
        let mainEnd = Math.max(...this._forestUnits.map((u) => u.main + this._mainSize()))
        let crossEnd = Math.max(...this._forestUnits.map((u) => u.cross + u.unitCross))
        return { mainExtent: mainEnd + this._pad, crossExtent: crossEnd + this._pad }
    }

    // Somebody added to a locked chart takes their parent's displacement, which
    // can land them on top of a sibling who was frozen in place. The frozen
    // cards do not move - that is the lock - so the newcomer (and anyone added
    // beneath them) steps along the row until there is room.
    _clearFreshOverlaps() {
        let main = this._mainSize()
        let overlaps = (a, b) => a.main < b.main + main && b.main < a.main + main &&
            a.cross < b.cross + b.unitCross && b.cross < a.cross + a.unitCross
        let freshTree = (unit) => [unit, ...unit.children.filter((c) => c.fresh).flatMap(freshTree)]
        this._forestUnits.filter((u) => u.fresh).forEach((unit) => {
            let group = freshTree(unit)
            let others = this._forestUnits.filter((u) => !group.includes(u))
            for (let guard = 0; guard < 60; guard++) {
                let hit = null
                for (let mine of group) {
                    let other = others.find((o) => overlaps(mine, o))
                    if (other) { hit = { mine, other }; break }
                }
                if (!hit) break

                let shift = hit.other.cross + hit.other.unitCross + this._siblingGap - hit.mine.cross
                group.forEach((u) => { u.cross += shift })
            }
        })
    }

    // Where every unit is NOW, in slots - what locking freezes.
    _frozenSnapshot() {
        let slot = this._slots()
        let snapshot = { ...this._layout.frozen }
        this._forestUnits.forEach((unit) => {
            snapshot[String(unit.id)] = [this._round(unit.main / slot.main), this._round(unit.cross / slot.cross)]
        })
        return snapshot
    }

    // The inverse, for unlocking: express the frozen places as offsets from the
    // automatic layout so nothing moves on screen when the lock comes off.
    _offsetsFromCurrent() {
        let slot = this._slots()
        let offsets = {}
        let visit = (unit, parentMain, parentCross) => {
            let dMain = unit.main - unit.autoMain
            let dCross = unit.cross - unit.autoCross
            let ownMain = this._round((dMain - parentMain) / slot.main)
            let ownCross = this._round((dCross - parentCross) / slot.cross)
            if (ownMain !== 0 || ownCross !== 0) offsets[String(unit.id)] = [ownMain, ownCross]
            unit.children.forEach((child) => visit(child, dMain, dCross))
        }
        this._lastForest?.forEach((unit) => visit(unit, 0, 0))
        return offsets
    }

    _round(n) {
        return Math.round(n * 1000) / 1000
    }

    // --- The controls ---------------------------------------------------------

    toggleArrange(event) {
        event?.preventDefault()
        if (!this._ownsLayout || this._locked) return
        this._arranging = !this._arranging
        this._afterLayoutModeChange()
    }

    toggleLock(event) {
        event?.preventDefault()
        if (!this._ownsLayout) return
        if (this._locked) {
            this._layout.offsets = this._offsetsFromCurrent()
            this._layout.frozen = {}
            this._locked = false
        } else {
            this._layout.frozen = this._frozenSnapshot()
            this._locked = true
            this._arranging = false
        }
        this._afterLayoutModeChange()
        this._saveLayout()
    }

    resetLayout(event) {
        event?.preventDefault()
        if (!this._ownsLayout) return
        if (!window.confirm("Put every card back where the tree would place it?")) return

        this._layout = { offsets: {}, frozen: {} }
        this._locked = false
        this._arranging = false
        this._afterLayoutModeChange()
        this._saveLayout()
    }

    _afterLayoutModeChange() {
        this._render()
    }

    // Button state, status pill and the data attribute CSS keys off. Called at
    // the end of every render so none of it can be left describing the last one.
    _renderLayoutStatus() {
        let chart = CHART_VIEWS.includes(this._activeView())
        let mode = this._locked ? "locked" : (this._arranging ? "arrange" : "")
        if (mode && chart) this.element.dataset.layoutMode = mode
        else delete this.element.dataset.layoutMode

        this.element.querySelectorAll("[data-arrange-toggle]").forEach((button) => {
            button.setAttribute("aria-pressed", this._arranging ? "true" : "false")
            button.disabled = this._locked
            let label = this._locked ? "Unlock the layout to arrange it" : (this._arranging ? "Done arranging" : "Arrange branches")
            button.title = label
            button.setAttribute("aria-label", label)
        })
        this.element.querySelectorAll("[data-lock-toggle]").forEach((button) => {
            button.setAttribute("aria-pressed", this._locked ? "true" : "false")
            let label = this._locked ? "Unlock layout" : "Lock layout"
            button.title = label
            button.setAttribute("aria-label", label)
            let icon = button.querySelector("i")
            if (icon) icon.className = this._locked ? "bi bi-lock-fill" : "bi bi-unlock"
        })

        this._layoutPill?.remove()
        this._layoutPill = null
        if (!this.hasViewportTarget || !chart) return

        let owner = this._ownsLayout
        let text = null
        let actions = []
        if (this._arranging) {
            text = "Drag a card to move its branch"
            actions.push(["Done", () => this.toggleArrange()])
        } else if (this._locked) {
            text = this._freshCount > 0
                ? `Layout locked. ${this._freshCount} added since, outlined.`
                : "Layout locked."
            if (owner) actions.push(["Unlock", () => this.toggleLock()], ["Reset", () => this.resetLayout()])
            else text = "Layout fixed by the owner."
        } else if (this._layoutCustomised()) {
            text = owner ? "Layout customised." : "Arranged by the owner."
            if (owner) actions.push(["Reset", () => this.resetLayout()])
        }
        if (!text) return

        let pill = document.createElement("div")
        pill.className = "contact-tree-layout-status"
        pill.setAttribute("role", "status")
        if (this._arranging) pill.title = "Arrow keys nudge the focused card (Shift for a full step). Hold Alt while dragging to skip the grid."
        let label = document.createElement("span")
        label.textContent = text
        pill.appendChild(label)
        actions.forEach(([name, run]) => {
            let button = document.createElement("button")
            button.type = "button"
            button.className = "contact-tree-layout-action"
            button.textContent = name
            button.addEventListener("pointerdown", (event) => event.stopPropagation())
            button.addEventListener("click", (event) => {
                event.preventDefault()
                run()
            })
            pill.appendChild(button)
        })
        this.viewportTarget.appendChild(pill)
        this._layoutPill = pill
    }

    // --- Moving a branch ------------------------------------------------------

    _bindArrange(canvas) {
        if (!this._ownsLayout || !this._arranging || this._locked) return

        canvas.addEventListener("pointerdown", (event) => this._startLayoutDrag(event), true)
        // Arranging is not opening: a click on a card is the end of a drag, or a
        // stray press, and neither should navigate away mid-arrangement.
        canvas.addEventListener("click", (event) => {
            if (event.target.closest(".contact-tree-card--placed")) {
                event.preventDefault()
                event.stopPropagation()
            }
        }, true)
        canvas.addEventListener("keydown", (event) => this._nudgeFromKey(event))
    }

    _unitForCard(el) {
        let card = el.closest(".contact-tree-card--placed")
        return card ? this._unitsByMember.get(card.dataset.nodeId) : null
    }

    _startLayoutDrag(event) {
        if (event.pointerType === "mouse" && event.button !== 0) return
        if (event.target.closest(".contact-tree-toggle, .contact-tree-card-menu-btn")) return

        let unit = this._unitForCard(event.target)
        if (!unit) return

        event.preventDefault()
        event.stopPropagation()
        let key = String(unit.id)
        this._drag = {
            key,
            startX: event.clientX,
            startY: event.clientY,
            base: [...(this._layout.offsets[key] || [0, 0])],
            moved: false
        }
        this._dragIds = new Set()
        let collect = (u) => {
            u.members.forEach((id) => this._dragIds.add(String(id)))
            u.children.forEach(collect)
        }
        collect(unit)
        this._boundDragMove = (e) => this._moveLayoutDrag(e)
        this._boundDragEnd = () => this._endLayoutDrag(true)
        window.addEventListener("pointermove", this._boundDragMove)
        window.addEventListener("pointerup", this._boundDragEnd)
        window.addEventListener("pointercancel", this._boundDragEnd)
    }

    _moveLayoutDrag(event) {
        let drag = this._drag
        if (!drag) return

        let dx = (event.clientX - drag.startX) / this.scale
        let dy = (event.clientY - drag.startY) / this.scale
        if (!drag.moved && Math.hypot(dx, dy) < PAN_THRESHOLD) return

        drag.moved = true
        let slot = this._slots()
        let main = (this.landscape ? dx : dy) / slot.main
        let cross = (this.landscape ? dy : dx) / slot.cross
        // A quarter-slot grid by default, so branches line up with each other
        // instead of almost lining up; Alt gives the free hand.
        let snap = event.altKey ? (n) => this._round(n) : (n) => Math.round(n * 4) / 4
        this._setOffset(drag.key, [snap(drag.base[0] + main), snap(drag.base[1] + cross)])
        this._scheduleLayoutRender()
    }

    _endLayoutDrag(commit) {
        window.removeEventListener("pointermove", this._boundDragMove)
        window.removeEventListener("pointerup", this._boundDragEnd)
        window.removeEventListener("pointercancel", this._boundDragEnd)
        let moved = this._drag?.moved
        this._drag = null
        this._dragIds = null
        if (!commit || !moved) return

        this._render()
        this._saveLayout()
    }

    _setOffset(key, pair) {
        if (pair[0] === 0 && pair[1] === 0) delete this._layout.offsets[key]
        else this._layout.offsets[key] = pair
    }

    _scheduleLayoutRender() {
        if (this._layoutFrame) return

        this._layoutFrame = window.requestAnimationFrame(() => {
            this._layoutFrame = null
            if (this._drag || this._arranging) this._render()
        })
    }

    // Arrow keys nudge the focused card's branch. A card is focusable through
    // its open control, so keyboard users get the same reach as a mouse.
    _nudgeFromKey(event) {
        let step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key]
        if (!step) return

        let unit = this._unitForCard(event.target)
        if (!unit) return

        event.preventDefault()
        let size = event.shiftKey ? 1 : 0.25
        let [sx, sy] = step
        let main = (this.landscape ? sx : sy) * size
        let cross = (this.landscape ? sy : sx) * size
        let key = String(unit.id)
        let current = this._layout.offsets[key] || [0, 0]
        this._setOffset(key, [this._round(current[0] + main), this._round(current[1] + cross)])
        this._arrangeFocusId = String(unit.members[0])
        this._render()
        this._saveLayoutSoon()
    }

    _restoreArrangeFocus() {
        let id = this._arrangeFocusId
        this._arrangeFocusId = null
        if (!id || !this._arranging) return

        this.treeTarget.querySelector(`.contact-tree-card[data-node-id="${CSS.escape(id)}"] .contact-tree-card-open`)?.focus({ preventScroll: true })
    }

    // --- Saving ---------------------------------------------------------------

    _saveLayoutSoon() {
        clearTimeout(this._layoutSaveTimer)
        this._layoutSaveTimer = setTimeout(() => this._saveLayout(), 500)
    }

    async _saveLayout() {
        clearTimeout(this._layoutSaveTimer)
        if (!this._ownsLayout) return

        try {
            let response = await fetch(this.layoutUrlValue, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
                credentials: "same-origin",
                body: JSON.stringify({ layout: this._layout, locked: this._locked })
            })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
        } catch {
            showSystemToast("Couldn't save the layout. It will be lost when you leave.")
        }
    }

    // --- Layout --------------------------------------------------------------
    //
    // Everything here works in (main, cross) rather than (x, y). MAIN is the
    // generation axis — one step per generation, the direction descent lines
    // run. CROSS is the sibling spread — the direction a couple sits side by
    // side and siblings fan out along.
    //
    // Portrait maps main→y and cross→x; landscape swaps them. That swap is the
    // ONLY difference between the two chart views, which is why _pt exists
    // instead of a second copy of this engine: collapse, cross-links, the
    // unplaced row, search highlighting and the card menus are all written
    // once and come out correct in both.
    //
    // The card itself never rotates — it stays a 260×64 rectangle — so its
    // extent along each axis swaps with the orientation, which is what
    // _mainSize/_crossSize answer.

    _mainSize() {
        return this.landscape ? this._cardW : this._cardH
    }

    _crossSize() {
        return this.landscape ? this._cardH : this._cardW
    }

    // (main, cross) -> the {x, y} the DOM and SVG actually want.
    _pt(main, cross) {
        return this.landscape ? { x: main, y: cross } : { x: cross, y: main }
    }

    _measure(units) {
        units.forEach((unit) => {
            unit.unitCross = unit.members.length * this._crossSize() + (unit.members.length - 1) * this._coupleGap
            this._measure(unit.children)
            let childrenCross = unit.children.reduce((sum, child) => sum + child.subtreeCross, 0) + Math.max(0, unit.children.length - 1) * this._siblingGap
            unit.subtreeCross = Math.max(unit.unitCross, childrenCross)
        })
    }

    // Which generation ROW a root tree starts on.
    //
    // Every root used to start at 0, because depth was counted inside its own
    // tree. So a partner's grandfather - two generations above your mother, who
    // is one above you - was drawn on the SAME top row as your own oldest
    // ancestor, as though the two families were level with each other. Whole
    // branches read as separate trees standing side by side rather than as one
    // family seen from a person.
    //
    // Genealogy charts are drawn FROM somebody. Generations are counted from the
    // ego - up through parents, down through children, sideways through
    // partners - and every branch, in-laws included, lands on the row its
    // distance from you puts it on.
    //
    // Falls back to 0 for anybody the ego cannot reach, which is what the
    // "not yet placed" row is already for.
    _rootDepth(unit) {
        let generations = this._generationsFromEgo()
        if (!generations) return 0

        let mine = unit.members.map((id) => generations.get(id)).filter((g) => g != null)
        return mine.length ? Math.min(...mine) - this._minGeneration : 0
    }

    // One definition, shared with the timeline's bands - see
    // generationsFromEgo. Cached per render.
    _generationsFromEgo() {
        if (this._generationCache !== undefined) return this._generationCache

        let gen = generationsFromEgo({
            egoId: this._egoId, parentsOf: this.parentsOf, childrenOf: this.childrenOf, partnersOf: this.partnersOf
        })
        if (!gen || !this.nodesById?.has(this._egoId)) return (this._generationCache = null)

        this._minGeneration = Math.min(...gen.values())
        return (this._generationCache = gen)
    }

    _place(unit, crossStart, depth) {
        unit.main = this._pad + depth * (this._mainSize() + this._rowGap)
        if (unit.children.length) {
            let cross = crossStart + (unit.subtreeCross - (unit.children.reduce((sum, child) => sum + child.subtreeCross, 0) + (unit.children.length - 1) * this._siblingGap)) / 2
            unit.children.forEach((child) => {
                this._place(child, cross, depth + 1)
                cross += child.subtreeCross + this._siblingGap
            })
            let first = unit.children[0]
            let last = unit.children[unit.children.length - 1]
            let mid = (this._dropCross(first) + this._dropCross(last)) / 2
            unit.cross = Math.min(Math.max(mid - unit.unitCross / 2, crossStart), crossStart + unit.subtreeCross - unit.unitCross)
        } else {
            unit.cross = crossStart + (unit.subtreeCross - unit.unitCross) / 2
        }
    }

    // { node id => {main, cross} } of every card the forest draws, keyed the
    // same way the cards themselves are positioned in _renderUnit.
    _indexPositions(units) {
        units.forEach((unit) => {
            unit.members.forEach((id, index) => {
                this.cardPos.set(id, { main: unit.main, cross: unit.cross + index * (this._crossSize() + this._coupleGap) })
            })
            this._indexPositions(unit.children)
        })
    }

    _depth(unit) {
        return unit.children.length ? 1 + Math.max(...unit.children.map((child) => this._depth(child))) : 0
    }

    // Where descent lines attach, on the cross axis: the couple connector
    // midpoint for couples, the card's centre for singles. For a child unit
    // this is also where the incoming drop lands.
    _dropCross(unit) {
        return unit.members.length > 1 ? unit.cross + this._crossSize() + this._coupleGap / 2 : unit.cross + this._crossSize() / 2
    }

    _anchorCross(unit) {
        return unit.cross + this._crossSize() / 2
    }

    // --- Rendering -----------------------------------------------------------

    _svg(forest, width, height) {
        let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        svg.setAttribute("class", "contact-tree-lines")
        svg.setAttribute("width", width)
        svg.setAttribute("height", height)
        svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
        forest.forEach((unit) => this._drawUnit(unit, svg))
        // After every unit, so a cross-link is drawn over the descent lines it
        // has to cross rather than under them.
        forest.forEach((unit) => this._drawLinks(unit, svg))
        return svg
    }

    // All four connector segments are expressed as (main, cross) pairs and
    // handed to _line, which maps them for the orientation in play.
    _drawUnit(unit, svg) {
        let midMain = unit.main + this._mainSize() / 2
        if (unit.members.length > 1) {
            let edge = unit.cross + this._crossSize()
            this._line(svg, midMain, edge, midMain, edge + this._coupleGap, "couple")
        }
        if (unit.children.length) {
            let dropCross = this._dropCross(unit)
            let startMain = unit.members.length > 1 ? midMain : unit.main + this._mainSize()
            let busMain = unit.main + this._mainSize() + this._rowGap / 2
            this._line(svg, startMain, dropCross, busMain, dropCross, "descent")
            let anchors = unit.children.map((child) => this._anchorCross(child))
            let minCross = Math.min(dropCross, ...anchors)
            let maxCross = Math.max(dropCross, ...anchors)
            this._line(svg, busMain, minCross, busMain, maxCross, "descent")
            unit.children.forEach((child) => {
                // The last segment - the one that actually touches the child -
                // is the only part of a descent bus that belongs to ONE
                // parentage, so it is the only part that can honestly carry how
                // well that parentage is supported. The bus above it is shared
                // by every sibling and says nothing about any of them.
                this._line(svg, busMain, this._anchorCross(child), child.main, this._anchorCross(child), "descent", this._childConfidence(unit, child))
                this._drawUnit(child, svg)
            })
        }
    }

    // Parent edges to a child who is drawn elsewhere in the forest (see the
    // header). Same elbow as a descent line, but routed along the empty
    // channel above the child's row so it crosses no cards, and dashed so it
    // reads as "the same person, over there" rather than a second child.
    _drawLinks(unit, svg) {
        (unit.links || []).forEach((childId) => {
            let pos = this.cardPos.get(childId)
            if (!pos) return
            let dropCross = this._dropCross(unit)
            let startMain = unit.main + (unit.members.length > 1 ? this._mainSize() / 2 : this._mainSize())
            // Half a row-gap before the card is where descent buses already
            // run, and a cross-link often shares that band with the bus of the
            // parents the child IS drawn under - riding on top of it reads as
            // one continuous line. Sit a quarter-gap out instead, so the dashed
            // link runs parallel to the solid bus rather than under it.
            let channelMain = pos.main - this._rowGap / 4
            let targetCross = pos.cross + this._crossSize() / 2
            this._line(svg, startMain, dropCross, channelMain, dropCross, "linked")
            this._line(svg, channelMain, dropCross, channelMain, targetCross, "linked")
            this._line(svg, channelMain, targetCross, pos.main, targetCross, "linked")
        })
        unit.children.forEach((child) => this._drawLinks(child, svg))
    }

    // How well THIS child's parentage is supported: the best of the parent
    // edges from the people in this unit, because a link one parent has a
    // certificate for is documented whatever the other parent's link says.
    _childConfidence(unit, child) {
        if (!this.evidence?.active) return null

        let best = "unverified"
        let rank = { unverified: 0, reported: 1, documented: 2 }
        ;(unit.members || []).forEach((parentId) => {
            let level = this.edgeConfidence?.get(`${parentId}|${child.id}`)
            if (level && rank[level] > rank[best]) best = level
        })
        return best === "documented" ? null : best
    }

    // Takes (main, cross) pairs, not (x, y) - see the Layout header.
    _line(svg, main1, cross1, main2, cross2, kind, confidence = null) {
        let from = this._pt(main1, cross1)
        let to = this._pt(main2, cross2)
        let line = document.createElementNS("http://www.w3.org/2000/svg", "line")
        line.setAttribute("x1", from.x)
        line.setAttribute("y1", from.y)
        line.setAttribute("x2", to.x)
        line.setAttribute("y2", to.y)
        let classes = `contact-tree-line contact-tree-line--${kind}`
        if (confidence) classes += ` contact-tree-line--${confidence}`
        line.setAttribute("class", classes)
        svg.appendChild(line)
    }

    _renderUnit(unit, canvas) {
        unit.members.forEach((id, index) => {
            let card = this._card(this.nodesById.get(id))
            if (unit.fresh) card.classList.add("contact-tree-card--fresh")
            if (this._dragIds?.has(String(id))) card.classList.add("contact-tree-card--dragging")
            let at = this._pt(unit.main, unit.cross + index * (this._crossSize() + this._coupleGap))
            card.style.left = `${at.x}px`
            card.style.top = `${at.y}px`
            card.classList.add("contact-tree-card--placed")
            canvas.appendChild(card)
        })
        if (this._foldsUpward(unit.id) ? this._hasParents(unit) : (unit.children.length || this._hasHiddenChildren(unit))) {
            canvas.appendChild(this._toggle(unit))
        }
        unit.children.forEach((child) => this._renderUnit(child, canvas))
    }

    _initialBubble(node) {
        let initial = document.createElement("span")
        initial.className = `contact-tree-avatar contact-tree-avatar--initial contact-tree-avatar--${this._accent(node)}`
        initial.textContent = this._initials(node)
        return initial
    }

    _toggle(unit) {
        let button = document.createElement("button")
        button.type = "button"
        button.className = "contact-tree-toggle"
        let expanded = !this.collapsed.has(unit.id)
        button.textContent = expanded ? "−" : "+"
        button.setAttribute("aria-label", expanded ? "Collapse branch" : "Expand branch")
        let at = this._pt(unit.main + this._mainSize(), this._dropCross(unit))
        button.style.left = `${at.x - 11}px`
        button.style.top = `${at.y - 11}px`
        button.addEventListener("click", (event) => {
            event.preventDefault()
            if (expanded) this.collapsed.add(unit.id)
            else this.collapsed.delete(unit.id)
            this._render()
        })
        return button
    }

    // Where actions on the viewer's own CONTACT go. On a shared tree
    // contactsBase points at the tree's people (that is where tree writes
    // belong), so a contact action needs its own base or it would post a
    // LinkedIn sync at a person row.
    get _contactActionsBase() {
        return this.contactActionsBaseValue || this.contactsBaseValue
    }

    _card(node) {
        let card = document.createElement("div")
        card.className = `contact-tree-card contact-tree-card--${this._accent(node)}`
        let link = document.createElement(node.url ? "a" : (node.detail_url ? "button" : "span"))
        link.className = "contact-tree-card-open"
        if (link.tagName === "BUTTON") link.type = "button"
        // Search-match dim/highlight classes are applied by id after render
        // (_applySearchClasses) rather than baked in here, so re-searching
        // doesn't require rebuilding every card.
        card.dataset.nodeId = String(node.id)
        if (this._viewerId != null && node.id === this._viewerId) card.classList.add("contact-tree-card--you")
        if (node.known) card.classList.add("contact-tree-card--known")
        // Sex reads at a glance from the card's SHAPE and tint, not just the
        // small glyph. Not from the edge colour: that already means grandparent
        // line, and "you" is blue. Family trees only - an org chart's gender is
        // not a fact the chart is about.
        if (this.kindValue === "family" && GENDER_ICONS[node.gender]) card.classList.add(`contact-tree-card--${node.gender}`)
        // Company charts carry external (non-contact) people with a LinkedIn
        // link and no in-app page; open those in a new tab, and leave nodes
        // with no link unclickable rather than href="undefined".
        // A shared tree emits `url` only for a person linked to a contact the
        // VIEWER owns (FamilyTrees::SharedPayload#viewer_contact), so the
        // payload decides who gets a card that opens - not the mode.
        // A shared tree gives EVERY person a detail_url, so a card always opens
        // something; `url` (a real page) is present only for somebody the
        // viewer owns a contact for and stays the modifier-click fallback.
        // A public reader gets the panel too. It used to be withheld because a
        // public payload carried no detail_url at all, which left every card on
        // a share inert - and an inert card teaches a visitor that nothing on
        // the page is worth pressing. Theirs is read-only and scrubbed
        // (FamilyTreePublicController#person_detail); what it answers is "who
        // was this", which is the question a share exists to invite.
        if (node.detail_url) {
            card.dataset.detailUrl = node.detail_url
            link.dataset.detailUrl = node.detail_url
            link.dataset.turboFrame = "_top"
        }
        if (node.url) {
            link.href = node.url
            if (node.external) {
                link.target = "_blank"
                link.rel = "noopener noreferrer"
            } else {
                // Break out of any enclosing turbo-frame (the company chart
                // lives in one) so an in-app link isn't answered by a page
                // that lacks that frame → "Content missing".
                link.dataset.turboFrame = "_top"
                // Contact cards open the detail sidebar in place (detail-modal
                // on the page element picks this up); the href stays as the
                // fallback for modifier-clicks and wherever no sidebar is
                // mounted. "self" is the owner, not a contact.
                //
                // Gated on public, NOT readonly. Readonly is about whose graph
                // the kebab edits — the company org chart corrects a web-sourced
                // snapshot rather than your contacts — and it was suppressing the
                // sidebar as a side effect, so a card on that chart navigated
                // away from the chart you were reading. Public trees are the
                // ones with nothing to open: no detail-modal, and the node ids
                // aren't the viewer's contacts.
                if (!this.publicValue && node.id !== "self" && !node.detail_url) {
                    let contactId = node.contact_id || node.id
                    card.dataset.detailUrl = `${this._contactActionsBase}/${contactId}/detail`
                    link.dataset.detailUrl = `${this._contactActionsBase}/${contactId}/detail`
                }
            }
        }

        if (node.avatar_url) {
            let img = document.createElement("img")
            img.className = "contact-tree-avatar"
            img.src = node.avatar_url
            img.alt = ""
            img.referrerPolicy = "no-referrer"
            // The payload prefers our own cached copy, but a card can still
            // point at a raw CDN URL (nothing cached yet), and those expire -
            // a LinkedIn photo URL is signed for about a month. A dead one used
            // to leave the browser's broken-image glyph sitting in the chart;
            // fall back to the initials bubble the node would have had.
            img.addEventListener("error", () => img.replaceWith(this._initialBubble(node)), { once: true })
            link.appendChild(img)
        } else {
            link.appendChild(this._initialBubble(node))
        }

        let copy = document.createElement("span")
        copy.className = "contact-tree-copy"
        let name = document.createElement("span")
        name.className = "contact-tree-name"
        name.textContent = node.name
        // Cards are fixed-width and both lines ellipsize; real corporate
        // titles truncate constantly, so hover recovers the full text.
        name.title = node.name || ""
        copy.appendChild(name)
        if (this.kindValue === "org") {
            if (node.meta) {
                let sub = document.createElement("span")
                sub.className = "contact-tree-relation"
                sub.textContent = node.meta
                sub.title = node.meta
                copy.appendChild(sub)
            }
        } else {
            copy.appendChild(this._personMeta(node))
        }
        link.appendChild(copy)
        if (this._viewerId != null && node.id === this._viewerId) {
            let badge = document.createElement("span")
            badge.className = "contact-tree-you-badge"
            badge.textContent = "You"
            link.appendChild(badge)
        } else if (node.known) {
            // Read-only company charts: flag the people already in the network.
            let badge = document.createElement("span")
            badge.className = "contact-tree-you-badge contact-tree-known-badge"
            badge.textContent = "In network"
            badge.title = "One of your contacts - opens their card"
            link.appendChild(badge)
        }
        // Two documents that disagree about this person. Only ever drawn when
        // there IS a disagreement, so the mark means one thing and a clean tree
        // carries none of them.
        if (node.evidence?.conflicts?.length) {
            let flag = document.createElement("span")
            flag.className = "contact-tree-conflict-dot"
            flag.title = `Sources disagree about ${node.evidence.conflicts.map((f) => CONFLICT_LABELS[f] || f).join(", ")}`
            flag.innerHTML = '<i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i>'
            link.appendChild(flag)
        }
        // Company charts: a hand-edited/revealed node carries a small mark so
        // you can tell your durable corrections from web-sourced guesses.
        if (node.corrected) {
            let dot = document.createElement("span")
            dot.className = "contact-tree-corrected-dot"
            dot.title = "Edited - survives rebuilds"
            dot.innerHTML = '<i class="bi bi-pencil-fill" aria-hidden="true"></i>'
            link.appendChild(dot)
        }
        // Every card (except "you") gets the ⋮ menu — one pattern across all
        // trees; the action set differs (contact graph vs. company chart).
        // The public share view (family/:token) is read-only with no mutation
        // endpoints behind it, so it skips the kebab entirely rather than
        // falling into the readonly company-chart menu (Add report/Correct…),
        // which makes no sense for a family tree and isn't for visitors anyway.
        // The public share used to have no kebab at all, because the readonly
        // company-chart menu made no sense on a family. But the reader of a
        // share link is exactly the person who wants to ask "how am I related
        // to her?", and they had no control anywhere that could ask it. So the
        // family share gets a kebab carrying the two lenses and nothing else.
        card.appendChild(link)
        if (node.id !== "self" && (!this.publicValue || this.kindValue === "family")) card.appendChild(this._kebab(node))
        return card
    }

    // --- Card kebab menu -------------------------------------------------------

    _kebab(node) {
        let button = document.createElement("button")
        button.type = "button"
        button.className = "contact-tree-card-menu-btn"
        button.setAttribute("aria-label", `Actions for ${node.name}`)
        button.setAttribute("aria-haspopup", "true")
        button.setAttribute("aria-expanded", "false")
        button.innerHTML = '<i class="bi bi-three-dots-vertical" aria-hidden="true"></i>'
        button.addEventListener("pointerdown", (event) => event.stopPropagation())
        button.addEventListener("click", (event) => {
            event.preventDefault()
            event.stopPropagation()
            this._openMenu(node, button)
        })
        return button
    }

    _openMenu(node, anchor) {
        if (this._menuNode === node.id && this._menu) {
            this._closeMenu()
            return
        }
        this._closeMenu()
        let panel = document.createElement("div")
        panel.className = "assistant-header-menu-panel contact-tree-menu is-open"
        panel.setAttribute("role", "menu")
        if (this.publicValue && this.kindValue === "family") {
            // Read-only, and nothing here writes: both items only change what
            // this reader is looking at.
            this._lensMenuItems(panel, node, { lead: true })
        } else if (this.readonlyValue) {
            this._companyMenuItems(panel, node)
        } else {
            this._contactMenuItems(panel, node)
        }
        document.body.appendChild(panel)
        this._menu = panel
        this._menuNode = node.id
        this._menuAnchor = anchor
        anchor.setAttribute("aria-expanded", "true")
        this._positionMenu(panel, anchor)
        this._menuOutsideClick = (event) => {
            if (!panel.contains(event.target)) this._closeMenu()
        }
        this._menuKeydown = (event) => {
            if (event.key === "Escape") {
                let anchor = this._menuAnchor
                this._closeMenu()
                anchor?.focus()
            }
        }
        // Deferred so the opening click doesn't immediately close the menu.
        window.setTimeout(() => {
            document.addEventListener("click", this._menuOutsideClick)
            document.addEventListener("keydown", this._menuKeydown)
        }, 0)
    }

    // Family / your org chart: act on the user's own contact graph.
    // Both lenses, on every card's menu, in every mode - including the public
    // share, whose reader is exactly the person most likely to ask "how am I
    // related to this person?" and who has no other control to ask it with.
    // On the kebab rather than the toolbar because the toolbar row is at its
    // width budget, and because both questions are ABOUT a particular person -
    // they belong where that person is.
    _lensMenuItems(panel, node, { lead = false } = {}) {
        if (this.kindValue !== "family") return

        // `lead` is the public share, where these are the ONLY items and a
        // separator above them would be a rule under nothing.
        if (!lead) panel.appendChild(this._menuSeparator())
        panel.appendChild(this._menuItem("bi-bullseye", "Focus on this branch", () => {
            this._closeMenu()
            this._setFocus(node.id)
        }))
        if (this.hasRelationshipUrlValue && this.relationshipUrlValue) {
            panel.appendChild(this._menuItem("bi-signpost-2", "How are we related?…", () => {
                this._closeMenu()
                this._openRelateModal(node.id)
            }))
        }
        panel.appendChild(this._menuItem("bi-link-45deg", "Copy link to this person", () => {
            this._closeMenu()
            this._copyPersonLink(node)
        }))
        // Only a share has somewhere to send it. On a member's own tree the
        // value is empty and the item never appears - they can just edit.
        if (this.hasSuggestUrlValue && this.suggestUrlValue && this.hasSuggestOverlayTarget) {
            panel.appendChild(this._menuItem("bi-pencil-square", "Suggest a correction…", () => {
                this._closeMenu()
                this._openSuggestModal(node)
            }))
        }
    }

    // The reader writing back. See contacts/_suggest_modal for why this files
    // evidence rather than making an edit.
    _openSuggestModal(node) {
        this._suggestNode = node
        if (this.hasSuggestSubjectTarget) this.suggestSubjectTarget.textContent = node ? `About ${node.name}` : ""
        if (this.hasSuggestBodyTarget) this.suggestBodyTarget.value = ""
        this._setSuggestError(null)
        this.suggestOverlayTarget.hidden = false
        this.suggestNameTarget?.focus()
    }

    closeSuggestModal(event) {
        event?.preventDefault()
        if (this.hasSuggestOverlayTarget) this.suggestOverlayTarget.hidden = true
    }

    suggestOverlayClick(event) {
        if (event.target === this.suggestOverlayTarget) this.closeSuggestModal(event)
    }

    async submitSuggestion(event) {
        event.preventDefault()
        if (!this._suggestNode || !this.suggestUrlValue) return

        let name = this.suggestNameTarget?.value.trim() || ""
        let body = this.suggestBodyTarget?.value.trim() || ""
        if (!body) return this._setSuggestError("Say what should change.")
        if (!name) return this._setSuggestError("Add your name, so they know who to thank.")

        // The button is the only thing telling a visitor the press landed, so
        // it says so and stops a second one: a double-send would file the same
        // note twice, and nobody is watching to merge them.
        let button = this.hasSuggestSubmitTarget ? this.suggestSubmitTarget : null
        if (button) { button.disabled = true; button.textContent = "Sending…" }
        this._setSuggestError(null)
        try {
            // A base, not a template: the route constrains person_id to digits,
            // so a ":person_id" placeholder cannot be generated server-side.
            let url = `${this.suggestUrlValue}/people/${encodeURIComponent(this._suggestNode.id)}/suggest`
            let response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || "" },
                body: JSON.stringify({ name: name, body: body })
            })
            let data = await response.json().catch(() => ({}))
            if (!response.ok || !data.ok) throw new Error(data.error || "That did not send.")
            this.closeSuggestModal()
            showSystemToast(data.message || "Sent.")
        } catch (error) {
            this._setSuggestError(error.message || "That did not send.")
        } finally {
            if (button) { button.disabled = false; button.textContent = "Send" }
        }
    }

    _setSuggestError(message) {
        if (!this.hasSuggestErrorTarget) return
        this.suggestErrorTarget.textContent = message || ""
        this.suggestErrorTarget.hidden = !message
    }

    // A link that opens the tree ON somebody.
    //
    // WHY THIS EXISTS. Sending a family tree is almost never "look at my tree";
    // it is "look at HIM" - the ancestor you just found, the cousin you want
    // identified. The reader of a bare share link lands on a canvas of a
    // thousand cards with no idea which one you meant, and the person who sent
    // it has no way to say. ?focus= has always rendered that view; nothing
    // offered to write one, so producing it meant hand-editing a query string.
    //
    // Prefers the SHARE url when the tree has one, for the same reason
    // _copyShareLink does: the member url is a dead end for everybody outside
    // the family. On the public share itself there is no shareBase - the
    // current location already IS the shareable one.
    //
    // Drops the lens you happened to be wearing (path, view, root, gap, people,
    // plain). A link about one person should not also impose the two-person
    // path you were tracing a moment ago, and `plain` would strip the controls
    // the reader needs to look around from where you put them.
    async _copyPersonLink(node) {
        let target = this.shareBaseValue
            ? new URL(this.shareBaseValue, window.location.origin)
            : new URL(window.location.href)
        ;["path", "view", "root", "gap", "people", "plain"].forEach((key) => target.searchParams.delete(key))
        target.searchParams.set("focus", String(node.id))
        await this._copy(target.toString(), "Link to this person copied.")
    }

    _contactMenuItems(panel, node) {
        // A collaborator works in someone ELSE's tree: the only powers are the
        // add-links (writes go to the owner's graph, attributed). No pin, no
        // profile, no LinkedIn, no group chips - none of that is theirs.
        if (this.collaboratorValue) {
            // A viewer reads the tree: no add-links (the server refuses them
            // too, and the modals they open are not even rendered for a viewer).
            if (!this.viewerValue) {
                (ADD_KINDS[this.kindValue] || []).forEach((entry) => {
                    panel.appendChild(this._menuItem(entry.icon, entry.label, () => {
                        this._closeMenu()
                        this._openModal(node, entry)
                    }))
                })
            }
            // Correcting, not just adding. Adding was the only power a
            // collaborator had, which made the invitation half a promise -
            // seeing your own mother's name misspelled and being unable to
            // touch it is not helping. Not offered on the owner's own card:
            // that is a User, not a contact of theirs to fix.
            if (this.hasPersonOverlayTarget && node.id !== OWNER_NODE_ID) {
                panel.appendChild(this._menuSeparator())
                panel.appendChild(this._menuItem("bi-pencil", "Edit details…", () => {
                    this._closeMenu()
                    this._openPersonModal(node)
                }))
            }
            // The address-book half, for a person who is also one of YOUR
            // contacts. Their card, their photo sync, their pin - all of it
            // yours to act on, and simply absent for a member who has no
            // contact of their own for this person.
            if (node.contact_id) {
                panel.appendChild(this._menuSeparator())
                panel.appendChild(this._pinItem(node))
                let profile = document.createElement("a")
                profile.className = "assistant-header-menu-item"
                profile.href = node.url
                profile.dataset.turboFrame = "_top"
                profile.innerHTML = '<i class="bi bi-person" aria-hidden="true"></i><span>View profile</span>'
                panel.appendChild(profile)
                if (node.linkedin) {
                    panel.appendChild(this._menuItem("bi-linkedin", "Sync LinkedIn", () => this._syncLinkedin(node)))
                }
            }
            // Who changed this person, when, and from where - and the Undo.
            let historyUrl = this._historyUrl(node)
            if (historyUrl) {
                panel.appendChild(this._menuSeparator())
                panel.appendChild(this._menuItem("bi-clock-history", "History", () => {
                    this._closeMenu()
                    window.location.assign(historyUrl)
                }))
            }
            this._lensMenuItems(panel, node)
            return
        }
        // Lead with the links themselves. Building out a tree is mostly
        // "this person needs a parent / a partner / a manager", and one item
        // called "Edit connections…" made you open a modal and then find the
        // kind in a select before you could do any of it.
        let addKinds = ADD_KINDS[this.kindValue] || []
        addKinds.forEach((entry) => {
            panel.appendChild(this._menuItem(entry.icon, entry.label, () => {
                this._closeMenu()
                this._openModal(node, entry)
            }))
        })
        if (addKinds.length) panel.appendChild(this._menuSeparator())
        panel.appendChild(this._menuItem("bi-diagram-3", "Edit connections…", () => {
            this._closeMenu()
            this._openModal(node)
        }))
        panel.appendChild(this._pinItem(node))
        let profile = document.createElement("a")
        profile.className = "assistant-header-menu-item"
        profile.href = node.url
        profile.dataset.turboFrame = "_top"
        profile.innerHTML = '<i class="bi bi-person" aria-hidden="true"></i><span>View profile</span>'
        panel.appendChild(profile)
        if (node.linkedin) {
            panel.appendChild(this._menuItem("bi-linkedin", "Sync LinkedIn", () => this._syncLinkedin(node)))
        }
        this._lensMenuItems(panel, node)
    }

    // The history page for one person of a family-owned tree
    // (FamilyTreeHistoryController): the writes base is ".../family_trees/ID/people".
    _historyUrl(node) {
        let match = String(this.contactsBaseValue || "").match(/^(.*\/family_trees\/\d+)\/people$/)
        if (!match || this.kindValue !== "family" || node.id === OWNER_NODE_ID) return null

        return `${match[1]}/history?person_id=${encodeURIComponent(node.id)}`
    }

    // Company chart: a web-sourced snapshot, so the actions open/correct the
    // node rather than editing the contact graph. Same grammar as the network
    // charts: lead with the add-links, then correct/reveal, then the rest.
    _companyMenuItems(panel, node) {
        panel.appendChild(this._menuItem("bi-arrow-down-circle", "Add report…", () => {
            this._closeMenu()
            this._openNodeAdder(node, "report")
        }))
        panel.appendChild(this._menuItem("bi-arrow-up-circle", "Add manager…", () => {
            this._closeMenu()
            this._openNodeAdder(node, "manager")
        }))
        panel.appendChild(this._menuSeparator())
        if (node.known && node.url) {
            let open = document.createElement("a")
            open.className = "assistant-header-menu-item"
            open.href = node.url
            open.dataset.turboFrame = "_top"
            open.innerHTML = '<i class="bi bi-person" aria-hidden="true"></i><span>Open contact</span>'
            panel.appendChild(open)
        }
        if (node.external && node.url) {
            let li = document.createElement("a")
            li.className = "assistant-header-menu-item"
            li.href = node.url
            li.target = "_blank"
            li.rel = "noopener noreferrer"
            li.innerHTML = '<i class="bi bi-linkedin" aria-hidden="true"></i><span>Open on LinkedIn</span>'
            panel.appendChild(li)
        }
        panel.appendChild(this._menuItem("bi-pencil", "Correct…", () => {
            this._closeMenu()
            this._openNodeEditor(node)
        }))
        // The auto-matcher misses nicknames/maiden names; let the user bind the
        // card by hand instead of leaving it a permanent external stranger.
        if (!node.known) {
            panel.appendChild(this._menuItem("bi-link-45deg", "Link to my contact…", () => {
                this._closeMenu()
                this._openNodeLinker(node)
            }))
        }
        // Apollo people-search masks last names; reveal spends 1 credit to
        // unmask the full name/title/LinkedIn.
        if (String(node.key || "").startsWith("apollo:")) {
            panel.appendChild(this._menuItem("bi-stars", "Reveal full details (1 credit, kept on rebuild)", () => {
                this._closeMenu()
                this._revealNode(node)
            }))
        }
    }

    _revealNode(node) {
        let companyId = (this.urlValue.match(/companies\/(\d+)/) || [])[1]
        if (!companyId) return
        let department = new URL(this.urlValue, window.location.origin).searchParams.get("department") || ""
        fetch(`/companies/${companyId}/org_chart/reveal`, {
            method: "POST",
            credentials: "same-origin",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            body: JSON.stringify({ key: node.key, department: department })
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.ok) window.location.reload()
                else showSystemToast("Couldn't reveal this person right now.", { level: "warning" })
            })
            .catch(() => showSystemToast("Couldn't reveal this person right now.", { level: "danger" }))
    }

    // Fill and show the add-a-person dialog (a form in the frame): the anchor
    // card's key plus the relation ("report" hangs the new person under the
    // anchor, "manager" re-points the anchor under them). The server journals
    // the addition as a correction, so it survives rebuilds.
    _openNodeAdder(node, relation) {
        let modal = document.getElementById("org-node-adder")
        if (!modal) return

        modal.querySelector("form")?.reset()
        let set = (sel, val) => { let el = modal.querySelector(sel); if (el) el.value = val || "" }
        set("[data-add-anchor-key]", node.key)
        set("[data-add-relation]", relation)
        let title = modal.querySelector("[data-add-title]")
        if (title) title.textContent = relation === "manager" ? `Add ${node.name}'s manager` : `Add a report to ${node.name}`
        modal.showModal ? modal.showModal() : modal.setAttribute("open", "true")
        modal.querySelector("[data-add-name]")?.focus()
    }

    // Fill and show the "Link to my contact…" dialog: the node's key plus a
    // contact typeahead. Picking a contact journals an upsert carrying
    // contact_id (OrgCharts::Editor#link_node), so the "In network" badge
    // sticks even after the source-driven auto-matcher keeps missing them.
    _openNodeLinker(node) {
        let modal = document.getElementById("org-node-linker")
        if (!modal) return

        let form = modal.querySelector("form")
        form?.reset()
        let set = (sel, val) => { let el = modal.querySelector(sel); if (el) el.value = val || "" }
        set("[data-link-key]", node.key)
        // form.reset() restores markup defaults, but the contact-picker
        // controller's own pick state (and the submit button it disables)
        // isn't part of that - clear both by hand, same as _openModal does.
        let hidden = form?.querySelector("[data-contact-picker-target='id']")
        if (hidden) hidden.value = ""
        let submit = form?.querySelector("[data-contact-picker-target='submit']")
        if (submit) submit.disabled = true
        modal.showModal ? modal.showModal() : modal.setAttribute("open", "true")
        modal.querySelector("[data-contact-picker-target='input']")?.focus()
    }

    // Fill and show the company-chart node editor (a form in the frame). Uses
    // the snapshot key (node.key), not the render id, so edits target the row.
    _openNodeEditor(node) {
        let modal = document.getElementById("org-node-editor")
        if (!modal) return

        modal.querySelectorAll("[data-node-key]").forEach((el) => { el.value = node.key || "" })
        let set = (sel, val) => { let el = modal.querySelector(sel); if (el) el.value = val || "" }
        set("[data-node-name]", node.name)
        set("[data-node-title]", node.title)
        set("[data-node-department]", node.department)

        let select = modal.querySelector("[data-node-manager]")
        if (select) {
            let currentParent = this.parentsOf?.get(node.id)?.[0]
            select.innerHTML = ""
            let top = document.createElement("option")
            top.value = ""
            top.textContent = "Top-level (no manager)"
            select.appendChild(top)
            this.nodes.forEach((n) => {
                if (n.id === node.id || !n.key) return
                let opt = document.createElement("option")
                opt.value = n.key
                opt.textContent = n.name
                if (n.id === currentParent) opt.selected = true
                select.appendChild(opt)
            })
        }
        modal.showModal ? modal.showModal() : modal.setAttribute("open", "true")
    }

    _closeMenu() {
        if (this._menuOutsideClick) document.removeEventListener("click", this._menuOutsideClick)
        if (this._menuKeydown) document.removeEventListener("keydown", this._menuKeydown)
        this._menuOutsideClick = null
        this._menuKeydown = null
        this._menuAnchor?.setAttribute("aria-expanded", "false")
        this._menu?.remove()
        this._menu = null
        this._menuNode = null
        this._menuAnchor = null
    }

    _positionMenu(panel, anchor) {
        let rect = anchor.getBoundingClientRect()
        panel.style.position = "fixed"
        panel.style.right = "auto"
        panel.style.zIndex = "1200"
        panel.style.minWidth = "13rem"
        panel.style.width = "max-content"
        let maxHeight = Math.min(340, Math.max(160, window.innerHeight - 16))
        panel.style.maxHeight = `${maxHeight}px`
        panel.style.overflowY = "auto"
        let width = panel.offsetWidth || 208
        let left = Math.min(rect.right - width / 2, window.innerWidth - width - 8)
        panel.style.left = `${Math.max(8, left)}px`
        let panelHeight = panel.offsetHeight || maxHeight
        let top = rect.bottom + 6
        if (top + panelHeight > window.innerHeight - 8) {
            top = Math.max(8, rect.top - panelHeight - 6)
        }
        panel.style.top = `${top}px`
    }

    _menuSeparator() {
        let hr = document.createElement("div")
        hr.className = "assistant-header-menu-divider"
        hr.setAttribute("aria-hidden", "true")
        return hr
    }

    _menuItem(icon, label, onClick) {
        let button = document.createElement("button")
        button.type = "button"
        button.className = "assistant-header-menu-item"
        button.innerHTML = `<i class="bi ${icon}" aria-hidden="true"></i><span>${label}</span>`
        button.addEventListener("click", (event) => {
            event.preventDefault()
            event.stopPropagation()
            onClick()
        })
        return button
    }

    _pinItem(node) {
        let item = this._menuItem(node.pinned ? "bi-pin-fill" : "bi-pin", node.pinned ? "Unpin" : "Pin", () => {
            item.disabled = true
            let request = node.pinned ? this._unpinRequest(node) : this._pinRequest(node)
            request
                .then(({ response, data }) => {
                    if (!response.ok || !data.ok) throw new Error(data.error || "Could not update pin")
                    node.pinned = !!data.pinned
                    node.pin_id = data.pin_id || null
                    showSystemToast(node.pinned ? `Pinned ${node.name}.` : `Unpinned ${node.name}.`)
                    this._closeMenu()
                })
                .catch((error) => {
                    showSystemToast(error.message || "Could not update pin.", { level: "danger" })
                    item.disabled = false
                })
        })
        return item
    }

    _toggleGroup(node, group, chip) {
        let member = (node.group_slugs || []).includes(group.slug)
        chip.disabled = true
        requestGroupMembership(this._contactActionsBase, node.contact_id || node.id, group.slug, member ? "DELETE" : "POST")
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update group")
                let slugs = new Set(node.group_slugs || [])
                if (data.member) slugs.add(group.slug)
                else slugs.delete(group.slug)
                node.group_slugs = [...slugs]
                setChipMember(chip, data.member)
                showSystemToast(data.member ? `Added ${node.name} to ${group.name}.` : `Removed ${node.name} from ${group.name}.`)
                // Membership in the chart's backing group changes who belongs
                // on this chart — refresh it in place.
                let backingSlug = this.kindValue === "org" ? "coworkers" : "family"
                if (group.slug === backingSlug) this._load()
            })
            .catch((error) => showSystemToast(error.message || "Could not update group.", { level: "danger" }))
            .finally(() => { chip.disabled = false })
    }

    _syncLinkedin(node) {
        this._closeMenu()
        fetch(`${this._contactActionsBase}/${node.contact_id || node.id}/linkedin_refresh`, {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.message || "Could not start the LinkedIn sync.")
                showSystemToast(`Syncing ${node.name} from LinkedIn…`, { level: "info" })
            })
            .catch((error) => showSystemToast(error.message || "Could not start the LinkedIn sync.", { level: "danger" }))
    }

    _pinRequest(node) {
        return fetch(this.pinsUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ pinable_type: "Contact", pinable_id: String(node.contact_id || node.id), metadata: {} })
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _unpinRequest(node) {
        return fetch(`${this.pinsUrlValue}/${encodeURIComponent(node.pin_id)}`, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    // --- Edit-connections modal (group toggles + define relationship) -----------

    // `entry` is an ADD_KINDS row when opened from "Add parent…" and friends:
    // the kind is settled, so the select steps out of the way rather than
    // showing its mirrored wording ("Child of" under a menu item that said
    // "Add parent") for the user to second-guess.
    _openModal(node, entry) {
        if (!this.hasModalOverlayTarget) return
        this._modalNode = node
        // The heading carries the intent ("Add partner"), the line under it the
        // person it is for - saying both in one line read as a stutter.
        let intent = entry ? entry.label.replace(/…$/, "") : null
        this.modalNameTarget.textContent = intent ? `for ${node.name}` : node.name
        this.modalErrorTarget.hidden = true
        let title = this.modalOverlayTarget.querySelector(".contact-tree-modal-title")
        if (title) title.textContent = intent || "Edit connections"
        let groupsSection = this.modalOverlayTarget.querySelector(".contact-tree-modal-groups-section")
        if (groupsSection) groupsSection.hidden = !!entry
        if (this.hasModalGroupsTarget && !entry) {
            renderGroupChips(this.modalGroupsTarget, this.groups, (group) => (node.group_slugs || []).includes(group.slug), (group, chip) => this._toggleGroup(node, group, chip))
        }
        let form = this.modalFormTarget
        form.reset()
        form.setAttribute("data-contact-picker-exclude-id-value", String(node.id))
        let hidden = form.querySelector("[data-contact-picker-target='id']")
        if (hidden) hidden.value = ""
        let newName = form.querySelector("[data-contact-picker-target='name']")
        if (newName) newName.value = ""
        let select = form.querySelector(".contact-tree-modal-kind")
        if (select) {
            if (entry) select.value = entry.kind
            select.hidden = !!entry
        }
        let submit = form.querySelector("[data-contact-picker-target='submit']")
        if (submit) submit.disabled = true
        this.modalOverlayTarget.hidden = false
        form.querySelector("[data-contact-picker-target='input']")?.focus()
    }

    closeModal(event) {
        event?.preventDefault()
        if (this.hasModalOverlayTarget) this.modalOverlayTarget.hidden = true
        this._modalNode = null
    }

    overlayClick(event) {
        if (event.target === this.modalOverlayTarget) this.closeModal()
    }

    submitRelationship(event) {
        event.preventDefault()
        let node = this._modalNode
        if (!node) return
        let form = this.modalFormTarget
        let body = new FormData(form)
        if (!body.get("relationship[related_contact_id]") && !body.get("relationship[related_contact_name]")) return
        let submit = form.querySelector("[data-contact-picker-target='submit']")
        if (submit) submit.disabled = true
        fetch(`${this.contactsBaseValue}/${node.id}/relationships`, {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not add that relationship.")
                this.closeModal()
                showSystemToast(data.notice || "Relationship added.")
                this._refreshOpenPanel()
                // Keep the zoom and go to them, rather than refitting the whole
                // tree: after adding somebody the question is "where did they
                // land", and a refit answers "somewhere on this page".
                this._load({ refit: false }).then(() => this._settleOn(data.person_id))
            })
            .catch((error) => {
                this.modalErrorTarget.textContent = error.message || "Could not add that relationship."
                this.modalErrorTarget.hidden = false
                if (submit) submit.disabled = false
            })
    }

    // --- Misc --------------------------------------------------------------------

    // Two lines under the name, not one.
    //
    // They shared a line until now, which put the two longest strings in the
    // card against each other: "Great-great-great-great-grandfather" is 35
    // characters and a full lifespan is 23, so one of them always lost. First
    // the ellipsis ate the dates; then years-first saved the dates by clipping
    // the relation. Neither is a good trade on a genealogy card, and there was
    // no width left to buy.
    //
    // So: sex and relation on one line (both answer "who is this to me", both
    // short), whole dates on their own. Nothing competes and nothing truncates.
    // A person with no dates gets an empty third line rather than a shorter
    // card - the layout engine grids on a fixed CARD_H, and ragged heights would
    // cost more legibility than the blank line does.
    _personMeta(node) {
        let meta = document.createElement("span")
        meta.className = "contact-tree-relation contact-tree-meta"
        // Whole dates on hover. The card itself keeps years - it is a scanning
        // surface, and at 300px a full lifespan pushes the relation off the end
        // on exactly the generations whose relation is hardest to work out. But
        // "did my date save?" deserves an answer from wherever you are standing,
        // and a title costs no width at all.
        //
        // The relation rides the same title, which is what makes COMPACT honest:
        // that density drops the relation line to buy the width, and a fact you
        // can no longer read anywhere is a fact you have lost rather than one
        // you have tidied away.
        let exact = this._exactDates(node)
        let title = [node.relation, exact].filter(Boolean).join(" - ")
        if (title) meta.title = title

        // Sex and relation share a ROW inside the column. They were appended
        // straight to the column at first, and a float will not pull them onto
        // one line - a flex container ignores it - so the glyph took a line of
        // its own above the relation.
        let who = document.createElement("span")
        who.className = "contact-tree-who"
        let icon = this._genderIcon(node)
        if (icon) who.appendChild(icon)
        if (node.relation) {
            let el = document.createElement("span")
            el.className = "contact-tree-rel"
            el.textContent = node.relation
            who.appendChild(el)
        }
        if (who.childNodes.length) meta.appendChild(who)

        if (node.birthday_today === true) {
            let birthday = document.createElement("span")
            birthday.className = "contact-tree-birthday"
            let cake = document.createElement("span")
            cake.setAttribute("aria-hidden", "true")
            cake.textContent = "🎂 "
            let label = document.createElement("span")
            label.className = "contact-tree-birthday-label"
            label.textContent = "Birthday today"
            birthday.append(cake, label)
            meta.appendChild(birthday)
            return meta
        }
        let dates = this._cardDates(node)
        if (!dates) return meta

        let line = document.createElement("span")
        line.className = "contact-tree-dates"
        line.textContent = dates
        meta.appendChild(line)
        return meta
    }

    // "4 Dec 1923 - 2 Mar 2010", and the honest shorter forms: a year alone
    // where only the year is known, a day and month where the year is not, and
    // an open end for somebody gone whose date nobody has - which is a question,
    // not a blank.
    _cardDates(node) {
        let born = this._shortDate(node.birthday, node.birth_year_known) || (Number.isFinite(node.birth_year) ? String(node.birth_year) : null)
        if (!node.deceased) return born

        let died = this._shortDate(node.died_on, node.death_year_known) || (Number.isFinite(node.death_year) ? String(node.death_year) : null)
        if (born && died) return `${born} - ${died}`
        // A trailing dash alone is how a LIVING person's dates are written -
        // the branch above returns exactly that for somebody who is not
        // deceased. On a man born in 1840 it read as broken data rather than as
        // "nobody wrote down when he died", which is the ordinary state of the
        // far end of a tree. Same rule as FamilyTreePerson#lifespan_label.
        if (born) return `${born} - ?`
        return died ? `? - ${died}` : null
    }

    _shortDate(iso, yearKnown) {
        if (!iso) return null
        let date = new Date(`${iso}T00:00:00`)
        if (Number.isNaN(date.getTime())) return null
        if (!yearKnown) return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    }

    _exactDates(node) {
        let fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
        let parts = []
        if (node.birthday && node.birth_year_known) parts.push(`Born ${fmt(node.birthday)}`)
        else if (Number.isFinite(node.birth_year)) parts.push(`Born ${node.birth_year}`)
        if (node.deceased && node.died_on && node.death_year_known) parts.push(`died ${fmt(node.died_on)}`)
        else if (node.deceased && Number.isFinite(node.death_year)) parts.push(`died ${node.death_year}`)
        else if (node.deceased) parts.push("died - date unknown")
        return parts.length ? parts.join(", ") : null
    }

    _genderIcon(node) {
        let spec = GENDER_ICONS[node.gender]
        if (!spec) return null

        let el = document.createElement("i")
        el.className = `bi ${spec.icon} contact-tree-gender`
        el.setAttribute("role", "img")
        el.setAttribute("aria-label", spec.label)
        el.title = spec.label
        return el
    }

    _initials(node) {
        let parts = (node.name || "").trim().split(/\s+/)
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        return (node.initial || "?").toUpperCase()
    }

    // Who descends from which of the root's four grandparents.
    //
    // Only DIRECT ANCESTORS get a line, which is the convention and also the
    // only honest answer: a spouse married into the family and a cousin descends
    // from a line rather than being on it. Everyone else stays neutral, so the
    // colour means one thing wherever it appears.
    //
    // Pedigree collapse is real - the same couple can sit on two lines a few
    // generations up - and the first line to reach somebody keeps them, rather
    // than the colour flickering between renders.
    _buildGrandparentLines() {
        this.lineOf = new Map()
        this.lineHeads = []
        if (this.kindValue !== "family") return

        let root = this._egoId
        if (root == null) return

        // Father first, mother second, where the tree knows. Without gender the
        // order is still stable (by id), so the colours do not shuffle - they
        // simply cannot be NAMED as paternal or maternal, which the legend
        // handles by naming the grandparent instead.
        let parents = this._orderedParents(root)
        parents.slice(0, 2).forEach((parentId, parentIndex) => {
            this._orderedParents(parentId).slice(0, 2).forEach((grandId, grandIndex) => {
                let line = GRANDPARENT_LINES[parentIndex * 2 + grandIndex]
                if (!line) return

                let node = this.nodesById.get(grandId)
                this.lineHeads.push({
                    ...line, id: grandId, name: node ? node.name : "Unknown",
                    label: this._lineLabel(parentId, grandId)
                })
                this._claimAncestors(grandId, line.key)
            })
        })
    }

    // "Father's mother", when the tree actually knows. It very often does not -
    // a gender is optional and a tree with one parent recorded puts her in the
    // first slot regardless - and claiming "father's father" for a woman is the
    // kind of confident wrongness that makes somebody distrust the whole chart.
    // The legend names the grandparent either way; this is only the tooltip.
    _lineLabel(parentId, grandId) {
        let side = { male: "Father's", female: "Mother's" }[this.nodesById.get(parentId)?.gender]
        if (!side) return null

        let role = { male: "father", female: "mother" }[this.nodesById.get(grandId)?.gender] || "parent"
        return `${side} ${role}`
    }

    // Parents with the father first, when the tree knows which is which.
    _orderedParents(id) {
        return (this.parentsOf.get(id) || []).slice().sort((a, b) => {
            let rank = (personId) => {
                let gender = this.nodesById.get(personId)?.gender
                if (gender === "male") return 0
                if (gender === "female") return 1
                return 2
            }
            return rank(a) - rank(b) || a - b
        })
    }

    // The grandparent and everyone they descend from.
    _claimAncestors(startId, lineKey) {
        let queue = [startId]
        while (queue.length) {
            let id = queue.shift()
            if (this.lineOf.has(id)) continue

            this.lineOf.set(id, lineKey)
            ;(this.parentsOf.get(id) || []).forEach((parentId) => queue.push(parentId))
        }
    }

    _accent(node) {
        if (node.id === "self") return "blue"
        // Org charts: one color per DEPARTMENT, so the accents actually group
        // people. Hashing the node id (the family-tree behavior, where there
        // is no department) looked like meaningful coding and encoded nothing.
        // A family tree colours by GRANDPARENT LINE - see GRANDPARENT_LINES.
        // Somebody with no line (a spouse, a cousin, anyone below the root)
        // stays neutral rather than being given a colour that means nothing.
        if (this.kindValue === "family") {
            let line = this.lineOf?.get(node.id)
            return line ? GRANDPARENT_LINES.find((l) => l.key === line).accent : "slate"
        }
        let key = (node.department || "").trim().toLowerCase()
        if (!key) return "slate"
        let hash = 0
        key.split("").forEach((ch) => { hash = (hash * 31 + ch.charCodeAt(0)) % 997 })
        return ACCENTS[hash % ACCENTS.length]
    }

    _unplacedRow(nodes) {
        let wrap = document.createElement("div")
        wrap.className = "contact-tree-unplaced"
        let title = document.createElement("p")
        title.className = "contact-tree-unplaced-title"
        title.textContent = "Not yet placed"
        wrap.appendChild(title)
        let row = document.createElement("div")
        row.className = "contact-tree-unplaced-row"
        nodes.forEach((node) => row.appendChild(this._card(node)))
        wrap.appendChild(row)
        return wrap
    }

    // A small unobtrusive note when the server truncated the payload
    // (MAX_TREE_NODES in ContactsController#tree_payload) - sits right below
    // the "Not yet placed" row since that's what gets dropped first.
    _truncatedNote() {
        let note = document.createElement("p")
        note.className = "contact-tree-truncated-note"
        let word = this.truncatedCount === 1 ? "person" : "people"
        note.textContent = `${this.truncatedCount} more ${word} not shown`
        return note
    }

    // Org charts sitting at the build cap: an incomplete picture must not
    // present itself as the whole company (see org_chart_tree_payload).
    _cappedNote() {
        let note = document.createElement("p")
        note.className = "contact-tree-truncated-note"
        note.textContent = `Showing the first ${this.cappedTotal} people - the full organization is larger`
        return note
    }

    _push(map, key, value) {
        if (!map.has(key)) map.set(key, [])
        map.get(key).push(value)
    }

    // --- Search --------------------------------------------------------------
    // Client-side, name/title filter over whatever's already loaded - works
    // in read-only/public mode since it never touches the network. Matches
    // dim everyone else (a CSS class, not removal, so the layout never
    // reflows), auto-expand any collapsed ancestor hiding a match, and the
    // stage pans/zooms to center the first one. Enter cycles matches,
    // Escape/clearing the input resets.

    search(event) {
        clearTimeout(this._searchTimer)
        let raw = event.target.value
        this._searchTimer = setTimeout(() => this._setSearchQuery(raw), 150)
    }

    searchKeydown(event) {
        if (event.key === "Enter") {
            event.preventDefault()
            this._cycleMatch(event.shiftKey ? -1 : 1)
        } else if (event.key === "Escape") {
            event.preventDefault()
            this.clearSearch()
        }
    }

    clearSearch(event) {
        event?.preventDefault()
        clearTimeout(this._searchTimer)
        if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
        this._setSearchQuery("")
    }

    _setSearchQuery(raw) {
        this._searchQuery = this._normalizeSearch(raw)
        // While "show only matches" is on, WHO is drawn depends on the query, so
        // the chart has to be redrawn before the matches are counted against it
        // - and refitted, because five cards at the scale that fitted a hundred
        // and twenty-five are a speck in the middle of an empty canvas.
        if (this._searchOnly) {
            this._needsFit = true
            this._render()
        }
        this._recomputeMatches({ focus: true })
    }

    // Re-filters against the CURRENT node set without touching the input -
    // used after a data refresh (poll / sidebar save) so stale highlighting
    // doesn't linger, and it deliberately doesn't re-focus the stage (that
    // would fight a build's own growth or whatever the user was doing).
    _recomputeMatches({ focus = false } = {}) {
        if (!this._searchQuery) {
            this._searchMatches = []
            this._searchIndex = -1
            this._alternateSearch?.([], false)
            this._applySearchClasses()
            this._updateSearchCount()
            return
        }
        let matches = (this.nodes || []).filter((node) => this._nodeMatchesSearch(node, this._searchQuery)).map((node) => node.id)
        this._searchMatches = matches
        this._searchIndex = matches.length ? 0 : -1
        this._alternateSearch?.(matches, true)
        let expanded = CHART_VIEWS.includes(this._activeView()) && this._expandAncestorsFor(matches)
        // _render() re-applies search classes itself once the DOM is fresh;
        // otherwise nothing changed shape, so do it directly.
        if (expanded) this._render()
        else this._applySearchClasses()
        this._updateSearchCount()
        if (focus && matches.length) this._focusMatch(matches[0])
    }

    _nodeMatchesSearch(node, query) {
        let haystack = this._normalizeSearch([node.name, node.title, node.meta].filter(Boolean).join(" "))
        return haystack.includes(query)
    }

    // Lowercase + strip combining diacritics (café -> cafe) so an accent typed
    // or omitted on either side still matches.
    _normalizeSearch(value) {
        return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
    }

    // Walks every match up its manager/parent chain, dropping any collapsed
    // unit (keyed by either member of a couple/pair) along the way so a match
    // buried under a collapsed branch is actually visible. Returns whether
    // anything was expanded, so the caller knows whether a re-render is due.
    _expandAncestorsFor(matchIds) {
        let changed = false
        let visited = new Set()
        matchIds.forEach((id) => {
            let current = id
            while (current != null && !visited.has(current)) {
                visited.add(current)
                let unitIds = [current, ...(this.partnersOf?.get(current) || [])]
                unitIds.forEach((unitId) => { if (this.collapsed.delete(unitId)) changed = true })
                current = (this.parentsOf?.get(current) || [])[0]
            }
        })
        return changed
    }

    _cycleMatch(direction) {
        if (!this._searchMatches.length) return
        this._searchIndex = (this._searchIndex + direction + this._searchMatches.length) % this._searchMatches.length
        this._applySearchClasses()
        this._updateSearchCount()
        this._focusMatch(this._searchMatches[this._searchIndex])
    }

    _applySearchClasses() {
        if (!this.hasTreeTarget) return
        let hasQuery = !!this._searchQuery
        let matchSet = new Set(this._searchMatches.map(String))
        let currentId = this._searchIndex >= 0 ? String(this._searchMatches[this._searchIndex]) : null
        // A traced relationship dims the tree exactly the way a search does,
        // and for the same reason: the answer is three cards out of a hundred
        // and twenty-five, and the rest is context you should still be able to
        // see. Reusing the search's classes means every view that already
        // honours a search match honours this too.
        let pathSet = this._pathPair() && this._pathIds ? new Set([...this._pathIds].map(String)) : null
        // Deliberately not scoped to .contact-tree-card: a timeline chip, a
        // table row and a fan segment all carry data-node-id and all want the
        // same dim/match treatment. The class names still say "card" because
        // the chart's CSS is keyed on them; per-view styling picks them up too.
        //
        // BOTH HOSTS, not just the stage. .contact-tree-flow is a SIBLING of
        // the stage, not a child, so a layout that scrolls rather than pans was
        // invisible to this sweep: searching a document view highlighted
        // nothing and dimmed nothing. It went unnoticed while the scrolling
        // views were the table and the outline, whose own markup carries the
        // query anyway; the timeline moving off the stage is what surfaced it.
        let hosts = this.hasFlowTarget ? [this.treeTarget, this.flowTarget] : [this.treeTarget]
        hosts.forEach((host) => host.querySelectorAll("[data-node-id]").forEach((card) => {
            let id = card.dataset.nodeId
            let isMatch = matchSet.has(id)
            let onPath = pathSet ? pathSet.has(id) : false
            card.classList.toggle("contact-tree-card--dim", (hasQuery && !isMatch) || (!!pathSet && !onPath))
            card.classList.toggle("contact-tree-card--match", isMatch)
            card.classList.toggle("contact-tree-card--current-match", isMatch && id === currentId)
            card.classList.toggle("contact-tree-card--on-path", onPath)
        }))
    }

    // How many people are in the tree rides in the PLACEHOLDER ("Search 6
    // people") rather than in a suffix beside the input. Same fact, no width:
    // the count used to sit inside the box as its own text, and between that
    // and a 19rem input the toolbar wrapped to two lines on a phone.
    //
    // The slot next to it is then only ever the match position, which is what
    // you want while typing - and the placeholder it replaces is gone from view
    // at that moment anyway.
    _updateSearchCount() {
        this._updateSearchPlaceholder()
        this._syncSearchOnlyButton()
        if (!this.hasSearchCountTarget) return
        if (!this._searchQuery) {
            this.searchCountTarget.textContent = ""
            return
        }
        this.searchCountTarget.textContent = this._searchMatches.length ? `${this._searchIndex + 1} of ${this._searchMatches.length}` : "No matches"
    }

    _updateSearchPlaceholder() {
        if (!this.hasSearchInputTarget) return

        let total = this.nodes?.length || 0
        // Before the payload lands there is no count to give, and the org chart
        // holds colleagues rather than people-in-a-family - so both fall back
        // to the wording the markup ships with.
        this.searchInputTarget.placeholder = total && this.kindValue !== "org"
            ? `Search ${total.toLocaleString()} ${total === 1 ? "person" : "people"}`
            : this._defaultPlaceholder()
    }

    _defaultPlaceholder() {
        if (this._basePlaceholder === undefined) {
            this._basePlaceholder = this.searchInputTarget.getAttribute("placeholder") || ""
        }
        return this._basePlaceholder
    }

    // Centre on somebody just added or changed, and mark them for a moment so
    // the eye finds them without reading names. Same panning as a search hit -
    // the difference is only that nobody typed anything.
    _settleOn(nodeId) {
        if (nodeId == null) return

        // One frame, so the fresh cards have been laid out - _focusMatch pans
        // by offsetLeft/offsetTop and both are 0 until they have.
        window.requestAnimationFrame(() => {
            this._focusMatch(nodeId)
            let card = this.treeTarget?.querySelector(`[data-node-id="${CSS.escape(String(nodeId))}"]`)
            if (!card) return

            card.classList.add("contact-tree-card--landed")
            window.setTimeout(() => card.classList.remove("contact-tree-card--landed"), 2200)
        })
    }

    // Pans (and nudges the zoom up if it's currently zoomed way out) to center
    // the given node in the viewport.
    _focusMatch(nodeId) {
        if (!this.hasViewportTarget) return
        let card = this.treeTarget.querySelector(`[data-node-id="${CSS.escape(String(nodeId))}"]`)
        if (!card) return
        // The table scrolls rather than pans, so centring it by transform would
        // move a stage that isn't the thing with the scrollbar.
        if (this._panZoom === false) {
            card.scrollIntoView({ block: "center", behavior: "smooth" })
            return
        }
        this.scale = Math.min(Math.max(this.scale, 0.6), MAX_SCALE)
        let vw = this.viewportTarget.clientWidth
        let vh = this.viewportTarget.clientHeight
        let cx = card.offsetLeft + card.offsetWidth / 2
        let cy = card.offsetTop + card.offsetHeight / 2
        this.tx = vw / 2 - cx * this.scale
        this.ty = vh / 2 - cy * this.scale
        this._applyTransform()
    }

    // --- The "Play" tour -----------------------------------------------------
    //
    // A guided flight round a big tree: one stop per lineage, founder to
    // founder, largest first, then a last stop on the person the tree is
    // drawn from. Nothing is authored - the route falls straight out of the
    // same _overview model the minimap already draws
    // from, so it never drifts from what those show.
    //
    // this._tour is the whole of the tour's state - {stops, index, playing,
    // ended} - and nothing about it lives in the DOM. _render tears down and
    // rebuilds the minimap pane and the story card on every call (a poll, a
    // saved edit, unfolding a branch mid-flight), so anything the tour needs
    // to survive that has to be read back out of this object, not out of a
    // node reference - _renderMinimap and _renderStory both do exactly that.

    // One stop per lineage (already sorted largest first by _overviewModel),
    // then the ego if this tree has one drawn from a living person's
    // perspective. A lineage whose founder fell out of the current people
    // filter is skipped rather than landing on a card that doesn't exist.
    _tourRoute() {
        let model = this._overview
        if (!model) return []

        let stops = model.lineages
            .filter((lineage) => lineage.founderId != null && this.nodesById.has(lineage.founderId))
            .map((lineage) => {
                let dated = lineage.members.filter((id) => model.yearOf.has(id))
                let regions = new Map()
                dated.forEach((id) => {
                    let place = String(this.nodesById.get(id)?.birth_place || "").trim()
                    let region = place.split(",").pop()?.trim()
                    if (region) regions.set(region, (regions.get(region) || 0) + 1)
                })
                let mostly = [...regions.entries()].sort((a, b) => b[1] - a[1])[0]
                let region = mostly && dated.length && mostly[1] >= dated.length / 3 ? mostly[0] : null
                let body = `${lineage.members.length.toLocaleString()} ${lineage.members.length === 1 ? "person" : "people"} over ${lineage.generations.toLocaleString()} ${lineage.generations === 1 ? "generation" : "generations"}`
                if (region) body += ` · mostly ${region}`
                return {
                    id: lineage.founderId,
                    eyebrow: lineage.founderYear ? String(lineage.founderYear) : (lineage.fromYear ? `from ${lineage.fromYear}` : ""),
                    title: lineage.founderName,
                    body: `${body}.`
                }
            })

        if (model.egoId != null && this.nodesById.has(model.egoId)) {
            stops.push({
                id: model.egoId,
                eyebrow: "Today",
                title: this.nodesById.get(model.egoId).name,
                body: "Where this tree is drawn from."
            })
        }
        return stops
    }

    _toggleTourPlay(event) {
        event?.preventDefault()
        if (this._tour && this._tour.playing) this._pauseTour()
        else this._playTour()
    }

    _playTour() {
        if (!this._overview) return
        if (window.matchMedia("(max-width: 575.98px)").matches) this._setMinimapCollapsed(true)
        if (!this._tour || this._tour.ended) {
            let stops = this._tourRoute()
            if (!stops.length) return
            this._tour = { stops, index: 0, playing: true, ended: false }
        } else {
            this._tour.playing = true
        }
        this._startTourPulse()
        this._syncPlayButton()
        this._renderStory()
        this._goToStop(this._tour.index)
    }

    // Guarded so a click on Prev/Next, a stray wheel over the map, or calling
    // this with no tour running at all is a no-op - see _onWheel/_onPointerDown,
    // which call it unconditionally on every interaction with the chart.
    _pauseTour() {
        if (!this._tour || !this._tour.playing) return
        this._tour.playing = false
        this._clearTourDwell()
        this._syncPlayButton()
    }

    _endTour() {
        this._cancelTourMotion()
        this._tour = null
        this._storyEl?.remove()
        this._storyEl = null
        delete this.element.dataset.tour
        this._syncPlayButton()
        this._syncMinimap()
    }

    // Cancels the flight and the dwell/pulse timers without discarding the
    // route - what disconnect() and a departure to a non-chart view need
    // (_endTour additionally throws the route itself away).
    _cancelTourMotion() {
        this._clearTourDwell()
        this._stopTourPulse()
        if (this._tourRaf) {
            window.cancelAnimationFrame(this._tourRaf)
            this._tourRaf = null
        }
    }

    tourNext(event) {
        event?.preventDefault()
        if (!this._tour) return
        let next = this._tour.index + 1
        if (next >= this._tour.stops.length) return
        this._goToStop(next)
    }

    tourPrev(event) {
        event?.preventDefault()
        if (!this._tour) return
        let prev = this._tour.index - 1
        if (prev < 0) return
        this._goToStop(prev)
    }

    closeTour(event) {
        event?.preventDefault()
        this._endTour()
    }

    _goToStop(index) {
        if (!this._tour) return
        let stop = this._tour.stops[index]
        if (!stop) return
        this._tour.index = index
        this._tour.ended = false
        this._clearTourDwell()
        this._renderStory()
        this._flyToId(stop.id, () => {
            this._flashTourCard(stop.id)
            this._syncMinimap()
            if (this._tour && this._tour.playing) this._scheduleDwell()
        })
    }

    _advanceTour() {
        if (!this._tour || !this._tour.playing) return
        let next = this._tour.index + 1
        if (next >= this._tour.stops.length) {
            this._tour.playing = false
            this._tour.ended = true
            this._syncPlayButton()
            this._renderStory()
            this._syncMinimap()
            return
        }
        this._goToStop(next)
    }

    _scheduleDwell() {
        this._clearTourDwell()
        this._tourDwell = window.setTimeout(() => this._advanceTour(), TOUR_DWELL_MS)
    }

    _clearTourDwell() {
        if (this._tourDwell) {
            window.clearTimeout(this._tourDwell)
            this._tourDwell = null
        }
    }

    // The pulsing ring on the minimap needs its own heartbeat - unlike the
    // route line, it has to redraw during a stop's dwell, when the camera
    // itself isn't moving and nothing else calls _applyTransform.
    _startTourPulse() {
        if (this._tourPulseTimer) return
        this._tourPulseTimer = window.setInterval(() => this._syncMinimap(), 400)
    }

    _stopTourPulse() {
        if (this._tourPulseTimer) {
            window.clearInterval(this._tourPulseTimer)
            this._tourPulseTimer = null
        }
    }

    // Unfold whatever hides the stop, then fly the camera to it once the
    // (possibly fresh) card has a real position - one frame after a re-render,
    // same reasoning as _settleOn.
    _flyToId(id, onArrive) {
        let changed = this._expandAncestorsFor([id])
        if (changed) this._render()
        window.requestAnimationFrame(() => {
            let target = this._cameraFor(id)
            if (target) this._flyCamera(target, onArrive)
            else onArrive?.()
        })
    }

    // The same camera _landOn would compute: the readable floor for this
    // density, centred on the card.
    _cameraFor(id) {
        if (!this.hasViewportTarget) return null
        let card = this.treeTarget?.querySelector(`[data-node-id="${CSS.escape(String(id))}"]`)
        if (!card) return null
        let scale = this._density === "compact" ? MIN_OPEN_SCALE_COMPACT : MIN_OPEN_SCALE
        let vw = this.viewportTarget.clientWidth
        let vh = this.viewportTarget.clientHeight
        let cx = card.offsetLeft + card.offsetWidth / 2
        let cy = card.offsetTop + card.offsetHeight / 2
        return { scale, tx: vw / 2 - cx * scale, ty: vh / 2 - cy * scale }
    }

    // Animates tx/ty/scale from wherever the camera is now to the target over
    // TOUR_FLIGHT_MS, ease-in-out. A reader who asked for less motion gets the
    // jump instead - the tour still narrates and still lands, just without the
    // pan.
    _flyCamera(target, onDone) {
        if (this._tourRaf) {
            window.cancelAnimationFrame(this._tourRaf)
            this._tourRaf = null
        }
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            this.scale = target.scale
            this.tx = target.tx
            this.ty = target.ty
            this._applyTransform()
            onDone?.()
            return
        }

        let start = { scale: this.scale, tx: this.tx, ty: this.ty }
        let ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - ((-2 * p + 2) ** 3) / 2)
        let t0 = null
        let step = (now) => {
            if (t0 == null) t0 = now
            let p = Math.min((now - t0) / TOUR_FLIGHT_MS, 1)
            let e = ease(p)
            this.scale = start.scale + (target.scale - start.scale) * e
            this.tx = start.tx + (target.tx - start.tx) * e
            this.ty = start.ty + (target.ty - start.ty) * e
            this._applyTransform()
            if (p < 1) {
                this._tourRaf = window.requestAnimationFrame(step)
            } else {
                this._tourRaf = null
                onDone?.()
            }
        }
        this._tourRaf = window.requestAnimationFrame(step)
    }

    _flashTourCard(id) {
        let card = this.treeTarget?.querySelector(`[data-node-id="${CSS.escape(String(id))}"]`)
        if (!card) return
        card.classList.add("contact-tree-card--landed")
        window.setTimeout(() => card.classList.remove("contact-tree-card--landed"), 2200)
    }

    _syncPlayButton() {
        if (!this._minimapPlayBtn) return
        let tour = this._tour
        let playing = !!(tour && tour.playing)
        let ended = !!(tour && tour.ended)
        this._minimapPlayIcon.className = playing ? "bi bi-pause-fill" : "bi bi-play-fill"
        this._minimapPlayLabel.textContent = playing ? "Pause" : (tour ? (ended ? "Play again" : "Resume") : "Play")
        this._minimapPlayBtn.setAttribute("aria-pressed", playing ? "true" : "false")
    }

    // The narration card in the bottom-left tray, rebuilt from this._tour on
    // every call - the same "state, not DOM" rule the rest of the tour keeps.
    // Rendered fresh rather than patched in place because it is cheap, and a
    // pointerdown/wheel on it must never reach the viewport's own listeners
    // (see _onPointerDown/_onWheel) - simplest as a property set once per card.
    _renderStory() {
        this._storyEl?.remove()
        this._storyEl = null
        // CSS keys off this: on a phone the story card and the map pane would
        // share the same bottom strip, so the pane steps aside for the tour.
        if (this._tour) this.element.dataset.tour = "true"
        else delete this.element.dataset.tour
        if (!this._tour || !this.hasViewportTarget) return
        let stop = this._tour.stops[this._tour.index]
        if (!stop) return

        let card = document.createElement("div")
        card.className = "contact-tree-story"
        card.setAttribute("role", "group")
        card.setAttribute("aria-label", "Tree tour")
        card.addEventListener("pointerdown", (event) => event.stopPropagation())
        card.addEventListener("wheel", (event) => event.stopPropagation())

        let close = document.createElement("button")
        close.type = "button"
        close.className = "contact-tree-story-close"
        close.setAttribute("aria-label", "End the tour")
        close.textContent = "×"
        close.addEventListener("click", (event) => this.closeTour(event))
        card.appendChild(close)

        let eyebrow = document.createElement("div")
        eyebrow.className = "contact-tree-story-eyebrow"
        eyebrow.textContent = stop.eyebrow || ""
        card.appendChild(eyebrow)

        let title = document.createElement("div")
        title.className = "contact-tree-story-title"
        title.textContent = stop.title
        card.appendChild(title)

        let body = document.createElement("p")
        body.className = "contact-tree-story-body"
        body.textContent = stop.body
        card.appendChild(body)

        let row = document.createElement("div")
        row.className = "contact-tree-story-row"
        let prev = document.createElement("button")
        prev.type = "button"
        prev.className = "contact-tree-story-nav"
        prev.textContent = "Prev"
        prev.disabled = this._tour.index === 0
        prev.addEventListener("click", (event) => this.tourPrev(event))
        let progress = document.createElement("span")
        progress.className = "contact-tree-story-progress"
        progress.textContent = `${this._tour.index + 1} of ${this._tour.stops.length}`
        let next = document.createElement("button")
        next.type = "button"
        next.className = "contact-tree-story-nav"
        next.textContent = "Next"
        next.disabled = this._tour.index >= this._tour.stops.length - 1
        next.addEventListener("click", (event) => this.tourNext(event))
        row.append(prev, progress, next)
        card.appendChild(row)

        this._cornerTray().appendChild(card)
        this._storyEl = card
    }
}
